import React, { useEffect, useMemo, useState, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Chip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  Snackbar,
  Alert,
  Radio,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import GroupsIcon from '@mui/icons-material/Groups';
import PercentIcon from '@mui/icons-material/Percent';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ReplayIcon from '@mui/icons-material/Replay';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BankStatementUpload from './BankStatementUpload';
import { buildTallyVoucherRows, exportTallyVouchers, exportTallyXml, buildSuspenseRows, exportSuspenseSheet } from './tallyExport';
import CollectionDashboard from './CollectionDashboard';
import {
  buildRecommendationIndex,
  buildPhoneIndex,
  recommendClients,
  recommendForPayment,
  STRONG_NAME_MATCH,
  DEFAULT_TOLERANCE,
  extractPhones,
} from './recommendationEngine';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// ───────────────────────── helpers ─────────────────────────
const HEADER_SX = {
  backgroundColor: '#1e40af',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '13px',
};

// How far a bank amount may differ and still count as a match. Configurable
// without a code change; falls back to a rupee.
const AMOUNT_TOLERANCE = Number(process.env.REACT_APP_PAYMENT_MATCH_TOLERANCE) || DEFAULT_TOLERANCE;

// how many client suggestions the band shows — a short list is easier to judge
const MAX_CLIENT_SUGGESTIONS = 3;
// ...but when history names several clients, every one of them has to be shown
const MAX_HISTORY_CLIENT_SUGGESTIONS = 8;

// the payments list is polled so a statement upload or someone else's mapping
// shows up without a manual refresh
const AUTO_REFRESH_MS = 5 * 60 * 1000;

// hide non-essential columns on small screens to avoid horizontal scroll
const HIDE_SM = { display: { xs: 'none', lg: 'table-cell' } };

// payments table column widths — every column is sized to its widest realistic
// content so the description column absorbs all remaining horizontal space
const COL = {
  check: 32,
  expand: 24,
  trxId: 140, // AXIS + a 16-digit bank reference, monospace
  date: 148, // "Aug 01, 2026, 01:45 PM" on one line
  desc: 260, // minWidth only — this column absorbs whatever is left over
  status: 100,
  amount: 116,
  mode: 84,
  mappedAmount: 120,
  mappedType: 104,
  mapStatus: 124,
  action: 132, // Map button + two icon actions on one line
};
const CELL_PAD = '6px 8px';

const formatINR = (n) =>
  '₹ ' +
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtTimeOnly = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const toLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const monthStartISO = () => {
  const d = new Date();
  return toLocalISO(new Date(d.getFullYear(), d.getMonth(), 1));
};
const monthEndISO = () => {
  const d = new Date();
  return toLocalISO(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

// selectable TDS percentages for the mapping popup
const TDS_OPTIONS = ['0', '0.25', '0.5', '1', '1.25', '1.5', '2', '5', '10'];

// short labels for the closed filter control, so the toolbar stays on one row
const MAP_FILTER_SHORT = {
  all: 'All Status',
  UNMAPPED: 'Unmapped',
  PARTIAL: 'Partial',
  MAPPED: 'Mapped',
  SUSPENSE: 'Suspense',
  OTHER: 'Other',
};

// download an array of plain objects to .xlsx
const exportRowsToExcel = (rows, filename) => {
  if (!rows || rows.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  XLSX.writeFile(wb, filename);
};

// quick date-range presets shared by all tabs
const DATE_PRESETS = [
  { id: 'thisMonth', label: 'This Month' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'last7', label: 'Last 7 Days' },
  { id: 'last30', label: 'Last 30 Days' },
  { id: 'fy', label: 'This FY' },
];
const computePreset = (id) => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = 24 * 60 * 60 * 1000;
  switch (id) {
    case 'thisMonth':
      return { from: toLocalISO(new Date(y, m, 1)), to: toLocalISO(new Date(y, m + 1, 0)) };
    case 'lastMonth':
      return { from: toLocalISO(new Date(y, m - 1, 1)), to: toLocalISO(new Date(y, m, 0)) };
    case 'last7':
      return { from: toLocalISO(new Date(d.getTime() - 6 * day)), to: toLocalISO(d) };
    case 'last30':
      return { from: toLocalISO(new Date(d.getTime() - 29 * day)), to: toLocalISO(d) };
    case 'fy': {
      const start = m >= 3 ? y : y - 1; // Indian FY starts in April
      return { from: toLocalISO(new Date(start, 3, 1)), to: toLocalISO(new Date(start + 1, 2, 31)) };
    }
    default:
      return null;
  }
};

// ── mapping status derivation ──
const MAP_STATUS = {
  UNMAPPED: { key: 'UNMAPPED', label: 'Unmapped', bg: '#fef2f2', color: '#dc2626', border: '#fecaca', Icon: ErrorOutlineIcon },
  PARTIAL: { key: 'PARTIAL', label: 'Partially Mapped', bg: '#fef3c7', color: '#d97706', border: '#fde68a', Icon: DonutLargeIcon },
  MAPPED: { key: 'MAPPED', label: 'Fully Mapped', bg: '#ecfdf5', color: '#16a34a', border: '#bbf7d0', Icon: CheckCircleIcon },
  // parked: the money arrived but nobody knows whose it is yet
  SUSPENSE: { key: 'SUSPENSE', label: 'Suspense', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', Icon: HelpOutlineIcon },
  // a credit that was never an invoice payment — transfer, bank credit, other income
  OTHER: { key: 'OTHER', label: 'Other', bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe', Icon: AccountBalanceWalletIcon },
};

const allocatedTotal = (allocs) =>
  (allocs || []).reduce((s, a) => s + num(a.allocated), 0);

const tdsTotal = (allocs) => (allocs || []).reduce((s, a) => s + num(a.tds_amount), 0);

// TDS deducted at source: the payment received is net of TDS. If a client deducts
// t% on the invoice gross, then payment = gross·(1 − t/100) and tds = payment·t/(100 − t).
// TDS is deducted on the invoice value, so the invoice amount is the base — not
// the payment received. Preferring the invoice amount also keeps
// payment + TDS === invoice exactly, with no double-rounding drift.
const tdsBaseOf = (inv) => {
  const amount = num(inv?.invoice_amount);
  if (amount > 0) return amount;
  const due = num(inv?.due); // ledger-style entries carry no invoice amount
  if (due > 0) return due;
  return num(inv?.allocated); // last resort: all we know is what was paid
};

// Settling an invoice = payment allocated + TDS credited against it.
const computeTds = (base, tdsPct) => {
  const b = num(base);
  const t = num(tdsPct);
  if (t <= 0 || t >= 100) return 0;
  return Math.round((b * t) / 100);
};

const uniqueClientCount = (allocs) =>
  new Set((allocs || []).map((a) => a.client_id ?? a.client_name).filter(Boolean)).size;

// normalize free text for matching payment descriptions against client names
const normalizeText = (s) =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Generic words in radiology/diagnostics client names — ignored when matching so a
// distinctive token (surname / brand, e.g. "rathore") drives the match rather than
// common words like "digital", "xray", "diagnostics".
const NAME_STOPWORDS = new Set([
  // honorifics and titles: on hundreds of client names, identifying none of them
  'sri', 'shri', 'sree', 'shree', 'smt', 'messrs', 'saint', 'prof',
  // organisation words that sit at the end of a name, where a bank truncates it
  'science', 'sciences', 'medicine', 'institution', 'college', 'university',
  'and', 'the', 'of', 'for', 'llp', 'pvt', 'ltd', 'private', 'limited', 'co', 'company',
  'clinic', 'clinics', 'hospital', 'hospitals', 'diagnostic', 'diagnostics', 'scan', 'scans',
  'centre', 'center', 'xray', 'x', 'ray', 'lab', 'labs', 'laboratory', 'imaging', 'digital',
  'care', 'health', 'healthcare', 'medical', 'multispeciality', 'speciality', 'specialty',
]);

// How well a name matches the description. Returns hit count (999 = full name is a
// contiguous substring) and the earliest character position of a match. Position
// matters: in NEFT/IMPS strings the payer name comes before the bank name, so an
// early match (e.g. "RATHORE") is the payer while a late one (e.g. "BARODA" from
// "BANK OF BARODA") is usually just the bank.
// Bank narrations respace names freely: "ANI AM SCANS" for "Aniam Scans",
// "SURETECH" for "Sure Tech". So each check runs twice — once against the
// narration as written, once with all spaces removed. Position comes back as a
// ratio so hits from either version stay comparable.
const MIN_COMPACT_WORD = 5; // shorter words match too easily once spaces go

const nameMatchInfo = (name, descNorm, descCompact) => {
  if (!descNorm || !name) return { hits: 0, ratio: Infinity };
  const compact = descCompact ?? descNorm.replace(/ /g, '');
  const nameCompact = name.replace(/ /g, '');

  // the whole name as one run
  if (name.length >= 4 && descNorm.includes(name)) {
    return { hits: 999, ratio: descNorm.indexOf(name) / descNorm.length };
  }
  if (nameCompact.length >= 6 && compact.includes(nameCompact)) {
    return { hits: 999, ratio: compact.indexOf(nameCompact) / compact.length };
  }

  const words = name.split(' ').filter((w) => w.length >= 3 && !NAME_STOPWORDS.has(w));
  let hits = 0;
  let ratio = Infinity;
  words.forEach((w) => {
    let pos = descNorm.indexOf(w);
    let len = descNorm.length;
    if (pos < 0 && w.length >= MIN_COMPACT_WORD) {
      pos = compact.indexOf(w); // "aniam" inside "aniamscansanddiagnostics"
      len = compact.length;
    }
    if (pos >= 0) {
      hits += 1;
      const r = pos / (len || 1);
      if (r < ratio) ratio = r;
    }
  });
  return { hits, ratio };
};

// Earlier matches score higher; matches in the trailing bank region score ~0.
const positionBonus = (ratio) => {
  if (!Number.isFinite(ratio)) return 0;
  return Math.round((1 - Math.min(Math.max(ratio, 0), 1)) * 4); // ~4 at the start, ~0 near the end
};

// Acronym match: handles a client stored as an acronym (e.g. "BHS") whose expansion
// appears in the description ("BRAHMADANDA HEALTHCARE SOLUT…"), and the reverse where
// the description contains the acronym token for a full client name.
const acronymMatchInfo = (clientNameNorm, descNorm) => {
  if (!clientNameNorm || !descNorm) return { hit: false, ratio: Infinity };
  const descWords = descNorm.split(' ').filter(Boolean);

  // A) client name is itself an acronym → match consecutive word initials in the description
  const acr = clientNameNorm.replace(/\s+/g, '');
  if (acr.length >= 3 && acr.length <= 6 && /^[a-z]+$/.test(acr)) {
    for (let i = 0; i + acr.length <= descWords.length; i += 1) {
      let ok = true;
      for (let j = 0; j < acr.length; j += 1) {
        if (descWords[i + j][0] !== acr[j]) {
          ok = false;
          break;
        }
      }
      if (ok) return { hit: true, ratio: descNorm.indexOf(descWords[i]) / descNorm.length };
    }
  }

  // B) client full name's initials appear as a standalone token in the description
  const words = clientNameNorm.split(' ').filter((w) => w.length >= 2 && !NAME_STOPWORDS.has(w));
  if (words.length >= 2) {
    const clientAcr = words.map((w) => w[0]).join('');
    if (clientAcr.length >= 3 && descWords.includes(clientAcr)) {
      return { hit: true, ratio: descNorm.indexOf(clientAcr) / descNorm.length };
    }
  }
  return { hit: false, ratio: Infinity };
};

// ── previous-payment (history) matching ──
// Tokens that carry no payer identity: bank rails, the banks and PSPs themselves,
// and statement boilerplate. Bank names matter here — two unrelated payers both
// routed through AXIS would otherwise look similar to each other.
const DESC_NOISE = new Set([
  // rails / statement noise
  'neft', 'rtgs', 'imps', 'upi', 'ift', 'clg', 'ach', 'ecs', 'chq', 'cheque', 'trf', 'trfr',
  'p2a', 'p2m', 'p2p', 'attn', 'inb', 'brn', 'txn', 'impstxn', 'bank', 'banking', 'india',
  'indian', 'ltd', 'limited', 'pvt', 'private', 'payment', 'pay', 'paymnt', 'invoice', 'inv',
  'bill', 'billpay', 'urgent', 'collection', 'transfer', 'fund', 'funds', 'online', 'net',
  'netbanking', 'ref', 'from', 'the', 'and', 'for', 'redemption',
  // banks and payment services
  'axis', 'axisbank', 'icici', 'icicibank', 'iciciban', 'hdfc', 'hdfcbank', 'sbi', 'sbin',
  'statebank', 'canara', 'canarabank', 'cnrb', 'baroda', 'barb', 'bob', 'kotak', 'kotakmah',
  'kotakmahindra', 'idfc', 'idfcbank', 'idfcfirst', 'punb', 'pnb', 'punjab', 'indusind',
  'yesbank', 'federal', 'union', 'uco', 'iob', 'karur', 'karnataka', 'tmb', 'csb', 'rbl',
  'dbs', 'citi', 'hsbc', 'standard', 'chartered', 'idbi', 'indianbk', 'bandhan', 'aubank',
  'phonepe', 'paytm', 'gpay', 'googlepay', 'razorpay', 'billdesk', 'cred', 'bharatpe', 'amazonpay',
  'freecharge', 'mobikwik', 'slice', 'jupiter', 'fampay',
  // generic wallet / app phrasing: "Paid via CRED", "payment sent", "settlement"
  'paid', 'via', 'sent', 'send', 'received', 'receive', 'settle', 'settled',
  'settlement', 'dues', 'due', 'amount', 'money', 'cash', 'thanks', 'thank', 'you',
  'self', 'own', 'credit', 'debit', 'towards', 'against', 'account', 'acct', 'acc',
  'mandate', 'autopay', 'recurring', 'wallet', 'card', 'creditcard', 'rewards',
  // aggregator and purpose-of-payment boilerplate: on every narration, so it
  // cannot be the shared wording that identifies a payer
  'vendor', 'vendorpayment', 'venpay', 'purpose', 'settlement', 'collection',
  // UPI handle suffixes: "9981642145@ybl", "name@okaxis" — the handle's bank, not the payer
  'ybl', 'ibl', 'axl', 'apl', 'okaxis', 'okicici', 'oksbi', 'okhdfcbank', 'oksb',
  'yesbank', 'airtel', 'freecharge', 'jupiteraxis', 'fam', 'naviaxis', 'slc', 'waaxis',
]);

// Distinctive words of a narration. Tokens that are mostly digits are dropped —
// reference numbers change on every payment — but something like a UPI handle
// ("aavegbhardwaj0") is kept, because that is exactly what identifies a payer.
// Bank narrations cut words off at a fixed width: "PRIVATE" arrives as "PRIVAT",
// "HOSPITAL" as "HOSPI", "DIAGNOSTICS" as "DIAGNO". The truncation is not in the
// stopword list, so it survived as a distinctive word and made every
// "... PRIVAT/ICICI BANK" narration look like the same payer. Treat any prefix of
// a stopword as that stopword.
// Only descriptive words, never brand names — "BHARAT" is a prefix of "bharatpe"
// but it is also a real client's name, and dropping it would be worse than
// keeping "PRIVAT".
const TRUNCATABLE_WORDS = [
  'private', 'limited', 'incorporated', 'company', 'hospital', 'hospitals', 'clinic', 'clinics',
  'diagnostic', 'diagnostics', 'laboratory', 'laboratories', 'medical', 'healthcare', 'imaging',
  'radiology', 'sonography', 'multispeciality', 'multispecialty', 'speciality', 'specialty',
  'superspeciality', 'polyclinic', 'institute', 'research', 'foundation', 'memorial', 'services',
  'solutions', 'charitable', 'society', 'enterprises', 'international', 'national', 'university',
  'nursing', 'centre', 'center', 'medicare', 'medicity',
];

const STOPWORD_PREFIXES = (() => {
  const out = new Set();
  TRUNCATABLE_WORDS.forEach((w) => {
    for (let n = 5; n < w.length; n += 1) out.add(w.slice(0, n));
  });
  return out;
})();

const descTokens = (desc) => {
  const set = new Set();
  normalizeText(desc)
    .split(' ')
    .forEach((w) => {
      // NAME_STOPWORDS too: "scans", "diagnostics", "hospital" and the like say
      // nothing about WHICH payer this is, and without them every diagnostics
      // centre that ever paid looks like a match for every other one
      if (w.length < 3 || DESC_NOISE.has(w) || NAME_STOPWORDS.has(w)) return;
      if (STOPWORD_PREFIXES.has(w)) return; // a cut-off stopword, not a payer's name
      const digits = (w.match(/\d/g) || []).length;
      if (digits === w.length || digits / w.length >= 0.4) return;
      set.add(w);
    });
  return set;
};

// Overlap between two narrations: the share of the smaller token set they have in
// common, plus which tokens matched (needed to reject one-weak-word coincidences).
const descOverlap = (a, b) => {
  if (a.size === 0 || b.size === 0) return { similarity: 0, shared: [] };
  const shared = [];
  a.forEach((t) => {
    if (b.has(t)) shared.push(t);
  });
  return { similarity: shared.length === 0 ? 0 : shared.length / Math.min(a.size, b.size), shared };
};

// Previous payments reduced to (client, narration tokens). Indexed under both the
// client id and the normalised client name, because this feed may carry only one
// of the two and the invoice list keys clients by id.
// The rows may arrive bare, or wrapped in any of the envelopes this backend
// uses elsewhere. Missing the wrapper silently costs every recommendation the
// history signal, so try each one and finally any array in the body.
const rowsFromBody = (body) => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return [];
  const keys = ['data', 'results', 'result', 'history', 'payments', 'rows', 'records', 'response'];
  for (const k of keys) {
    if (Array.isArray(body[k])) return body[k];
    if (body[k] && typeof body[k] === 'object') {
      const inner = keys.find((j) => Array.isArray(body[k][j]));
      if (inner) return body[k][inner];
    }
  }
  const any = Object.values(body).find((v) => Array.isArray(v));
  return Array.isArray(any) ? any : [];
};

const buildHistoryIndex = (history) => {
  const rows = (history || [])
    .map((h) => ({
      clientId:
        h.client_id ??
        h.client_fk ??
        h.client_pk ??
        (typeof h.client === 'number' ? h.client : null) ??
        null,
      clientName: (
        h.client_name ??
        h.clientname ??
        h.name ??
        (typeof h.client === 'string' ? h.client : '') ??
        ''
      ).toString(),
      description: (
        h.trx_description ??
        h.description ??
        h.trx_desc ??
        h.narration ??
        h.trx_narration ??
        h.particulars ??
        h.remarks ??
        h.trx_remarks ??
        ''
      ).toString(),
      // the history feed may name the client only by its code (5C4881), with no
      // client_name at all, so this is the join that has to work
      uniqueId: (h.unique_id ?? h.client_unique_id ?? h.client_code ?? h.uniqueid ?? '').toString(),
      reference: (h.reference_no ?? h.invoice_num ?? h.reference ?? '').toString(),
      date: h.trx_date ?? h.payment_date ?? h.date ?? h.created_at ?? null,
      amount: num(h.trx_amt ?? h.amount ?? h.amt ?? h.trx_amount),
    }))
    .filter((h) => h.description && (h.clientId != null || h.clientName || h.uniqueId))
    .map((h) => ({ ...h, tokens: descTokens(h.description), phones: extractPhones(h.description) }));

  // How many DISTINCT clients carry each word. Counting rows was wrong: a client
  // that pays every month puts its own name on a dozen narrations, which made its
  // signature word look common and got its history thrown away — the opposite of
  // what should happen. "QRG" belongs to one client however often it appears;
  // "MEDICARE" is spread across dozens.
  const clientsPerToken = new Map();
  rows.forEach((h) => {
    const owner = historyKeysOf(h)[0] || '';
    h.tokens.forEach((t) => {
      let owners = clientsPerToken.get(t);
      if (!owners) {
        owners = new Set();
        clientsPerToken.set(t, owners);
      }
      if (owner) owners.add(owner);
    });
  });
  const df = new Map();
  clientsPerToken.forEach((owners, t) => df.set(t, owners.size));

  // A mobile number in a narration is the payer, not a word — and it is dropped
  // from the word list for being all digits. Index it separately: a number seen
  // on one payer's past narrations identifies them outright, whatever else the
  // narration says. An aggregator's number reaches many clients and identifies none.
  const clientsPerPhone = new Map();
  rows.forEach((h) => {
    const owner = historyKeysOf(h)[0] || '';
    h.phones.forEach((n) => {
      let owners = clientsPerPhone.get(n);
      if (!owners) {
        owners = new Set();
        clientsPerPhone.set(n, owners);
      }
      if (owner) owners.add(owner);
    });
  });

  const clientCount = new Set(rows.map((h) => historyKeysOf(h)[0] || '')).size;
  return { rows, df, clientsPerPhone, total: rows.length, clientCount };
};

const HISTORY_MIN_SIMILARITY = 0.5; // at least half the distinctive words must line up
// A word can name a SET of clients, not just one: a parent entity pays for several
// units, so "SAHRUDAYA" legitimately sits on every branch's narrations. Allow a
// small set outright, and scale with the book so a corpus-wide word like
// "MEDICARE" is still recognised as describing an industry rather than a payer.
const MAX_CLIENTS_PER_IDENTIFYING_WORD = 5;

// Keys a history row can be found under, mirroring how clients are keyed elsewhere.
const uidKey = (v) => (v ?? '').toString().trim().toUpperCase();

const historyKeysOf = (h) => {
  const keys = [];
  const uid = uidKey(h.uniqueId);
  if (uid) keys.push(`uid:${uid}`);
  if (h.clientId != null && h.clientId !== '') keys.push(`id:${String(h.clientId).trim()}`);
  const nameKey = normalizeText(h.clientName);
  if (nameKey) keys.push(`name:${nameKey}`);
  return keys;
};

// Best previous payment per client for the narration in hand.
const historyMatchesFor = (index, desc) => {
  const byKey = new Map();
  const tokens = descTokens(desc);
  const rows = index?.rows || [];
  const df = index?.df || new Map();
  const perPhone = index?.clientsPerPhone || new Map();
  const phones = extractPhones(desc);
  if ((tokens.size === 0 && phones.length === 0) || rows.length === 0) return byKey;
  // A word held by a handful of clients still names a payer — or a group of them,
  // which is the honest answer when a parent entity pays for several units. The
  // limit is a flat count, deliberately: scaling it with the book let a word on
  // 44 different clients count as identifying once the book reached 2,200.
  const isRare = (t) => {
    const owners = df.get(t) || 0;
    return owners > 0 && owners <= MAX_CLIENTS_PER_IDENTIFYING_WORD;
  };

  // Keep the best match per client, counting how many past payments matched.
  const record = (h, similarity, shared) => {
    historyKeysOf(h).forEach((key) => {
      const e = byKey.get(key);
      if (!e) {
        byKey.set(key, {
          similarity,
          count: 1,
          shared,
          description: h.description,
          date: h.date,
          amount: h.amount,
          clientName: h.clientName,
          reference: h.reference,
        });
        return;
      }
      e.count += 1;
      if (similarity > e.similarity) {
        e.similarity = similarity;
        e.shared = shared;
        e.description = h.description;
        e.date = h.date;
        e.amount = h.amount;
        e.reference = h.reference;
      }
    });
  };
  rows.forEach((h) => {
    // The same mobile number on a past narration is the same payer. Checked
    // first, and it stands on its own: the words around a UPI handle
    // ("Payment from Ph", "@ybl") say nothing about who paid.
    const samePhone = phones.filter(
      (n) => h.phones.includes(n) && (perPhone.get(n)?.size ?? 0) <= MAX_CLIENTS_PER_IDENTIFYING_WORD
    );
    if (samePhone.length > 0) {
      record(h, 1, samePhone.map((n) => `phone ${n}`));
      return;
    }
    const { similarity, shared } = descOverlap(tokens, h.tokens);
    if (similarity < HISTORY_MIN_SIMILARITY) return;
    // Generic wording in common proves nothing — "MEDICARE" or "VENDOR PAYMENT"
    // appear on every second narration. At least one shared word must be rare
    // enough to point at a single payer.
    if (!shared.some(isRare)) return;
    // and a lone rare word must be a real one, not a three-letter fragment,
    // unless a second word backs it up
    if (shared.length < 2 && !shared.some((t) => isRare(t) && t.length >= 3)) return;
    record(h, similarity, shared);
  });
  return byKey;
};

// Look a client up by id first, then by name, so either feed shape works.
const historyMatchOf = (byKey, client) => {
  if (!byKey || byKey.size === 0) return undefined;
  // the client code first: the two feeds may number their clients differently,
  // but 5C4881 is 5C4881 in both
  const uid = uidKey(client.unique_id ?? client.client_unique_id);
  if (uid) {
    const hit = byKey.get(`uid:${uid}`);
    if (hit) return hit;
  }
  const id = client.client_id ?? client.client_fk;
  if (id != null && id !== '') {
    const hit = byKey.get(`id:${String(id).trim()}`);
    if (hit) return hit;
  }
  const nameKey = normalizeText(client.client_name);
  return nameKey ? byKey.get(`name:${nameKey}`) : undefined;
};

// A perfect history match scores like a full name match, plus a little for repeats.
const historyScoreOf = (m) => (m ? Math.round(m.similarity * 12) + Math.min((m.count || 1) - 1, 3) : 0);

// ── invoice / billing-period hints in the narration ──
// Narrations often name the bill being paid: "0001JUL26R4985", "July26pa",
// "Excel scans june invoic", "5C JULY BILL". Both the invoice number and the
// month it belongs to are strong pointers to the right client.
const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8, sep: 9, sept: 9,
  september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

// letters and digits only, so "5C/JUL-26/R4985" and "0001JUL26R4985" line up
const compact = (v) => (v ?? '').toString().toUpperCase().replace(/[^A-Z0-9]/g, '');

// month (+ optional 2 or 4 digit year) mentioned anywhere in the narration
const periodsInText = (text) => {
  const out = new Set();
  const t = (text ?? '').toString().toLowerCase();
  const re = /(january|february|march|april|june|july|august|september|october|november|december|jan|feb|mar|apr|may|jun|jul|aug|sept|sep|oct|nov|dec)[^a-z0-9]{0,3}(\d{2,4})?/g;
  let m = re.exec(t);
  while (m) {
    const month = MONTHS[m[1]];
    if (month) {
      let year = m[2] ? Number(m[2]) : null;
      if (year !== null && year < 100) year += 2000;
      out.add(`${month}-${year ?? 'any'}`);
    }
    m = re.exec(t);
  }
  return out;
};

const invoicePeriod = (inv) => {
  const d = inv?.inv_date ? new Date(inv.inv_date) : null;
  if (d && !Number.isNaN(d.getTime())) return { month: d.getMonth() + 1, year: d.getFullYear() };
  // no date — read it off a reference like 5C/JUL-26/R4985
  const m = compact(inv?.reference_no).match(/(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(\d{2})/);
  if (!m) return null;
  return { month: MONTHS[m[1].toLowerCase()], year: 2000 + Number(m[2]) };
};

/**
 * Does the narration point at one of this client's invoices?
 * Returns the strongest hit: the invoice number itself, or the billing month.
 */
const invoiceMatchInfo = (client, description) => {
  const hay = compact(description);
  if (!hay) return { score: 0, reason: null, invoice: null };
  const periods = periodsInText(description);

  let best = { score: 0, reason: null, invoice: null };
  (client.invoices || []).forEach((inv) => {
    const ref = compact(inv.reference_no);
    if (ref.length >= 6) {
      // the whole reference, or its tail once the "5C" style prefix is dropped
      const tail = ref.replace(/^5C/, '');
      if (hay.includes(ref) || (tail.length >= 6 && hay.includes(tail))) {
        if (best.score < 14) best = { score: 14, reason: 'invoice no in description', invoice: inv.reference_no };
        return;
      }
    }
    if (periods.size === 0) return;
    const p = invoicePeriod(inv);
    if (!p) return;
    const exact = periods.has(`${p.month}-${p.year}`);
    const monthOnly = periods.has(`${p.month}-any`);
    if (exact && best.score < 6) {
      best = { score: 6, reason: `${MONTH_LABEL[p.month]} ${String(p.year).slice(2)} bill`, invoice: inv.reference_no };
    } else if (monthOnly && best.score < 4) {
      best = { score: 4, reason: `${MONTH_LABEL[p.month]} bill`, invoice: inv.reference_no };
    }
  });
  return best;
};

const MONTH_LABEL = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

// Amount match between a due value and the payment amount.
const amountMatch = (due, txnAmt) => {
  if (!(due > 0) || !(txnAmt > 0)) return { score: 0, reason: null };
  const diff = Math.abs(due - txnAmt);
  if (diff < 1) return { score: 5, reason: 'exact amount' };
  if (diff / txnAmt <= 0.02) return { score: 3, reason: 'amount ≈ match' };
  if (diff / txnAmt <= 0.1) return { score: 1, reason: 'amount close' };
  return { score: 0, reason: null };
};

const clientKeyOf = (inv) => inv.client_id ?? inv.client_name ?? '';

// Score a client as a match for a transaction: client (or group) name found in the
// payment description, plus the best amount match across the client's invoices and
// their total outstanding.
const scoreClientMatch = (client, descNorm, txnAmt, historyMatch, rawDescription) => {
  const descCompact = (descNorm ?? '').replace(/ /g, '');
  let nameScore = 0;
  const reasons = [];

  const clientNorm = normalizeText(client.client_name);
  const nameInfo = nameMatchInfo(clientNorm, descNorm, descCompact);
  if (nameInfo.hits === 999) {
    nameScore += 12 + positionBonus(nameInfo.ratio);
    reasons.push('name in description');
  } else if (nameInfo.hits > 0) {
    nameScore += 5 * nameInfo.hits + positionBonus(nameInfo.ratio);
    reasons.push(nameInfo.hits === 1 ? 'name match' : 'strong name match');
  } else {
    // no direct word match — try acronym (e.g. "BHS" ↔ "Brahmadanda Healthcare Solut…")
    const acr = acronymMatchInfo(clientNorm, descNorm);
    if (acr.hit) {
      nameScore += 6 + positionBonus(acr.ratio);
      reasons.push('acronym match');
    }
  }

  const groupInfo = nameMatchInfo(normalizeText(client.group_name), descNorm, descCompact);
  if (groupInfo.hits === 999) {
    nameScore += 8 + positionBonus(groupInfo.ratio);
    reasons.push('group in description');
  } else if (groupInfo.hits > 0 && nameInfo.hits === 0) {
    nameScore += 3 * groupInfo.hits + positionBonus(groupInfo.ratio);
    reasons.push('group match');
  }

  // best amount match: any single invoice due, or the client's total outstanding
  let best = amountMatch(client.totalDue, txnAmt);
  client.invoices.forEach((inv) => {
    const m = amountMatch(num(inv.due), txnAmt);
    if (m.score > best.score) best = m;
  });
  if (best.reason) reasons.push(best.reason);

  // the narration may name the bill itself — an invoice number is nearly proof,
  // a billing month is a decent nudge
  const invHit = invoiceMatchInfo(client, rawDescription ?? descNorm);
  if (invHit.reason) reasons.push(invHit.reason);

  // a payment from the same payer that was mapped to this client before is the
  // strongest signal available, so it is scored alongside name and amount
  const historyScore = historyScoreOf(historyMatch);
  if (historyScore > 0) {
    reasons.push(historyMatch.count > 1 ? `paid before (${historyMatch.count}x)` : 'paid before');
  }

  return {
    nameScore,
    amountScore: best.score,
    historyScore,
    invoiceScore: invHit.score,
    invoiceHit: invHit.invoice,
    historyMatch: historyMatch || null,
    score: nameScore + best.score + historyScore + invHit.score,
    reasons,
  };
};

// group the saved allocations by client for the expanded view; client sub-headers
// only render when a single payment was split across more than one client (group payment)
const groupAllocsByClient = (allocs) => {
  const list = allocs || [];
  const multi = uniqueClientCount(list) > 1;
  const map = new Map();
  list.forEach((a) => {
    const client = a.client_name || a.client_id || '—';
    if (!map.has(client)) map.set(client, { key: client, client, items: [], payment: 0, tds: 0 });
    const o = map.get(client);
    o.items.push(a);
    o.payment += num(a.allocated);
    o.tds += num(a.tds_amount);
  });
  return Array.from(map.values()).map((g) => ({ ...g, showHeader: multi }));
};

// Status from the TOTAL mapped amount (server already-mapped + this session) vs the
// transaction amount, so mapping the remaining of a partially-mapped txn reads as fully mapped.
const statusByMapped = (totalMapped, amount) => {
  if (totalMapped <= 0) return MAP_STATUS.UNMAPPED;
  // within ₹1 rounding tolerance counts as fully mapped
  if (totalMapped + 1 >= num(amount)) return MAP_STATUS.MAPPED;
  return MAP_STATUS.PARTIAL;
};

// Map the API's mapping_status onto our status palette:
//   'mapped' → Fully Mapped, 'partially_mapped' → Partially Mapped, null/other → Unmapped.
// Credits that are not invoice payments. Recorded through the same mapping
// endpoint with mapping_type 'other', the category standing in for the invoice.
const OTHER_MAPPING_CATEGORIES = [
  {
    id: 'inter_account_transfer',
    label: 'Inter-account transfer',
    hint: 'movement between our own accounts — not client revenue',
  },
  {
    id: 'interest_bank_credit',
    label: 'Interest / bank credit',
    hint: 'interest, refunds or reversals credited by the bank',
  },
  {
    id: 'other_income',
    label: 'Other income / miscellaneous',
    hint: 'anything else — a remark is required',
    remarkRequired: true,
  },
];

// Parked payments go through the same endpoint as the non-invoice ones, under
// their own status, so they can be picked up and mapped later.
const SUSPENSE_STATUS = 'suspense';

// The backend may report a parked payment in mapping_status, or leave that as
// 'other' and put 'suspense' in its own field — accept either.
const isSuspenseRow = (r) =>
  [r?.mapping_status, r?.other_status, r?.trx_status, r?.status_type].some(
    (v) => (v ?? '').toString().toLowerCase().trim() === SUSPENSE_STATUS
  );

// ── search ──
// Every value a row puts on screen, flattened into one lowercase string. Amounts
// and dates go in twice — raw and formatted — so "163904", "1,63,904" and
// "Aug 01, 2026" all match. Built once per row, not per keystroke.
/**
 * Is this payment inside the chosen date range?
 *
 * Each endpoint decides for itself which column it filters on — some use the
 * mapping's created_at rather than the payment's own date — so the range was
 * meaning different things on different tabs. This makes the UI agree with its
 * label: everything is filtered on the PAYMENT date. Compared date-only, both
 * ends inclusive. A row with no readable date is kept, because dropping data on
 * a missing field is worse than showing it.
 */
const inPaymentRange = (value, from, to) => {
  if (!from && !to) return true;
  const raw = (value ?? '').toString();
  if (!raw) return true;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return true;
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
};

const searchHaystack = (parts) =>
  (parts || [])
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map((v) => String(v).toLowerCase())
    .join(' | ');

// space-separated terms all have to match, so "aditya 163904" narrows down
const matchesQuery = (hay, query) => {
  const terms = (query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  if (!hay) return false;
  return terms.every((t) => hay.includes(t));
};

// the amount as typed and as displayed
const amountTokens = (v) => {
  const n = num(v);
  if (!n) return [];
  return [n, formatINR(n), Math.round(n)];
};

const dateTokens = (v) => (v ? [v, fmtDate(v), fmtDateTime(v)] : []);

// Which bank the credit came through. Statement uploads build the id as AXIS +
// the bank reference, so the prefix is the signal; bank_type is honoured when the
// API sends it. Anything else is treated as the ICICI account, matching the two
// bank ledgers used in Tally.
const BANK_TYPE_OPTIONS = ['AXIS', 'ICICI'];
const bankTypeOf = (row) => {
  const declared = (row?.bank_type ?? '').toString().trim().toUpperCase();
  if (BANK_TYPE_OPTIONS.includes(declared)) return declared;
  return (row?.trx_id ?? '').toString().trim().toUpperCase().startsWith('AXIS') ? 'AXIS' : 'ICICI';
};

// A transaction can be acknowledged once it is fully mapped. Module level so the
// rule is shared and never caught by declaration order inside a component.
const canAckLine = (l) => !!l?.trx_id && (l?.mappingStatusRaw ?? '') !== 'partially_mapped';

// ── tally sync / mail sent status ──
// these flags may arrive as a boolean, a number, or a word — read them all
const flagState = (raw) => {
  if (raw === null || raw === undefined || raw === '') return 'NONE';
  if (typeof raw === 'boolean') return raw ? 'YES' : 'NO';
  if (typeof raw === 'number') return raw > 0 ? 'YES' : 'NO';
  const s = String(raw).toLowerCase().trim();
  if (['y', 'yes', 'true', '1', 'synced', 'success', 'done', 'completed', 'sent'].includes(s)) return 'YES';
  if (['failed', 'fail', 'error', 'bounced', 'rejected'].includes(s)) return 'FAILED';
  return 'NO';
};

const TALLY_STATUS = {
  SYNCED: { key: 'SYNCED', label: 'Synced', color: '#16a34a', bg: '#ecfdf5', border: '#bbf7d0', Icon: CheckCircleIcon },
  PENDING: { key: 'PENDING', label: 'Not synced', color: '#b45309', bg: '#fffbeb', border: '#fde68a', Icon: DonutLargeIcon },
  FAILED: { key: 'FAILED', label: 'Failed', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', Icon: ErrorOutlineIcon },
  UNKNOWN: { key: 'UNKNOWN', label: '—', color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', Icon: null },
};

const MAIL_STATUS = {
  SENT: { key: 'SENT', label: 'Sent', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc', Icon: MarkEmailReadIcon },
  PENDING: { key: 'PENDING', label: 'Not sent', color: '#b45309', bg: '#fffbeb', border: '#fde68a', Icon: MailOutlineIcon },
  FAILED: { key: 'FAILED', label: 'Failed', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', Icon: ErrorOutlineIcon },
  UNKNOWN: { key: 'UNKNOWN', label: '—', color: '#94a3b8', bg: '#f8fafc', border: '#e2e8f0', Icon: null },
};

// a missing field means "not reported", which is not the same as "not done" —
// so it renders as a dash rather than a red flag
const pickStatus = (raw, at, states) => {
  const s = flagState(raw);
  const base =
    s === 'YES' ? states.done : s === 'FAILED' ? states.failed : s === 'NO' ? states.pending : at ? states.done : states.unknown;
  return { ...base, at };
};

const tallyStatusOf = (r) =>
  pickStatus(
    r.tally_sync ?? r.is_tally_synced ?? r.tally_synced ?? r.tally_status ?? r.tally,
    r.tally_synced_at ?? r.tally_sync_date ?? r.tally_synced_on ?? r.synced_at ?? null,
    { done: TALLY_STATUS.SYNCED, pending: TALLY_STATUS.PENDING, failed: TALLY_STATUS.FAILED, unknown: TALLY_STATUS.UNKNOWN }
  );

const mailStatusOf = (r) =>
  pickStatus(
    r.mail_sent ?? r.is_mail_sent ?? r.mail_status ?? r.ack_sent ?? r.acknowledgement_sent ?? r.email_sent,
    r.mail_sent_at ?? r.mail_sent_on ?? r.ack_sent_at ?? r.acknowledgement_sent_at ?? r.email_sent_at ?? null,
    { done: MAIL_STATUS.SENT, pending: MAIL_STATUS.PENDING, failed: MAIL_STATUS.FAILED, unknown: MAIL_STATUS.UNKNOWN }
  );

// shared renderer for the Tally Sync / Mail Sent cells
function StatusChip({ status }) {
  if (status.key === 'UNKNOWN') return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>—</Typography>;
  const Icon = status.Icon;
  return (
    <>
      <Chip
        size="small"
        icon={Icon ? <Icon style={{ fontSize: 13, color: status.color }} /> : undefined}
        label={status.label}
        title={status.at ? fmtDateTime(status.at) : undefined}
        sx={{
          backgroundColor: status.bg,
          color: status.color,
          border: `1px solid ${status.border}`,
          fontSize: '10px',
          fontWeight: 600,
          height: 20,
          '& .MuiChip-icon': { marginLeft: '4px' },
        }}
      />
      {status.at && <Typography sx={{ fontSize: '9px', color: '#94a3b8', mt: 0.25 }}>{fmtDate(status.at)}</Typography>}
    </>
  );
}

// A credit marked as "not an invoice payment" via /map_other_payments/. The
// status is an id from OTHER_MAPPING_CATEGORIES; older rows may only carry
// mapping_type 'other'.
const otherInfoOf = (row) => {
  const raw = (row.mapping_status ?? '').toString().toLowerCase().trim();
  const key = row._mapStatus?.key;
  const isSuspense = raw === SUSPENSE_STATUS || key === 'SUSPENSE' || isSuspenseRow(row);
  const isOther = raw === 'other' || key === 'OTHER' || isSuspense;
  if (!isOther) return { isOther: false, isSuspense: false };
  if (isSuspense) {
    return {
      isOther: true,
      isSuspense: true,
      label: 'In suspense',
      reason: (row.other_reason ?? row.notes ?? '').toString().trim(),
    };
  }
  // which head it was booked under, when the API reports it
  const status = (row.other_status ?? row.other_type ?? '').toString().trim();
  const cat = OTHER_MAPPING_CATEGORIES.find((c) => c.id === status);
  return {
    isOther: true,
    isSuspense: false,
    label: cat ? cat.label : status ? titleCase(status) : 'Other',
    reason: (row.other_reason ?? row.notes ?? '').toString().trim(),
  };
};

// Row order for the payments table: work first, then done, then the credits that
// were never invoice payments.
const MAP_ORDER_RANK = { UNMAPPED: 0, PARTIAL: 1, MAPPED: 2, SUSPENSE: 3, OTHER: 4 };
const mapOrderRank = (row) => MAP_ORDER_RANK[row._mapStatus?.key] ?? 0;

const statusFromApi = (mappingStatus) => {
  const s = (mappingStatus ?? '').toString().toLowerCase().trim();
  if (s === 'mapped') return MAP_STATUS.MAPPED;
  if (s === 'partially_mapped') return MAP_STATUS.PARTIAL;
  if (s === 'other') return MAP_STATUS.OTHER;
  if (s === 'suspense') return MAP_STATUS.SUSPENSE;
  return MAP_STATUS.UNMAPPED;
};

// ───────────────────────── stat card ─────────────────────────
function StatCard({ label, value, hint, palette }) {
  const p = palette || { bg: '#eef2ff', border: '#3b82f6', color: '#1e40af', Icon: PaymentsIcon };
  const Icon = p.Icon;
  return (
    <Box
      title={hint || undefined}
      sx={{
        backgroundColor: p.bg,
        borderRadius: '6px',
        padding: '4px 10px',
        borderLeft: `3px solid ${p.border}`,
        flex: '1 1 132px',
        minWidth: 118,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        {Icon && <Icon sx={{ fontSize: 13, color: p.color }} />}
        <Typography
          noWrap
          sx={{ fontSize: '9px', color: '#64748b', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '16px', fontWeight: 700, color: p.color, lineHeight: 1.15 }}>
        {value}
      </Typography>
    </Box>
  );
}

function MapStatusChip({ status }) {
  const Icon = status.Icon;
  return (
    <Chip
      size="small"
      icon={<Icon style={{ fontSize: 14, color: status.color }} />}
      label={status.label}
      sx={{
        backgroundColor: status.bg,
        color: status.color,
        fontSize: '11px',
        fontWeight: 600,
        height: 22,
        border: `1px solid ${status.border}`,
        '& .MuiChip-icon': { marginLeft: '4px' },
      }}
    />
  );
}

// ───────────────────────── audit trail ─────────────────────────
const titleCase = (s) =>
  (s ?? '')
    .toString()
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

// the backend stores codes; people read words
const STATUS_WORDS = {
  MAPPED: 'Fully matched',
  FULLY_MAPPED: 'Fully matched',
  PARTIAL: 'Partly matched',
  PARTIALLY_MAPPED: 'Partly matched',
  PARTIALLY_MAPPED_STATUS: 'Partly matched',
  UNMAPPED: 'Not matched yet',
  NOT_MAPPED: 'Not matched yet',
};
const statusWords = (v) =>
  v === null || v === undefined || v === '' ? 'Not matched yet' : STATUS_WORDS[String(v).toUpperCase()] || titleCase(v);

const TYPE_WORDS = { manual_type: 'entered by a person', manual: 'entered by a person', auto: 'matched automatically' };
const typeWords = (v) => (v ? TYPE_WORDS[String(v).toLowerCase()] || titleCase(v) : null);

const AUDIT_META = {
  STATUS_CHANGED: { label: 'Status update', color: '#1e40af', bg: '#eef2ff', border: '#c7d2fe', Icon: DonutLargeIcon },
  MAPPING_CREATED: { label: 'Invoice matched', color: '#16a34a', bg: '#ecfdf5', border: '#bbf7d0', Icon: AddLinkIcon },
  MAPPING_DELETED: { label: 'Match removed', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', Icon: LinkOffIcon },
  MAPPING_UPDATED: { label: 'Match edited', color: '#d97706', bg: '#fffbeb', border: '#fde68a', Icon: PercentIcon },
  ACKNOWLEDGEMENT_SENT: { label: 'Receipt sent', color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc', Icon: CheckCircleIcon },
};

const auditMeta = (action) =>
  AUDIT_META[action] || {
    label: titleCase(action) || 'Activity',
    color: '#475569',
    bg: '#f1f5f9',
    border: '#e2e8f0',
    Icon: ReceiptLongIcon,
  };

// "2 days ago" reads faster than a timestamp when scanning a history
const relTime = (iso) => {
  const t = new Date(iso || 0).getTime();
  if (!t || Number.isNaN(t)) return '';
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'} ago`;
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return plural(mins, 'minute');
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return plural(hrs, 'hour');
  const days = Math.round(hrs / 24);
  if (days < 30) return plural(days, 'day');
  const months = Math.round(days / 30);
  if (months < 12) return plural(months, 'month');
  return plural(Math.round(months / 12), 'year');
};

// who did it — falls back to whoever is recorded inside the payload
const actorOf = (ev) =>
  ev.performed_by ||
  ev.new_value?.created_by ||
  ev.new_value?.updated_by ||
  ev.old_value?.created_by ||
  ev.old_value?.updated_by ||
  null;

const personName = (a) => {
  if (!a) return 'the system';
  const local = String(a).split('@')[0].replace(/[._-]+/g, ' ').trim();
  return local ? titleCase(local) : String(a);
};

const invoiceOf = (ev) => ev.invoice_num || ev.new_value?.invoice_num || ev.old_value?.invoice_num || null;
const amountOf = (ev) => ev.amount ?? ev.new_value?.amt ?? ev.old_value?.amt ?? null;

// one plain-English sentence per raw event, used inside the details panel
const describeEvent = (ev) => {
  const inv = invoiceOf(ev);
  const amt = amountOf(ev);
  const money = amt != null ? formatINR(amt) : null;
  switch (ev.action) {
    case 'MAPPING_CREATED':
      return `Matched${money ? ` ${money}` : ''} to invoice ${inv || '(not recorded)'}`;
    case 'MAPPING_DELETED':
      return `Removed the match${money ? ` of ${money}` : ''} from invoice ${inv || '(not recorded)'}`;
    case 'MAPPING_UPDATED':
      return `Edited the match on invoice ${inv || '(not recorded)'}`;
    case 'ACKNOWLEDGEMENT_SENT':
      return 'Sent the payment receipt to the client';
    case 'STATUS_CHANGED': {
      const before = statusWords(ev.old_value?.mapping_status);
      const after = statusWords(ev.new_value?.mapping_status);
      return before === after ? `Status confirmed as “${after}”` : `Status changed from “${before}” to “${after}”`;
    }
    default:
      return titleCase(ev.action) || 'Activity recorded';
  }
};

// a request_id groups every row the backend wrote for one user action, so the
// headline describes the action as a whole rather than each database write
const summarizeGroup = (items) => {
  const created = items.filter((i) => i.action === 'MAPPING_CREATED');
  const deleted = items.filter((i) => i.action === 'MAPPING_DELETED');
  const status = items.find((i) => i.action === 'STATUS_CHANGED');
  const sum = (arr) => arr.reduce((t, i) => t + num(amountOf(i)), 0);
  const invoiceWord = (n) => `${n} invoice${n === 1 ? '' : 's'}`;

  let meta;
  let title;
  let detail;

  if (created.length && deleted.length) {
    meta = auditMeta('MAPPING_UPDATED');
    title = `Invoice matching was changed`;
    detail = `${invoiceWord(created.length)} added (${formatINR(sum(created))}), ${invoiceWord(deleted.length)} removed (${formatINR(sum(deleted))})`;
  } else if (created.length) {
    meta = auditMeta('MAPPING_CREATED');
    title = `This payment was matched to ${invoiceWord(created.length)}`;
    detail = `${formatINR(sum(created))} of the payment was used to settle ${created.length === 1 ? 'the invoice' : 'these invoices'}`;
  } else if (deleted.length) {
    meta = auditMeta('MAPPING_DELETED');
    title = `The match with ${invoiceWord(deleted.length)} was removed`;
    detail = `${formatINR(sum(deleted))} went back to being unmatched`;
  } else if (status) {
    meta = auditMeta('STATUS_CHANGED');
    const before = statusWords(status.old_value?.mapping_status);
    const after = statusWords(status.new_value?.mapping_status);
    title = before === after ? `Marked as “${after}”` : `Status changed to “${after}”`;
    detail = before === after ? null : `It was “${before}” before this`;
  } else {
    meta = auditMeta(items[0].action);
    title = describeEvent(items[0]);
    detail = null;
  }

  // how the match was made, when the backend recorded it
  const how = typeWords(status?.new_value?.mapping_type);
  if (how && (created.length || deleted.length || status)) {
    detail = detail ? `${detail} · ${how}` : titleCase(how);
  }

  const lines = [...created, ...deleted]
    .filter((i) => invoiceOf(i) || amountOf(i) != null)
    .map((i) => ({
      invoice: invoiceOf(i) || '(invoice not recorded)',
      amount: num(amountOf(i)),
      tds: num(i.new_value?.tds_amt ?? i.old_value?.tds_amt),
      added: i.action === 'MAPPING_CREATED',
    }));

  const notes = Array.from(new Set(items.map((i) => i.notes).filter(Boolean)));
  const actor = actorOf(items.find((i) => actorOf(i)) || {});

  return { meta, title, detail, lines, notes, actor };
};

function AuditEventCard({ group, index, isLast }) {
  const { meta, title, detail, lines, notes, actor } = useMemo(() => summarizeGroup(group.items), [group]);
  const Icon = meta.Icon;
  const when = group.items[0]?.created_at;

  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'stretch' }}>
      {/* timeline rail */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            backgroundColor: meta.bg,
            border: `2px solid ${meta.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 16, color: meta.color }} />
        </Box>
        {!isLast && <Box sx={{ flex: 1, width: 2, backgroundColor: '#e2e8f0', minHeight: 12, my: 0.5 }} />}
      </Box>

      {/* what happened */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          mb: 1.75,
          border: '1px solid #e2e8f0',
          borderLeft: `3px solid ${meta.color}`,
          borderRadius: '8px',
          backgroundColor: '#fff',
          p: 1.25,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#0f172a', lineHeight: 1.35 }}>
              <Box component="span" sx={{ color: '#94a3b8', fontWeight: 700, mr: 0.75 }}>
                {index + 1}.
              </Box>
              {title}
            </Typography>
            {detail && (
              <Typography sx={{ fontSize: '12px', color: '#475569', mt: 0.25 }}>{detail}</Typography>
            )}
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
              {fmtDateTime(when)}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{relTime(when)}</Typography>
          </Box>
        </Box>

        {/* who */}
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mt: 0.75, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={actor ? `Done by ${personName(actor)}` : 'Done automatically by the system'}
            title={actor || 'No user recorded — this was an automatic change'}
            sx={{ backgroundColor: '#f1f5f9', color: '#475569', fontSize: '10.5px', fontWeight: 600, height: 20 }}
          />
          {notes.map((n) => (
            <Chip
              key={n}
              size="small"
              label={`Reason: ${n}`}
              sx={{ backgroundColor: '#fffbeb', color: '#b45309', fontSize: '10.5px', fontWeight: 600, height: 20, border: '1px solid #fde68a' }}
            />
          ))}
        </Box>

        {/* invoice-by-invoice breakdown */}
        {lines.length > 0 && (
          <Box sx={{ mt: 1, backgroundColor: '#f8faff', border: '1px solid #eef2f7', borderRadius: '6px', px: 1, py: 0.5 }}>
            {lines.map((l, i) => (
              <Box
                key={`${l.invoice}-${i}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, flexWrap: 'wrap', borderTop: i === 0 ? 'none' : '1px dashed #e8eef7' }}
              >
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 700, color: l.added ? '#16a34a' : '#dc2626', width: 14, textAlign: 'center', flexShrink: 0 }}
                  title={l.added ? 'Added' : 'Removed'}
                >
                  {l.added ? '+' : '−'}
                </Typography>
                <Typography sx={{ fontSize: '10px', color: '#64748b', flexShrink: 0 }}>Invoice</Typography>
                <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, color: '#334155', flex: '1 1 150px', wordBreak: 'break-all' }}>
                  {l.invoice}
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: l.added ? '#16a34a' : '#dc2626', whiteSpace: 'nowrap' }}>
                  {formatINR(l.amount)}
                </Typography>
                {l.tds > 0 && (
                  <Typography sx={{ fontSize: '10px', color: '#7c3aed', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    incl. {formatINR(l.tds)} TDS
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function AuditLogDialog({ open, trxId, transaction, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [newestFirst, setNewestFirst] = useState(false);

  useEffect(() => {
    if (!open || !trxId) return undefined;
    const api = process.env.REACT_APP_GET_TRX_AUDIT_LOG;
    if (!api) {
      setError('API not configured (REACT_APP_GET_TRX_AUDIT_LOG)');
      setRows([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(api, { params: { trx_id: trxId } })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data) ? res.data : (res.data?.data ?? res.data?.logs ?? []);
        setRows(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.message || e.message || 'Could not load the history for this payment');
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, trxId, reload]);

  // rows written by one request are a single action — group them, oldest first
  const groups = useMemo(() => {
    const sorted = [...rows].sort(
      (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime() || num(a.id) - num(b.id)
    );
    const out = [];
    sorted.forEach((r) => {
      const key = r.request_id || `row-${r.id}`;
      const last = out[out.length - 1];
      if (last && last.key === key) last.items.push(r);
      else out.push({ key, items: [r] });
    });
    return out;
  }, [rows]);

  const summary = useMemo(() => {
    const created = rows.filter((r) => r.action === 'MAPPING_CREATED');
    const deleted = rows.filter((r) => r.action === 'MAPPING_DELETED');
    const statuses = rows
      .filter((r) => r.action === 'STATUS_CHANGED')
      .sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
    const times = rows.map((r) => new Date(r.created_at || 0).getTime()).filter((t) => t > 0);
    const people = Array.from(new Set(rows.map(actorOf).filter(Boolean)));
    return {
      created: created.length,
      deleted: deleted.length,
      latestStatus: statuses.length ? statuses[statuses.length - 1].new_value?.mapping_status : undefined,
      hasStatus: statuses.length > 0,
      first: times.length ? new Date(Math.min(...times)) : null,
      last: times.length ? new Date(Math.max(...times)) : null,
      people,
    };
  }, [rows]);

  const ordered = newestFirst ? [...groups].reverse() : groups;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', width: '92vw', maxWidth: 1000 } }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
            color: '#fff',
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <HistoryIcon sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>
              Transaction Mapping Logs
            </Typography>
            <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, opacity: 0.95 }}>
              {trxId ?? '—'}
              {transaction ? ` — ${formatINR(transaction.amount)}` : ''}
            </Typography>
            {transaction?.description && (
              <Typography sx={{ fontSize: '11px', opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {transaction.description}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setReload((n) => n + 1)} disabled={loading} sx={{ color: '#fff' }} title="Reload">
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2, backgroundColor: '#f8faff' }}>
        {/* one-paragraph plain-English recap of the whole trail */}
        {!loading && rows.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: '8px',
              p: 1.25,
              mb: 1.5,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 18, color: '#1e40af', mt: '1px', flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: '12.5px', color: '#1e293b', lineHeight: 1.5 }}>
                This payment has been changed <strong>{groups.length}</strong> time{groups.length === 1 ? '' : 's'}
                {summary.first ? (
                  <>
                    , starting <strong>{fmtDate(summary.first)}</strong> and last touched <strong>{relTime(summary.last)}</strong>
                  </>
                ) : null}
                .{' '}
                {summary.created > 0 && (
                  <>
                    It was matched to an invoice <strong>{summary.created}</strong> time{summary.created === 1 ? '' : 's'}
                    {summary.deleted > 0 ? ' and ' : '. '}
                  </>
                )}
                {summary.deleted > 0 && (
                  <>
                    a match was removed <strong>{summary.deleted}</strong> time{summary.deleted === 1 ? '' : 's'}.{' '}
                  </>
                )}
                {summary.hasStatus && (
                  <>
                    Its status right now is <strong>{statusWords(summary.latestStatus)}</strong>.
                  </>
                )}
              </Typography>
              {summary.people.length > 0 && (
                <Typography sx={{ fontSize: '11px', color: '#475569', mt: 0.5 }}>
                  People involved: {summary.people.map((p) => personName(p)).join(', ')}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* counts */}
        {!loading && rows.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <StatCard
              label="Changes"
              value={groups.length}
              hint={`${rows.length} record${rows.length === 1 ? '' : 's'} in total`}
              palette={{ bg: '#eef2ff', border: '#3b82f6', color: '#1e40af', Icon: ReceiptLongIcon }}
            />
            <StatCard
              label="Invoices matched"
              value={summary.created}
              palette={{ bg: '#ecfdf5', border: '#16a34a', color: '#16a34a', Icon: AddLinkIcon }}
            />
            <StatCard
              label="Matches removed"
              value={summary.deleted}
              palette={{ bg: '#fef2f2', border: '#dc2626', color: '#dc2626', Icon: LinkOffIcon }}
            />
            <StatCard
              label="Last change"
              value={summary.last ? fmtTimeOnly(summary.last) : '—'}
              hint={summary.last ? fmtDate(summary.last) : undefined}
              palette={{ bg: '#fffbeb', border: '#d97706', color: '#d97706', Icon: AccessTimeIcon }}
            />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1.5, fontSize: '12px' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={30} />
          </Box>
        ) : rows.length === 0 && !error ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
            <HistoryIcon sx={{ fontSize: 34, mb: 0.5, opacity: 0.5 }} />
            <Typography sx={{ fontSize: '13px' }}>Nothing has happened to this payment yet.</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', flex: 1 }}>
                What happened, step by step
              </Typography>
              <Button
                size="small"
                onClick={() => setNewestFirst((v) => !v)}
                sx={{ textTransform: 'none', fontSize: '11px', color: '#1e40af' }}
              >
                {newestFirst ? 'Show oldest first' : 'Show newest first'}
              </Button>
            </Box>
            {ordered.map((g, i) => (
              <AuditEventCard
                key={g.key}
                group={g}
                index={newestFirst ? ordered.length - 1 - i : i}
                isLast={i === ordered.length - 1}
              />
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontSize: '10.5px', color: '#94a3b8', flex: 1 }}>
          “Matched” means part of this payment was used to settle an invoice.
        </Typography>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748b' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────── invoice picker row ─────────────────────────
// hard cap on rows rendered in the picker at once — each row carries inputs,
// so rendering thousands of them freezes the dialog
const MAX_VISIBLE_INVOICE_ROWS = 300;

// memoized so typing in one allocation input doesn't re-render every other row
const InvoiceRow = memo(function InvoiceRow({ inv, sel, stripe, disabled, onToggle, onSetField, recommendedTds }) {
  const checked = !!sel;
  const tdsAmt = checked ? computeTds(tdsBaseOf(inv), sel.tds_pct) : 0;
  // What is still open on this invoice. TDS belongs with the payment, not with
  // what is owed: the client settles the invoice by paying (invoice - TDS) and
  // withholding the TDS, so allocation + TDS together clear the bill.
  const remaining =
    num(inv.invoice_amount) +
    num(inv.debit) -
    (num(inv.credit) + num(inv.prev_payment) + num(sel?.allocated) + tdsAmt);
  const numSx = { fontSize: '11px', textAlign: 'right', whiteSpace: 'nowrap' };
  return (
    <TableRow
      sx={{
        backgroundColor: checked ? '#f0fdf4' : stripe ? '#f8faff' : '#ffffff',
        opacity: disabled && !checked ? 0.55 : 1,
        '&:hover': { backgroundColor: checked ? '#dcfce7' : '#e8f4fd' },
      }}
    >
      <TableCell sx={{ padding: '0 4px' }}>
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled && !checked}
          onChange={() => onToggle(inv)}
          sx={{ p: 0.5 }}
        />
      </TableCell>
      <TableCell sx={{ fontSize: '11px' }}>
        <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, color: '#334155', wordBreak: 'break-all' }}>
          {inv.reference_no}
        </Typography>
        <Typography sx={{ fontSize: '10px', color: '#64748b' }}>
          {inv.client_name ?? inv.client_id ?? '—'}
          {inv.inv_date ? ` · ${fmtDate(inv.inv_date)}` : ''}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...numSx, color: '#475569' }}>{formatINR(inv.invoice_amount)}</TableCell>
      <TableCell sx={{ ...numSx, color: num(inv.credit) > 0 ? '#16a34a' : '#cbd5e1' }}>
        {num(inv.credit) > 0 ? formatINR(inv.credit) : '—'}
      </TableCell>
      <TableCell sx={{ ...numSx, color: num(inv.debit) > 0 ? '#dc2626' : '#cbd5e1' }}>
        {num(inv.debit) > 0 ? formatINR(inv.debit) : '—'}
      </TableCell>
      <TableCell sx={{ ...numSx, color: num(inv.prev_payment) > 0 ? '#475569' : '#cbd5e1' }}>
        {num(inv.prev_payment) > 0 ? formatINR(inv.prev_payment) : '—'}
      </TableCell>
      <TableCell sx={{ ...numSx, color: '#0059B2', fontWeight: 700 }}>{formatINR(inv.due)}</TableCell>
      <TableCell sx={{ padding: '4px' }}>
        <TextField
          size="small"
          type="number"
          value={sel?.allocated ?? ''}
          disabled={!checked}
          onChange={(e) => onSetField(inv.reference_no, 'allocated', e.target.value)}
          inputProps={{ min: 0, style: { fontSize: '11px', textAlign: 'right', padding: '4px 6px' } }}
          sx={{ width: 100, '& .MuiOutlinedInput-root': { backgroundColor: checked ? '#fff' : '#f8fafc' } }}
        />
      </TableCell>
      <TableCell sx={{ padding: '4px' }}>
        <TextField
          select
          size="small"
          value={TDS_OPTIONS.includes(String(sel?.tds_pct)) ? String(sel.tds_pct) : '0'}
          disabled={!checked}
          onChange={(e) => onSetField(inv.reference_no, 'tds_pct', e.target.value)}
          SelectProps={{ sx: { fontSize: '11px' } }}
          sx={{ width: 70, '& .MuiSelect-select': { padding: '4px 6px' } }}
        >
          {TDS_OPTIONS.map((t) => (
            <MenuItem key={t} value={t} sx={{ fontSize: '11px' }}>
              {t}%
            </MenuItem>
          ))}
        </TextField>
        {!checked && recommendedTds !== null && recommendedTds !== undefined && (
          <Typography
            sx={{ fontSize: '9px', fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap' }}
            title="The payment matches this invoice at this TDS rate — ticking the row applies it"
          >
            {recommendedTds}% suggested
          </Typography>
        )}
      </TableCell>
      <TableCell
        sx={{ ...numSx, fontWeight: 700, color: remaining > 0 ? '#dc2626' : remaining < 0 ? '#d97706' : '#16a34a' }}
        title={`(${num(inv.invoice_amount)} invoice + ${num(inv.debit)} debit) - (${num(inv.credit)} credit + ${num(inv.prev_payment)} prev payment + ${num(sel?.allocated)} adjusting now + ${tdsAmt} TDS)`}
      >
        {formatINR(remaining)}
      </TableCell>
      <TableCell sx={{ ...numSx, color: tdsAmt > 0 ? '#7c3aed' : '#cbd5e1', fontWeight: 600 }}>
        {tdsAmt > 0 ? formatINR(tdsAmt) : '—'}
        {tdsAmt > 0 && (
          // what this invoice is actually settled by: payment + TDS
          <Typography sx={{ fontSize: '9px', fontWeight: 700, color: '#0059B2', whiteSpace: 'nowrap' }}>
            = {formatINR(num(sel.allocated) + tdsAmt)}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
});

// ───────────────────────── mapping dialog ─────────────────────────
function MappingDialog({ open, transaction, invoices, invoicesLoading, initialAllocations, lockedMapped, saving, onClose, onSave, paymentHistory, historyInfo, clientPhones }) {
  const [search, setSearch] = useState('');
  // selected: { [reference_no]: { invoice, allocated, tds_pct } }
  const [selected, setSelected] = useState({});
  const [defaultTds, setDefaultTds] = useState('0');
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // groups collapsed by default
  const [clientFilter, setClientFilter] = useState(new Set()); // selected suggested clients
  const [groupFilter, setGroupFilter] = useState('all'); // filter by group_name

  const txnAmount = num(transaction?.amount);
  // amount already mapped outside this dialog (e.g. a partially-mapped transaction);
  // only the remaining unmapped amount can be allocated here.
  const alreadyMapped = Math.max(0, num(lockedMapped));
  const targetAmount = Math.max(0, txnAmount - alreadyMapped);

  useEffect(() => {
    if (!open) return;
    const seed = {};
    (initialAllocations || []).forEach((a) => {
      seed[a.reference_no] = {
        invoice: {
          reference_no: a.reference_no,
          client_name: a.client_name,
          client_id: a.client_id,
          group_name: a.group_name,
          due: a.due,
        },
        allocated: String(a.allocated ?? ''),
        tds_pct: String(a.tds_pct ?? '0'),
      };
    });
    setSelected(seed);
    setSearch('');
    setExpandedGroups(new Set());
    setClientFilter(new Set());
    setGroupFilter('all');
    // adopt the TDS rate already present on existing allocations, if any
    const existingTds = (initialAllocations || []).find((a) => num(a.tds_pct) > 0);
    setDefaultTds(existingTds ? String(existingTds.tds_pct) : '0');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction?.trx_id, transaction?._rid]);

  const groupOptions = useMemo(
    () =>
      Array.from(new Set(invoices.map((inv) => (inv.group_name ?? '').toString().trim()).filter(Boolean))).sort(),
    [invoices]
  );

  const searchLower = search.trim().toLowerCase();
  const filteredInvoices = useMemo(() => {
    let base = invoices;
    // filter by group_name (trim both sides so suggested-group values match)
    if (groupFilter !== 'all') {
      base = base.filter((inv) => (inv.group_name ?? '').toString().trim() === groupFilter);
    }
    // when suggested clients are selected, show only those clients' invoices
    if (clientFilter.size > 0) {
      base = base.filter((inv) => clientFilter.has(clientKeyOf(inv)));
    }
    if (!searchLower) return base;
    return base.filter(
      (inv) =>
        (inv.reference_no ?? '').toString().toLowerCase().includes(searchLower) ||
        (inv.client_name ?? '').toString().toLowerCase().includes(searchLower) ||
        (inv.client_id ?? '').toString().toLowerCase().includes(searchLower) ||
        (inv.group_name ?? '').toString().toLowerCase().includes(searchLower)
    );
  }, [invoices, searchLower, clientFilter, groupFilter]);

  // group invoices client-wise; the group_name (when present) is shown in the label
  const grouped = useMemo(() => {
    const map = new Map();
    filteredInvoices.forEach((inv) => {
      const key = `c:${inv.client_id ?? inv.client_name ?? 'other'}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          clientName: inv.client_name ?? 'Other',
          groupName: (inv.group_name ?? '').toString().trim(),
          isGroup: false,
          invoices: [],
          clients: new Set(),
          total: 0,
        });
      }
      const o = map.get(key);
      o.invoices.push(inv);
      const c = inv.client_id ?? inv.client_name;
      if (c) o.clients.add(c);
      o.total += num(inv.due);
    });
    // sort invoices within each group by invoice date ascending
    const arr = Array.from(map.values());
    arr.forEach((g) =>
      g.invoices.sort((a, b) => new Date(a.inv_date || 0) - new Date(b.inv_date || 0))
    );
    return arr.sort((a, b) => b.total - a.total);
  }, [filteredInvoices]);

  const selectedList = Object.values(selected);
  const allocated = selectedList.reduce((s, x) => s + num(x.allocated), 0);
  const tds = selectedList.reduce((s, x) => s + computeTds(tdsBaseOf(x.invoice), x.tds_pct), 0);
  const settled = allocated + tds;
  const remaining = targetAmount - allocated;
  const pctAllocated = targetAmount > 0 ? Math.min(100, (allocated / targetAmount) * 100) : 0;
  const overAllocated = allocated - targetAmount > 1;

  // per-client roll-up (this is what makes a single group payment legible)
  const byClient = useMemo(() => {
    const m = {};
    selectedList.forEach((x) => {
      const c = x.invoice.client_name || x.invoice.client_id || '—';
      if (!m[c]) m[c] = { allocated: 0, tds: 0, count: 0 };
      m[c].allocated += num(x.allocated);
      m[c].tds += computeTds(tdsBaseOf(x.invoice), x.tds_pct);
      m[c].count += 1;
    });
    return m;
  }, [selectedList]);
  const clientKeys = Object.keys(byClient);

  // Suggested clients for this transaction: client/group name found in the payment
  // description and/or one of the client's dues close to the payment amount.
  const descNorm = normalizeText(transaction?.description);

  // clients this payer has paid before, matched on narration wording
  const historyByClient = useMemo(
    () => historyMatchesFor(buildHistoryIndex(paymentHistory), transaction?.description),
    [paymentHistory, transaction]
  );

  // The invoice book, indexed once: name tokens, invoice dues, and every
  // TDS-adjusted expected amount. Rebuilt only when the invoice list changes.
  const recIndex = useMemo(
    () => buildRecommendationIndex(invoices, { tolerance: AMOUNT_TOLERANCE }),
    [invoices]
  );

  /**
   * Recommendations for this payment, straight from the engine:
   *   P0  phone number in the narration belongs to the client
   *   P1  name >= 90% or matching history, + invoice due
   *   P2  name >= 90% or matching history, + TDS on invoice amount
   *   P3  name >= 90% or matching history, no amount lines up
   *   P4  nothing identifies the payer, invoice due equals the payment
   *   P5  nothing identifies the payer, invoice less TDS equals the payment
   * Ties inside a band: better name %, then closest amount, then the client with
   * the fewest open invoices, then the most recent invoice.
   */
  // phone -> client, so a number in the narration can name the payer outright
  const phoneIndex = useMemo(() => buildPhoneIndex(clientPhones), [clientPhones]);

  // Every candidate comes from the outstanding-invoice feed, so a client with
  // nothing open cannot be suggested however well the wording matches. Say so
  // rather than silently falling back to amount-only guesses.
  const bookKeys = useMemo(() => {
    const set = new Set();
    recIndex.clients.forEach((c) => {
      if (c.client_id != null && c.client_id !== '') set.add(String(c.client_id).trim());
      // the client code, because the two feeds number their clients differently
      const uid = (c.unique_id ?? '').toString().trim().toUpperCase();
      if (uid) set.add(uid);
      const n = normalizeText(c.client_name);
      if (n) set.add(n);
    });
    return set;
  }, [recIndex]);

  const historyOutsideBook = useMemo(() => {
    if (!open || !historyByClient || historyByClient.size === 0) return [];
    const found = new Map();
    historyByClient.forEach((e, key) => {
      const uid = key.startsWith('uid:') ? key.slice(4) : '';
      const id = key.startsWith('id:') ? key.slice(3) : '';
      const nm = key.startsWith('name:') ? key.slice(5) : normalizeText(e.clientName);
      if ((uid && bookKeys.has(uid)) || (id && bookKeys.has(id)) || (nm && bookKeys.has(nm))) return;
      const label = e.clientName || uid || nm;
      if (label && !found.has(label)) found.set(label, e.count || 1);
    });
    return Array.from(found, ([name, count]) => ({ name, count }));
  }, [open, historyByClient, bookKeys]);

  // When the wording points at several clients — a parent paying for its units —
  // all of them belong on screen, not the first three.
  const historyClientCount = useMemo(() => {
    if (!historyByClient.size) return 0;
    let n = 0;
    recIndex.clients.forEach((c) => {
      if (historyMatchOf(historyByClient, c)) n += 1;
    });
    return n;
  }, [historyByClient, recIndex]);

  const suggestionLimit = Math.min(
    MAX_HISTORY_CLIENT_SUGGESTIONS,
    Math.max(MAX_CLIENT_SUGGESTIONS, historyClientCount)
  );

  const clientSuggestions = useMemo(() => {
    if (!invoices.length || !open) return [];
    const recs = recommendClients(
      recIndex,
      { description: transaction?.description, amount: txnAmount },
      {
        limit: suggestionLimit,
        tolerance: AMOUNT_TOLERANCE,
        phoneIndex,
        // history is a supporting signal: it lifts a match and can stand alone
        // only when the same wording has been mapped here repeatedly
        historyFor: (client) => historyMatchOf(historyByClient, client),
      }
    );

    return recs
      .map((r) => {
        const client = recIndex.clients.get(r.clientKey);
        const hist = historyMatchOf(historyByClient, {
          client_id: r.client_id,
          client_name: r.client_name,
          unique_id: r.unique_id,
        });
        const reasons = [];
        if (r.name_match_pct >= STRONG_NAME_MATCH) reasons.push(`name ${r.name_match_pct}%`);
        else if (r.name_match_pct > 0) reasons.push(`name ${r.name_match_pct}% (partial)`);
        if (r.tds_pct !== null && r.tds_pct !== undefined) reasons.push(`TDS ${r.tds_pct}% on invoice`);
        else if (r.invoice_num) reasons.push(r.exact ? 'exact invoice due' : 'close to invoice due');
        if (r.matching_method === 'NAME + CLIENT DUE') reasons.push('client overall due');
        if (r.phone_match) reasons.push(`phone ${r.phone_match}`);
        if (r.history_reliable) reasons.push(`paid here ${r.history_count}x before`);
        else if (hist) reasons.push(hist.count > 1 ? `paid before (${hist.count}x)` : 'paid before');
        // a short open book is why this client can outrank another with the same amount
        if (r.client_invoice_count > 0 && r.client_invoice_count <= 2) {
          reasons.push(`only ${r.client_invoice_count} open invoice${r.client_invoice_count === 1 ? '' : 's'}`);
        }

        return {
          // shape the rest of the dialog already expects
          key: r.clientKey,
          client_name: r.client_name,
          client_id: r.client_id,
          unique_id: r.unique_id,
          group_name: r.group_name,
          invoices: client ? client.invoices : [],
          totalDue: r.client_overall_due,
          reasons,
          // engine output, shown on the card and used for ordering
          tier: r.tier,
          confidence: r.confidence,
          matchingMethod: r.matching_method,
          namePct: r.name_match_pct,
          amountPct: r.amount_match_pct,
          recommendedInvoice: r.invoice_num,
          recommendedInvoiceAmount: r.invoice_amount,
          recommendedInvoiceDue: r.invoice_due,
          recommendedTds: r.tds_pct,
          expectedAmount: r.expected_amount,
          why: r.reason,
          historyMatch: hist || null,
          historyScore: hist ? 1 : 0,
          phoneMatch: r.phone_match || null,
          historyReliable: !!r.history_reliable,
          historyCount: r.history_count || 0,
          score: Math.max(0, 100 - (r.tier - 1) * 20) + (hist ? 2 : 0),
        };
      })
      .sort(
        (a, b) =>
          a.tier - b.tier ||
          b.namePct - a.namePct ||
          b.amountPct - a.amountPct ||
          b.historyScore - a.historyScore
      );
  }, [invoices, open, recIndex, transaction, txnAmount, historyByClient, phoneIndex, suggestionLimit]);

  /**
   * The engine's recommendation per invoice: which TDS rate (if any) made the
   * amounts line up, and the payment it expects. Ticking that invoice then
   * pre-fills its TDS % and payment instead of falling back to the dialog default.
   */
  const invoiceRecommendations = useMemo(() => {
    const m = new Map();
    if (!open || !invoices.length) return m;
    recommendForPayment(
      recIndex,
      { description: transaction?.description, amount: txnAmount },
      {
        limit: 300,
        tolerance: AMOUNT_TOLERANCE,
        phoneIndex,
        historyFor: (client) => historyMatchOf(historyByClient, client),
      }
    ).forEach((r) => {
      if (!r.invoice_num || m.has(r.invoice_num)) return; // first (best) wins
      m.set(r.invoice_num, {
        tds: r.tds_pct,
        expected: r.expected_amount,
        method: r.matching_method,
        confidence: r.confidence,
      });
    });
    return m;
  }, [open, invoices, recIndex, transaction, txnAmount, phoneIndex, historyByClient]);

  // Order the invoice table by the suggested-clients band above it, so the most
  // likely client is the first thing you scroll into. Unsuggested clients keep
  // the largest-outstanding-first order after them.
  const suggestionRank = useMemo(() => {
    const m = new Map();
    clientSuggestions.forEach((c, i) => m.set(String(c.key), i));
    return m;
  }, [clientSuggestions]);

  const orderedGroups = useMemo(() => {
    const rankOf = (g) => {
      const inv = g.invoices[0];
      const r = inv ? suggestionRank.get(String(clientKeyOf(inv))) : undefined;
      return r === undefined ? Infinity : r;
    };
    return grouped
      .map((g) => ({ ...g, _rank: rankOf(g) }))
      .sort((a, b) => (a._rank !== b._rank ? a._rank - b._rank : b.total - a.total));
  }, [grouped, suggestionRank]);

  // Suggested groups for this transaction: group_name found in the payment description
  // and/or the group's dues close to the payment amount.
  const groupSuggestions = useMemo(() => {
    if (!invoices.length) return [];
    const map = new Map();
    invoices.forEach((inv) => {
      const gname = (inv.group_name ?? '').toString().trim();
      if (!gname) return;
      if (!map.has(gname)) {
        map.set(gname, { group_name: gname, clients: new Set(), invoices: [], totalDue: 0 });
      }
      const o = map.get(gname);
      o.invoices.push(inv);
      const c = inv.client_id ?? inv.client_name;
      if (c) o.clients.add(c);
      o.totalDue += num(inv.due);
    });
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        // score the group_name as the entity name
        ...scoreClientMatch({ client_name: g.group_name, group_name: '', totalDue: g.totalDue, invoices: g.invoices }, descNorm, txnAmount),
      }))
      .filter((g) => g.score > 0)
      .sort((a, b) => (b.nameScore !== a.nameScore ? b.nameScore - a.nameScore : b.amountScore - a.amountScore))
      .slice(0, 3);
  }, [invoices, descNorm, txnAmount]);

  /**
   * The groups band is only useful when the client is still in doubt.
   *  - a confident client recommendation (phone, or name+amount) hides it entirely
   *  - otherwise a group is shown only when it is actually supported: its own name
   *    appears in the narration, or one of its clients is itself a candidate
   * A group matched on amount alone is noise — a 27 lakh group total tells you
   * nothing about a 3,093 payment.
   */
  // A candidate above tier 3 rests on nothing but the amount lining up. Saying
  // "suggested client" for that is a claim the data does not support.
  const identified = clientSuggestions.length > 0 && clientSuggestions[0].tier <= 3;

  // Opening the dialog on a payment whose payer is identified should land on the
  // invoices — that is the decision to make. Only the matched client opens; every
  // other client stays closed, and once per payment, so collapsing it sticks.
  const autoExpandedFor = useRef(null);
  useEffect(() => {
    if (!open) {
      autoExpandedFor.current = null;
      return;
    }
    const payment = transaction?.trx_id ?? transaction?._rid ?? '';
    if (autoExpandedFor.current === payment) return;
    // no client identified — an amount coincidence is not worth opening
    if (!identified || orderedGroups.length === 0) return;
    // match on the group that is actually rendered, so the key format cannot drift
    const wanted = String(clientSuggestions[0].key);
    const group = orderedGroups.find(
      (g) => String(clientKeyOf(g.invoices[0] || {})) === wanted
    );
    if (!group) return; // its invoices are not in the picker yet — try again next render
    autoExpandedFor.current = payment;
    setExpandedGroups(new Set([group.key]));
  }, [open, identified, clientSuggestions, transaction, orderedGroups]);

  const copyMatchInfo = useCallback(() => {
    const info = {
      narration: transaction?.description ?? '',
      amount: txnAmount,
      distinctive_words: Array.from(descTokens(transaction?.description)),
      history_rows: paymentHistory.length,
      history_state: historyInfo?.state ?? 'idle',
      history_columns: Object.keys(paymentHistory[0] || {}),
      history_keys: Array.from(historyByClient.keys()),
      history_matched: Array.from(historyByClient.entries()).map(([k, v]) => ({
        key: k,
        similarity: v.similarity,
        times: v.count,
        shared: v.shared,
      })),
      book_client_codes_sample: Array.from(recIndex.clients.values()).slice(0, 5).map((c) => ({
        client_id: c.client_id,
        unique_id: c.unique_id,
        name: c.client_name,
      })),
      phone_rows: clientPhones.length,
      phone_columns: Object.keys(clientPhones[0] || {}),
      invoice_clients: recIndex.clients.size,
      suggestions: clientSuggestions.map((c) => ({
        client: c.client_name,
        priority: c.tier,
        confidence: c.confidence,
        method: c.matchingMethod,
        name_pct: c.namePct,
        amount_pct: c.amountPct,
        invoice: c.recommendedInvoice,
        unique_id: c.unique_id,
        open_invoices: c.invoices.length,
      })),
    };
    const text = JSON.stringify(info, null, 2);
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
    else console.log(text);
  }, [transaction, txnAmount, paymentHistory, historyInfo, historyByClient, clientPhones, recIndex, clientSuggestions]);

  const visibleGroupSuggestions = useMemo(() => {
    if (!groupSuggestions.length) return [];
    const best = clientSuggestions[0];
    const clientIsClear = !!best && (best.confidence === 'HIGH' || best.tier <= 2);
    if (clientIsClear) return [];
    const candidateKeys = new Set(clientSuggestions.map((c) => String(c.key)));
    return groupSuggestions.filter(
      (g) =>
        g.nameScore > 0 || Array.from(g.clients).some((k) => candidateKeys.has(String(k)))
    );
  }, [groupSuggestions, clientSuggestions]);

  const toggleClientFilter = useCallback((key) => {
    setGroupFilter('all'); // client & group filters are mutually exclusive
    setClientFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // selecting a group clears any client filter (mutually exclusive)
  const applyGroupFilter = useCallback((value) => {
    setClientFilter(new Set());
    setGroupFilter(value);
  }, []);

  const suggestAllocation = useCallback(
    (inv, runningRemaining) => {
      const t = num(defaultTds);
      // the client pays the outstanding less the TDS withheld on the invoice value,
      // so payment + TDS settles the invoice exactly
      const net = Math.max(0, num(inv.due) - computeTds(tdsBaseOf(inv), t));
      if (runningRemaining > 0) return Math.max(0, Math.min(net || runningRemaining, runningRemaining));
      return net;
    },
    [defaultTds]
  );

  /**
   * What to adjust against one invoice.
   *
   * The engine's expected amount is derived from the invoice VALUE, so on a
   * part-paid invoice it asks for more than is owed — a 6,075 invoice with 2,500
   * already paid was suggesting 5,999 against an outstanding of 3,575. Whatever
   * the engine expects, the adjustment can never exceed the outstanding less the
   * TDS withheld, which is what makes Remaining land on zero.
   */
  const allocationFor = useCallback(
    (inv, rem) => {
      const rec = invoiceRecommendations.get(inv.reference_no);
      const recTds = rec && rec.tds !== null && rec.tds !== undefined ? rec.tds : null;
      const tdsPct = recTds !== null ? recTds : num(defaultTds);
      const base = recTds !== null ? num(rec.expected) : suggestAllocation(inv, rem);
      const outstanding = num(inv.due);
      const cap = outstanding > 0 ? Math.max(0, outstanding - computeTds(tdsBaseOf(inv), tdsPct)) : null;
      const capped = cap === null ? base : Math.min(base, cap);
      return {
        allocated: Math.max(0, Math.round(Math.min(capped, rem))),
        tds_pct: String(recTds !== null ? recTds : defaultTds),
      };
    },
    [invoiceRecommendations, defaultTds, suggestAllocation]
  );

  const toggleInvoice = useCallback(
    (inv) => {
      setSelected((prev) => {
        const next = { ...prev };
        if (next[inv.reference_no]) {
          delete next[inv.reference_no];
          return next;
        }
        // block selecting more once the payment amount is fully allocated
        const rem = targetAmount - Object.values(next).reduce((s, x) => s + num(x.allocated), 0);
        if (rem <= 0) return prev;
        const a = allocationFor(inv, rem);
        next[inv.reference_no] = { invoice: inv, allocated: String(a.allocated), tds_pct: a.tds_pct };
        return next;
      });
    },
    [targetAmount, allocationFor]
  );

  // select / deselect every invoice in a group at once.
  // If any invoice in the group is selected, clicking clears the whole group;
  // otherwise select as many as fit the remaining payment amount.
  const toggleGroup = useCallback(
    (g) => {
      setSelected((prev) => {
        const next = { ...prev };
        const anySelected = g.invoices.some((inv) => next[inv.reference_no]);
        if (anySelected) {
          g.invoices.forEach((inv) => delete next[inv.reference_no]);
        } else {
          g.invoices.forEach((inv) => {
            const rem = targetAmount - Object.values(next).reduce((s, x) => s + num(x.allocated), 0);
            if (rem <= 0) return; // stop once the payment amount is fully allocated
            const a = allocationFor(inv, rem);
            if (a.allocated <= 0) return; // nothing left owed on this invoice
            next[inv.reference_no] = { invoice: inv, allocated: String(a.allocated), tds_pct: a.tds_pct };
          });
        }
        return next;
      });
    },
    [targetAmount, allocationFor]
  );

  const toggleExpandGroup = useCallback(
    (key) =>
      setExpandedGroups((prev) => {
        const n = new Set(prev);
        if (n.has(key)) n.delete(key);
        else n.add(key);
        return n;
      }),
    []
  );

  const setField = useCallback(
    (ref, field, value) =>
      setSelected((prev) => {
        // how much of the payment is free for this row
        const roomFor = (key) =>
          Math.max(
            0,
            targetAmount -
              Object.entries(prev).reduce((s, [k, x]) => (k === key ? s : s + num(x.allocated)), 0)
          );

        if (field === 'allocated') {
          // clamp so the total allocated never exceeds the payment (mappable) amount
          const maxForRow = roomFor(ref);
          const v = num(value) > maxForRow ? String(maxForRow) : value;
          return { ...prev, [ref]: { ...prev[ref], allocated: v } };
        }

        if (field === 'tds_pct') {
          const row = prev[ref];
          if (!row) return prev;
          const inv = row.invoice;
          const outstanding = num(inv.due);
          const wasTds = computeTds(tdsBaseOf(inv), row.tds_pct);
          const nowTds = computeTds(tdsBaseOf(inv), value);
          // Was this row settling the invoice exactly? Then keep it settling
          // exactly: drop the rate to 0 and the payment has to cover the whole
          // outstanding instead. A part payment the operator typed is left alone.
          const wasSettlingInFull =
            outstanding > 0 && Math.abs(num(row.allocated) + wasTds - outstanding) <= 1;
          if (!wasSettlingInFull) {
            return { ...prev, [ref]: { ...row, tds_pct: value } };
          }
          const settle = Math.max(0, outstanding - nowTds);
          return {
            ...prev,
            [ref]: {
              ...row,
              tds_pct: value,
              allocated: String(Math.round(Math.min(settle, roomFor(ref)))),
            },
          };
        }

        return { ...prev, [ref]: { ...prev[ref], [field]: value } };
      }),
    [targetAmount]
  );

  const handleSave = () => {
    const allocations = selectedList
      .filter((x) => num(x.allocated) > 0)
      .map((x) => ({
        reference_no: x.invoice.reference_no,
        client_name: x.invoice.client_name,
        client_id: x.invoice.client_id,
        client_fk: x.invoice.client_fk ?? x.invoice.client_id,
        group_name: x.invoice.group_name ?? '',
        due: x.invoice.due,
        allocated: num(x.allocated),
        tds_pct: num(x.tds_pct),
        tds_amount: computeTds(tdsBaseOf(x.invoice), x.tds_pct),
        settled: num(x.allocated) + computeTds(tdsBaseOf(x.invoice), x.tds_pct),
      }));
    onSave(allocations);
  };

  const savedCount = selectedList.filter((x) => num(x.allocated) > 0).length;

  const SummaryStat = ({ label, value, color }) => (
    <Box sx={{ flex: '1 1 96px', minWidth: 90 }}>
      <Typography sx={{ fontSize: '9px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '15px', fontWeight: 700, color }}>{value}</Typography>
    </Box>
  );

  // Build the visible rows with a hard cap so the picker never renders thousands of
  // input-bearing rows at once. Selected invoices are always shown (they don't consume
  // the budget); unselected rows fill the remaining budget, newest groups first.
  const invoiceBody = [];
  if (open && !invoicesLoading) {
    let budget = MAX_VISIBLE_INVOICE_ROWS;
    orderedGroups.forEach((g) => {
      // groups are collapsed by default; invoices show only when the row is expanded
      const isExpanded = expandedGroups.has(g.key);
      if (budget <= 0) return;
      const sels = g.invoices.filter((inv) => selected[inv.reference_no]);
      const allSel = g.invoices.length > 0 && sels.length === g.invoices.length;
      const someSel = !allSel && sels.length > 0;
      budget -= 1; // the header row itself
      invoiceBody.push(
        <TableRow
          key={`gh:${g.key}`}
          sx={{
            backgroundColor: g._rank === 0 ? '#dcfce7' : g._rank < Infinity ? '#f0fdf4' : '#eef2ff',
          }}
        >
          <TableCell sx={{ padding: '0 4px' }}>
            <Checkbox
              size="small"
              checked={allSel}
              indeterminate={someSel}
              disabled={!isExpanded}
              onChange={() => toggleGroup(g)}
              sx={{ p: 0.5 }}
            />
          </TableCell>
          <TableCell colSpan={10} sx={{ py: 0.5, cursor: 'pointer' }} onClick={() => toggleExpandGroup(g.key)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <IconButton size="small" sx={{ p: 0 }} onClick={(e) => { e.stopPropagation(); toggleExpandGroup(g.key); }}>
                {isExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#3730a3', lineHeight: 1.2 }}>
                  {g.clientName}
                </Typography>
                {g.groupName && (
                  <Typography sx={{ fontSize: '9px', color: '#6366f1', lineHeight: 1.2 }}>
                    {g.groupName}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontSize: '10px', color: '#6366f1', ml: 0.5 }}>
                {g.invoices.length} invoice{g.invoices.length === 1 ? '' : 's'} · {formatINR(g.total)} outstanding
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      );
      if (!isExpanded) return;
      const shown = g.invoices.slice(0, Math.max(0, budget));
      budget -= shown.length;
      shown.forEach((inv, i) => {
        invoiceBody.push(
          <InvoiceRow
            key={`${g.key}:${inv.reference_no}`}
            inv={inv}
            sel={selected[inv.reference_no]}
            recommendedTds={invoiceRecommendations.get(inv.reference_no)?.tds ?? null}
            stripe={i % 2 === 1}
            disabled={!selected[inv.reference_no] && remaining <= 0}
            onToggle={toggleInvoice}
            onSetField={setField}
          />
        );
      });
    });
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: { borderRadius: '12px', width: '90vw', maxWidth: 1350 } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
            color: '#fff',
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <AddLinkIcon sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, lineHeight: 1.2 }}>
              Map Payment to Invoices
            </Typography>
            <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, opacity: 0.95 }}>
              {transaction?.trx_id ?? '—'} — {formatINR(transaction?.amount)}
            </Typography>
            <Typography sx={{ fontSize: '11px', opacity: 0.85 }}>
              {transaction?.description || 'No description'} · {fmtDate(transaction?.payment_date)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* allocation summary */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <SummaryStat label="Transaction" value={formatINR(txnAmount)} color="#1e40af" />
          {alreadyMapped > 0 && (
            <SummaryStat label="Already Mapped" value={formatINR(alreadyMapped)} color="#64748b" />
          )}
          {alreadyMapped > 0 && (
            <SummaryStat label="Mappable" value={formatINR(targetAmount)} color="#ea580c" />
          )}
          <SummaryStat label="Payment Allocated" value={formatINR(allocated)} color="#16a34a" />
          <SummaryStat label="TDS" value={formatINR(tds)} color="#7c3aed" />
          <SummaryStat label="Settled (Pmt+TDS)" value={formatINR(settled)} color="#0059B2" />
          <SummaryStat
            label={remaining < 0 ? 'Over-allocated' : 'Remaining'}
            value={formatINR(Math.abs(remaining))}
            color={remaining < 0 ? '#dc2626' : '#d97706'}
          />
        </Box>

        {/* allocation progress bar — fills as due rows are selected */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            sx={{
              position: 'relative',
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e2e8f0',
              overflow: 'hidden',
              flex: 1,
              minWidth: 120,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: `${pctAllocated}%`,
                backgroundColor: overAllocated ? '#dc2626' : pctAllocated >= 99 ? '#16a34a' : '#2563eb',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>
            {Math.round(pctAllocated)}% · {selectedList.length} inv
            {overAllocated
              ? ` · over ${formatINR(Math.abs(remaining))}`
              : remaining > 0
                ? ` · ${formatINR(remaining)} left`
                : ' · full'}
          </Typography>
        </Box>

        {/* per-client breakdown — appears when a payment covers more than one client (group payment) */}
        {clientKeys.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              flexWrap: 'wrap',
              alignItems: 'center',
              backgroundColor: '#f5f3ff',
              border: '1px solid #ddd6fe',
              borderRadius: '6px',
              p: 0.75,
              mb: 1,
            }}
          >
            <GroupsIcon sx={{ fontSize: 16, color: '#7c3aed' }} />
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>
              Group split
            </Typography>
            {clientKeys.map((c) => (
              <Chip
                key={c}
                size="small"
                label={`${c}: ${formatINR(byClient[c].allocated)}${
                  byClient[c].tds > 0 ? ` +${formatINR(byClient[c].tds)} TDS` : ''
                }`}
                sx={{ backgroundColor: '#fff', border: '1px solid #ddd6fe', color: '#5b21b6', fontSize: '10px', height: 22 }}
              />
            ))}
          </Box>
        )}

        {/* suggested clients — click a client to show only its invoices below */}
        {clientSuggestions.length > 0 && (
          <Box
            sx={{
              backgroundColor: identified ? '#f0fdf4' : '#fffbeb',
              border: `1px solid ${identified ? '#bbf7d0' : '#fde68a'}`,
              borderRadius: '6px',
              p: 0.75,
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {identified ? (
                <CheckCircleIcon sx={{ fontSize: 15, color: '#16a34a' }} />
              ) : (
                <HelpOutlineIcon sx={{ fontSize: 15, color: '#b45309' }} />
              )}
              <Typography
                sx={{
                  fontSize: '10px',
                  fontWeight: 700,
                  color: identified ? '#16a34a' : '#b45309',
                  textTransform: 'uppercase',
                }}
              >
                {identified ? 'Suggested clients' : 'No client named in this narration'}
              </Typography>
              <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>
                click to filter invoices below
              </Typography>
              {clientSuggestions.length > 0 && clientSuggestions[0].tier >= 3 && (
                <Chip
                  size="small"
                  label="matched on amount only - check before mapping"
                  title="No client name in this narration reached 90%, so these are invoices whose due (or amount less TDS) equals the payment"
                  sx={{ height: 16, fontSize: '8px', fontWeight: 700, backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}
                />
              )}
              {paymentHistory.length === 0 && (
                <Chip
                  size="small"
                  label={
                    historyInfo?.state === 'error'
                      ? 'payment history failed to load'
                      : historyInfo?.state === 'empty'
                        ? 'payment history is empty'
                        : historyInfo?.state === 'loading'
                          ? 'checking previous payments...'
                          : 'payment history not loaded yet'
                  }
                  title={
                    historyInfo?.message ||
                    'Previous payments could not be used to identify the payer.'
                  }
                  sx={{ height: 16, maxWidth: 340, fontSize: '8px', fontWeight: 700, backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                />
              )}
              {paymentHistory.length > 0 && historyByClient.size === 0 && (
                <Chip
                  size="small"
                  label={`no previous payment worded like this (${paymentHistory.length} checked)`}
                  title="This narration does not share enough distinctive words with any previously mapped payment, so history could not name the payer."
                  sx={{ height: 16, fontSize: '8px', fontWeight: 700, backgroundColor: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}
                />
              )}
              {historyOutsideBook.length > 0 && (
                <Chip
                  size="small"
                  label={`paid before by ${historyOutsideBook
                    .map((h) => h.name)
                    .join(', ')} - nothing outstanding`}
                  title={`This wording was mapped to ${historyOutsideBook
                    .map((h) => `${h.name} (${h.count}x)`)
                    .join(', ')} before, but that client has no invoice in the outstanding list, so it cannot be suggested. Check whether the invoice is already settled or missing from the due report.`}
                  sx={{
                    height: 16,
                    maxWidth: 420,
                    fontSize: '8px',
                    fontWeight: 700,
                    backgroundColor: '#eff6ff',
                    color: '#1d4ed8',
                    border: '1px solid #bfdbfe',
                  }}
                />
              )}
              {clientFilter.size > 0 && (
                <Typography
                  onClick={() => setClientFilter(new Set())}
                  sx={{ fontSize: '9px', color: '#1e40af', fontWeight: 700, cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}
                >
                  Show all clients
                </Typography>
              )}
              <Typography
                onClick={copyMatchInfo}
                title="Copy what this screen matched on — narration words, history rows and columns, phone rows, and every candidate with its score. Paste it when reporting a wrong suggestion."
                sx={{
                  fontSize: '9px',
                  color: '#64748b',
                  cursor: 'pointer',
                  ml: clientFilter.size > 0 ? 1 : 'auto',
                  '&:hover': { textDecoration: 'underline', color: '#1e40af' },
                }}
              >
                copy match info
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {clientSuggestions.map((c, idx) => {
                const isSel = clientFilter.has(c.key);
                // the leader is green; every other candidate is slate, so the best
                // match is obvious at a glance rather than one green box among many
                const isBest = idx === 0 && c.score > 0;
                return (
                  <Box
                    key={c.key}
                    onClick={() => toggleClientFilter(c.key)}
                    title={[
                      `Priority ${c.tier} · ${c.confidence} confidence · ${c.matchingMethod}`,
                      c.why,
                      c.historyMatch ? `Paid before: ${c.historyMatch.description}` : '',
                    ]
                      .filter(Boolean)
                      .join('\n')}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSel ? '#e0e7ff' : isBest ? '#dcfce7' : '#fff',
                      border: isSel
                        ? '2px solid #1e40af'
                        : isBest
                          ? '2px solid #16a34a'
                          : '1px solid #cbd5e1',
                      borderRadius: '6px',
                      px: 1,
                      py: 0.5,
                      '&:hover': { borderColor: isBest ? '#15803d' : '#1e40af' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isSel && <CheckCircleIcon sx={{ fontSize: 13, color: '#1e40af' }} />}
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: isBest ? '#166534' : '#1e293b' }}>
                        {c.client_name ?? c.client_id ?? '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#0059B2' }}>
                        {formatINR(c.totalDue)}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${isBest ? 'BEST · ' : ''}P${c.tier} ${c.confidence}`}
                        sx={{
                          height: 15,
                          fontSize: '8px',
                          fontWeight: 700,
                          fontFamily: 'monospace',
                          backgroundColor: isBest
                            ? '#16a34a'
                            : c.confidence === 'HIGH'
                              ? '#dcfce7'
                              : c.confidence === 'MEDIUM'
                                ? '#fef3c7'
                                : '#e2e8f0',
                          color: isBest ? '#fff' : c.confidence === 'LOW' ? '#475569' : '#166534',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '9px', color: '#64748b' }}>
                        {c.invoices.length} invoice{c.invoices.length === 1 ? '' : 's'}
                      </Typography>
                      {c.phoneMatch && (
                        <Chip
                          size="small"
                          label={`phone ${c.phoneMatch}`}
                          title="This number appears in the narration and belongs to exactly one client"
                          sx={{ height: 15, fontSize: '8px', fontWeight: 700, backgroundColor: '#e0e7ff', color: '#1e40af', border: '1px solid #c7d2fe' }}
                        />
                      )}
                      {c.recommendedInvoice && (
                        <Typography
                          sx={{ fontSize: '9px', fontFamily: 'monospace', fontWeight: 700, color: isBest ? '#15803d' : '#475569' }}
                          title={`${c.matchingMethod} · expected ${formatINR(c.expectedAmount)}`}
                        >
                          {c.recommendedInvoice}
                        </Typography>
                      )}
                      {c.reasons.map((r) => {
                        const fromHistory = r.startsWith('paid before');
                        return (
                          <Chip
                            key={r}
                            size="small"
                            label={r}
                            sx={{
                              height: 15,
                              fontSize: '8px',
                              fontWeight: fromHistory ? 700 : 400,
                              backgroundColor: isBest ? '#ecfdf5' : '#f1f5f9',
                              color: isBest ? '#15803d' : '#475569',
                              border: fromHistory ? `1px solid ${isBest ? '#86efac' : '#cbd5e1'}` : 'none',
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* suggested groups — click a group to filter the invoices below to it */}
        {visibleGroupSuggestions.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#eef2ff',
              border: '1px solid #c7d2fe',
              borderRadius: '6px',
              p: 0.75,
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <GroupsIcon sx={{ fontSize: 15, color: '#4338ca' }} />
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#4338ca', textTransform: 'uppercase' }}>
                Suggested groups
              </Typography>
              <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>click to filter invoices below</Typography>
              {groupFilter !== 'all' && (
                <Typography
                  onClick={() => setGroupFilter('all')}
                  sx={{ fontSize: '9px', color: '#1e40af', fontWeight: 700, cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}
                >
                  Show all groups
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {visibleGroupSuggestions.map((g) => {
                const isSel = groupFilter === g.group_name;
                return (
                  <Box
                    key={g.group_name}
                    onClick={() => applyGroupFilter(isSel ? 'all' : g.group_name)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isSel ? '#c7d2fe' : '#fff',
                      border: `1px solid ${isSel ? '#4338ca' : '#c7d2fe'}`,
                      borderRadius: '6px',
                      px: 1,
                      py: 0.5,
                      '&:hover': { borderColor: '#4338ca', backgroundColor: '#eef2ff' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {isSel && <CheckCircleIcon sx={{ fontSize: 13, color: '#4338ca' }} />}
                      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#3730a3' }}>
                        {g.group_name}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#0059B2' }}>
                        {formatINR(g.totalDue)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '9px', color: '#64748b' }}>
                        {g.clients.size} client{g.clients.size === 1 ? '' : 's'} · {g.invoices.length} invoice
                        {g.invoices.length === 1 ? '' : 's'}
                      </Typography>
                      {g.reasons.map((r) => (
                        <Chip key={r} size="small" label={r} sx={{ height: 15, fontSize: '8px', backgroundColor: '#e0e7ff', color: '#4338ca' }} />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* toolbar: search + group + default TDS */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by reference, client, or group..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {groupOptions.length > 0 && (
            <TextField
              select
              size="small"
              label="Group"
              value={groupFilter}
              onChange={(e) => applyGroupFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="all">All Groups</MenuItem>
              {groupOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            size="small"
            label="Default TDS %"
            value={TDS_OPTIONS.includes(String(defaultTds)) ? String(defaultTds) : '0'}
            onChange={(e) => setDefaultTds(e.target.value)}
            sx={{ width: 140 }}
          >
            {TDS_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}%
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 360 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...HEADER_SX, width: 40, padding: '6px 4px' }} />
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', minWidth: 220 }}>Reference / Client</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Invoice Amount</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Credit</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Debit</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right', width: 90 }}>Prev Payment</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right', width: 90 }}>Outstanding</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right', width: 110 }}>Payment ₹</TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right', width: 78 }}>TDS %</TableCell>
                <TableCell
                  sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right', width: 110 }}
                  title="(invoice amount + debit) - (credit + previous payment + amount being adjusted now + TDS). TDS settles the invoice along with the payment, so a fully paid invoice reads 0."
                >
                  Remaining
                </TableCell>
                <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right', width: 110 }}>TDS Amt</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoicesLoading ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : invoiceBody.length > 0 ? (
                invoiceBody
              ) : (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 5, color: '#94a3b8' }}>
                    <Typography variant="body2">
                      {invoices.length === 0 ? 'No invoices available to map.' : 'No invoices match your search.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ fontSize: '10px', color: '#94a3b8', mt: 0.5 }}>
          {grouped.length} client{grouped.length === 1 ? '' : 's'} · {filteredInvoices.length} due invoice
          {filteredInvoices.length === 1 ? '' : 's'}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AddLinkIcon />}
          sx={{ textTransform: 'none', backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1e3a8a' } }}
        >
          {saving ? 'Saving…' : `Save Mapping (${savedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────── payments mapped (reconciliation) view ─────────────────────────
function MappedView({ from, to, email, can, refreshSignal, onChanged }) {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [unmapConfirm, setUnmapConfirm] = useState(null); // { trx_id } pending unmap
  const [unmapping, setUnmapping] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [ackSelected, setAckSelected] = useState(new Set()); // trx_ids chosen for acknowledgement
  const [ackConfirm, setAckConfirm] = useState(false);
  const [sendingAck, setSendingAck] = useState(false);
  const [bankFilter, setBankFilter] = useState('all'); // AXIS / ICICI
  const [createdByFilter, setCreatedByFilter] = useState('all'); // who mapped it
  const [exportPrompt, setExportPrompt] = useState(false); // asks about acknowledgements first
  const [exportFormat, setExportFormat] = useState('xlsx'); // which Tally file the prompt will produce
  const [exporting, setExporting] = useState(false);

  const fetchMapped = async () => {
    const api = process.env.REACT_APP_GET_PAYMENT_MAPPING_DETAILS;
    if (!api) {
      setError('API not configured (REACT_APP_GET_PAYMENT_MAPPING_DETAILS)');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(api, { params: { start_date: from, end_date: to } });
      const raw = res?.data?.data ?? res?.data ?? [];
      setData(Array.isArray(raw) ? raw : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load mapped payments');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMapped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, refreshSignal]);

  const handleUnmap = async (trxId, invoiceNum) => {
    const api = process.env.REACT_APP_PAYMENT_UNMAPPING_INVOICES;
    if (!api) {
      setSnack({ open: true, severity: 'warning', message: 'Unmap API not configured (REACT_APP_PAYMENT_UNMAPPING_INVOICES).' });
      return;
    }
    setUnmapping(true);
    try {
      await axios.post(api, { trx_id: trxId, invoice_num: invoiceNum, updated_by: email });
      setSnack({ open: true, severity: 'success', message: 'Invoice unmapped.' });
      // refresh the mapping details first; once that completes, refresh the payments data
      await fetchMapped();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Failed to unmap:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to unmap',
      });
    } finally {
      setUnmapping(false);
    }
  };

  // normalize each mapped record (one row per trx ↔ invoice link)
  const lines = useMemo(
    () =>
      data.map((r) => {
        const allocated = num(r.amt ?? r.allocated);
        const tdsAmt = num(r.tds_amt ?? r.tds_amount);
        return {
          trx_id: r.trx_id,
          payment_date: r.trx_date ?? r.payment_date ?? r.created_at,
          trx_mode: r.trx_mode ?? r.payment_mode ?? '',
          txnAmount: num(r.trx_amt ?? r.txn_amount ?? r.amt),
          reference_no: r.invoice_num ?? r.reference_no,
          client_name: r.client_name,
          client_id: r.client_fk ?? r.client_id,
          allocated,
          tds_pct: num(r.tds_per ?? r.tds_pct),
          tds_amount: tdsAmt,
          settled: allocated,
          unique_id: r.unique_id ?? '', // client code, used as the Tally voucher id
          description: r.description ?? r.trx_description ?? '',
          trx_description: r.trx_description ?? r.description ?? '',
          tally_ledger_name: r.tally_ledger_name ?? '', // party ledger as it exists in Tally
          // No ledger name means Tally has no account to post this party to. On
          // import it would CREATE one rather than fail, quietly splitting the
          // client's balance across two ledgers — so these rows never export.
          _noLedger: !String(r.tally_ledger_name ?? '').trim(),
          bank_type: r.bank_type ?? '',
          mapStatus: statusFromApi(r.mapping_status),
          mappingStatusRaw: (r.mapping_status ?? '').toString().toLowerCase().trim(),
          tally: tallyStatusOf(r),
          mail: mailStatusOf(r),
          created_by: r.created_by,
          created_at: r.created_at,
        };
      }).map((l) => ({
        ...l,
        // every column of this row, so the search box covers all of them
        _search: searchHaystack([
          l.trx_id,
          ...dateTokens(l.payment_date),
          l.trx_mode,
          ...amountTokens(l.txnAmount),
          l.reference_no,
          l.client_name,
          l.client_id,
          l.unique_id,
          l.tally_ledger_name,
          l.description,
          ...amountTokens(l.allocated),
          l.tds_pct,
          ...amountTokens(l.tds_amount),
          ...amountTokens(num(l.allocated) + num(l.tds_amount)),
          l.mapStatus?.label,
          l.tally.key === 'UNKNOWN' ? '' : l.tally.label,
          l.mail.key === 'UNKNOWN' ? '' : l.mail.label,
          l.created_by,
          ...dateTokens(l.created_at),
          bankTypeOf(l),
        ]),
      })),
    [data]
  );

  // whoever appears in the loaded rows, so the dropdown only offers real names
  const createdByOptions = useMemo(() => {
    const seen = new Map();
    lines.forEach((l) => {
      const v = (l.created_by ?? '').toString().trim();
      if (v && !seen.has(v.toLowerCase())) seen.set(v.toLowerCase(), v);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
  }, [lines]);

  const searchLower = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      lines.filter((l) => {
        if (!inPaymentRange(l.payment_date, from, to)) return false;
        if (bankFilter !== 'all' && bankTypeOf(l) !== bankFilter) return false;
        if (
          createdByFilter !== 'all' &&
          (l.created_by ?? '').toString().trim().toLowerCase() !== createdByFilter.toLowerCase()
        ) {
          return false;
        }
        return matchesQuery(l._search, searchLower);
      }),
    [lines, searchLower, bankFilter, createdByFilter, from, to]
  );

  const uniqueTxns = useMemo(
    () => new Set(lines.map((l) => l.trx_id).filter(Boolean)).size,
    [lines]
  );
  const totalAllocated = filtered.reduce((s, l) => s + l.allocated, 0);
  const totalTds = filtered.reduce((s, l) => s + l.tds_amount, 0);
  const totalSettled = filtered.reduce((s, l) => s + l.settled, 0);

  // Only rows that have not reached Tally are exportable — re-exporting a synced
  // row would create a duplicate entry on the Tally side.
  // A transaction is held back whole when ANY of its lines has no Tally ledger.
  // Dropping just the offending line would still produce a voucher, but one that
  // books less than the credit the bank actually received.
  const noLedgerTrxIds = useMemo(() => {
    const ids = new Set();
    filtered.forEach((l) => {
      if (l._noLedger && l.trx_id) ids.add(l.trx_id);
    });
    return ids;
  }, [filtered]);

  // Already-synced rows would duplicate in Tally; rows with no ledger name would
  // silently create a new ledger there. Neither may be exported, in either format.
  const exportable = useMemo(
    () => filtered.filter((l) => l.tally.key !== 'SYNCED' && !noLedgerTrxIds.has(l.trx_id)),
    [filtered, noLedgerTrxIds]
  );
  const noLedgerCount = useMemo(
    () => filtered.filter((l) => l.tally.key !== 'SYNCED' && noLedgerTrxIds.has(l.trx_id)).length,
    [filtered, noLedgerTrxIds]
  );
  const syncedCount = filtered.length - exportable.length;

  // transactions covered by the export, and those the client has not been told about
  const exportTxnIds = useMemo(
    () => Array.from(new Set(exportable.map((l) => l.trx_id).filter(Boolean))),
    [exportable]
  );
  const exportUnacked = useMemo(() => {
    const sent = new Set();
    const pending = new Set();
    exportable.forEach((l) => {
      if (!l.trx_id || !canAckLine(l)) return;
      if (l.mail.key === 'SENT') sent.add(l.trx_id);
      else pending.add(l.trx_id);
    });
    sent.forEach((id) => pending.delete(id)); // a mail on any line covers the txn
    return Array.from(pending);
  }, [exportable]);

  // The table's own columns, in the table's order, with the client's unique id
  // added just before the client name. Follows the search box.
  const handleExportTable = () => {
    const out = filtered.map((l) => ({
      'Transaction ID': l.trx_id ?? '',
      'Txn Amount': num(l.txnAmount),
      'Invoice No': l.reference_no ?? '',
      'Unique ID': l.unique_id ?? '',
      Client: l.client_name ?? '',
      'Client ID': l.client_id ?? '',
      Allocated: num(l.allocated),
      TDS: num(l.tds_amount),
      Settled: num(l.allocated) + num(l.tds_amount),
      'Tally Sync': l.tally.key === 'UNKNOWN' ? '' : l.tally.label,
      'Mail Sent': l.mail.key === 'UNKNOWN' ? '' : l.mail.label,
      'Created By': l.created_by ?? '',
      'Created At': l.created_at ? fmtDateTime(l.created_at) : '',
    }));
    if (out.length === 0) return;
    exportRowsToExcel(out, `Payments_Mapped_${from}_to_${to}.xlsx`);
  };

  // Exported in Tally's "Receipts and Payments with multiple references" layout so
  // the file can be imported straight into Tally.
  const runTallyExport = async (lines, format) => {
    try {
      if (format === 'xml') {
        const { vouchers } = await exportTallyXml(lines, `Tally_Receipts_NotSynced_${from}_to_${to}.xml`);
        if (vouchers === 0) return;
      } else {
        const out = buildTallyVoucherRows(lines);
        if (out.length === 0) return;
        await exportTallyVouchers(out, `Tally_Receipts_NotSynced_${from}_to_${to}.xlsx`);
      }
    } catch (err) {
      console.error('Failed to build the Tally file:', err);
      setSnack({ open: true, severity: 'error', message: 'Could not build the Tally export file.' });
    }
  };

  // Send the outstanding acknowledgements, then export. A failed mail does not
  // block the download — it is reported and the file is still produced.
  const handleExportWithAck = async (sendFirst) => {
    const lines = exportable; // snapshot: the ack refresh must not change what exports
    setExportPrompt(false);
    setExporting(true);
    try {
      if (sendFirst && exportUnacked.length > 0) {
        const api = process.env.REACT_APP_SEND_PAYMENT_ACKNOWLEDGEMENT;
        if (!api) {
          setSnack({ open: true, severity: 'warning', message: 'Acknowledgement API not configured — exporting only.' });
        } else {
          try {
            await axios.post(
              api,
              exportUnacked.map((trxId) => ({ trx_id: trxId, updated_by: email }))
            );
            setSnack({
              open: true,
              severity: 'success',
              message: `Acknowledgement sent for ${exportUnacked.length} transaction${exportUnacked.length === 1 ? '' : 's'}.`,
            });
          } catch (err) {
            console.error('Failed to send acknowledgements before export:', err);
            setSnack({
              open: true,
              severity: 'error',
              message: 'Acknowledgement failed — the Tally file was still downloaded.',
            });
          }
        }
      }
      await runTallyExport(lines, exportFormat);
    } finally {
      setExporting(false);
      if (sendFirst) fetchMapped(); // refresh the Mail Sent column
    }
  };

  const totalRows = filtered.length;
  const maxPage = totalRows <= 0 ? 0 : Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
  const safePage = page > maxPage ? maxPage : page;
  const paginated = filtered.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (totalRows > 0 && page > maxPage) setPage(maxPage);
  }, [totalRows, rowsPerPage, page, maxPage]);

  // Acknowledgement is sent per transaction, but a transaction spans several
  // invoice rows here — so selection is keyed by trx_id and every row of that
  // transaction ticks together.
  const pageTrxIds = useMemo(
    () => Array.from(new Set(paginated.filter(canAckLine).map((l) => l.trx_id))),
    [paginated]
  );
  const allPageSelected = pageTrxIds.length > 0 && pageTrxIds.every((id) => ackSelected.has(id));
  const somePageSelected = !allPageSelected && pageTrxIds.some((id) => ackSelected.has(id));
  const ackCount = ackSelected.size;

  const toggleAck = (trxId) =>
    setAckSelected((prev) => {
      const next = new Set(prev);
      if (next.has(trxId)) next.delete(trxId);
      else next.add(trxId);
      return next;
    });

  const toggleAckAll = () =>
    setAckSelected((prev) => {
      const next = new Set(prev);
      if (pageTrxIds.every((id) => next.has(id))) pageTrxIds.forEach((id) => next.delete(id));
      else pageTrxIds.forEach((id) => next.add(id));
      return next;
    });

  // roll the selected invoice rows up to one line per transaction for the dialog
  const ackSummary = useMemo(() => {
    const m = new Map();
    lines.forEach((l) => {
      if (!l.trx_id || !ackSelected.has(l.trx_id)) return;
      if (!m.has(l.trx_id)) {
        m.set(l.trx_id, {
          trx_id: l.trx_id,
          txnAmount: l.txnAmount,
          payment_date: l.payment_date,
          clients: new Set(),
          invoices: 0,
          allocated: 0,
          mail: l.mail,
        });
      }
      const e = m.get(l.trx_id);
      if (l.client_name) e.clients.add(l.client_name);
      e.invoices += 1;
      e.allocated += l.allocated;
      // a sent flag on any line of the transaction means the mail already went out
      if (l.mail.key === 'SENT') e.mail = l.mail;
    });
    return Array.from(m.values());
  }, [lines, ackSelected]);

  const ackAlreadySent = useMemo(() => ackSummary.filter((t) => t.mail?.key === 'SENT'), [ackSummary]);

  const handleSendAck = async () => {
    const api = process.env.REACT_APP_SEND_PAYMENT_ACKNOWLEDGEMENT;
    if (!api) {
      setSnack({ open: true, severity: 'warning', message: 'Acknowledgement API not configured (REACT_APP_SEND_PAYMENT_ACKNOWLEDGEMENT).' });
      return;
    }
    const items = ackSummary.map((t) => ({ trx_id: t.trx_id, updated_by: email }));
    if (items.length === 0) return;
    setSendingAck(true);
    try {
      await axios.post(api, items);
      setSnack({
        open: true,
        severity: 'success',
        message: `Acknowledgement sent for ${items.length} transaction${items.length === 1 ? '' : 's'}.`,
      });
      setAckSelected(new Set());
      await fetchMapped();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Failed to send acknowledgement:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to send acknowledgement',
      });
    } finally {
      setSendingAck(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (error) {
    return <Box sx={{ p: 4, textAlign: 'center', color: '#dc2626', fontSize: '14px' }}>{error}</Box>;
  }

  return (
    <>
      {/* KPI strip */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <StatCard label="Mapped Transactions" value={uniqueTxns} />
        <StatCard
          label="Invoice Lines"
          value={lines.length}
          palette={{ bg: '#eef2ff', border: '#3b82f6', color: '#1e40af', Icon: ReceiptLongIcon }}
        />
        <StatCard
          label="Total Allocated"
          value={formatINR(totalAllocated)}
          palette={{ bg: '#ecfdf5', border: '#16a34a', color: '#16a34a', Icon: PaymentsIcon }}
        />
        <StatCard
          label="Total TDS"
          value={formatINR(totalTds)}
          palette={{ bg: '#f5f3ff', border: '#7c3aed', color: '#7c3aed', Icon: PercentIcon }}
        />
        <StatCard
          label="Total Settled"
          value={formatINR(totalSettled)}
          palette={{ bg: '#fff7ed', border: '#ea580c', color: '#ea580c', Icon: AccountBalanceWalletIcon }}
        />
      </Box>

      {/* search bar */}
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          disabled={filtered.length === 0}
          onClick={handleExportTable}
          title="Download every row shown in this table, with client id and unique id"
          sx={{ textTransform: 'none', fontSize: '12px', color: '#1e40af', borderColor: '#93c5fd' }}
        >
          Export ({filtered.length})
        </Button>
        {allowed('Tally Export') && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          disabled={exportable.length === 0 || exporting}
          onClick={() => { setExportFormat('xlsx'); setExportPrompt(true); }}
          title={
            exportable.length === 0
              ? 'Nothing to export — every row here is already synced to Tally'
              : 'Downloads the not-yet-synced rows as a Tally receipt-voucher import sheet'
          }
          sx={{ textTransform: 'none', fontSize: '12px', color: '#1e40af', borderColor: '#93c5fd' }}
        >
          {exporting ? 'Working…' : `Tally Export (${exportable.length})`}
        </Button>
        )}
        {allowed('Tally Export') && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          disabled={exportable.length === 0 || exporting}
          onClick={() => { setExportFormat('xml'); setExportPrompt(true); }}
          title={
            exportable.length === 0
              ? 'Nothing to export — every row here is already synced to Tally'
              : 'Downloads the same not-yet-synced rows as a Tally XML voucher file'
          }
          sx={{ textTransform: 'none', fontSize: '12px', color: '#1e40af', borderColor: '#93c5fd' }}
        >
          {exporting ? 'Working…' : `Tally XML (${exportable.length})`}
        </Button>
        )}
        {allowed('Send Acknowledgement') && (
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
          disabled={ackCount === 0 || sendingAck}
          onClick={() => setAckConfirm(true)}
          sx={{ textTransform: 'none', fontSize: '12px', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
        >
          {sendingAck ? 'Sending…' : `Send Acknowledgement (${ackCount})`}
        </Button>
        )}
        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
          {filtered.length} of {lines.length} rows
          {syncedCount > 0 && (
            <Box component="span" sx={{ color: '#16a34a', fontWeight: 600 }}>
              {' '}
              · {syncedCount} already synced
            </Box>
          )}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 128, ml: 'auto' }}>
          <Select value={bankFilter} onChange={(e) => { setBankFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">All Banks</MenuItem>
            {BANK_TYPE_OPTIONS.map((b) => (
              <MenuItem key={b} value={b}>{b}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 168 }}>
          <Select
            value={createdByOptions.includes(createdByFilter) ? createdByFilter : 'all'}
            onChange={(e) => { setCreatedByFilter(e.target.value); setPage(0); }}
            renderValue={(v) => (v === 'all' ? 'All Created By' : v)}
            title="Show only the mappings made by one person"
          >
            <MenuItem value="all">All Created By</MenuItem>
            {createdByOptions.map((u) => (
              <MenuItem key={u} value={u} sx={{ fontSize: '13px' }}>{u}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search any column..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ width: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <IconButton
          size="small"
          onClick={fetchMapped}
          aria-label="Refresh"
          sx={{
            color: '#1e40af',
            border: '1px solid #cbd5e1',
            borderRadius: '6px',
            '&:hover': { borderColor: '#1e40af', backgroundColor: '#e0e7ff' },
          }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          borderRadius: '8px',
          maxHeight: 'calc(100vh - 360px)',
          overflow: 'auto',
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...HEADER_SX, width: 36, padding: '6px 0 6px 2px' }} title="Select transactions to acknowledge">
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={somePageSelected}
                  onChange={toggleAckAll}
                  disabled={pageTrxIds.length === 0}
                  sx={{ p: 0.5, color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }}
                />
              </TableCell>
              <TableCell sx={HEADER_SX}>Transaction ID</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'right' }}>Txn Amount</TableCell>
              <TableCell sx={{ ...HEADER_SX, minWidth: 200 }}>Invoice No</TableCell>
              <TableCell sx={HEADER_SX}>Client</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'right' }}>Allocated</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'right' }}>TDS</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'right' }}>Settled</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Tally Sync</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Mail Sent</TableCell>
              <TableCell sx={HEADER_SX}>Created By</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Created At</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length > 0 ? (
              paginated.map((l, idx) => (
                <TableRow
                  key={`${l.trx_id}-${l.reference_no}-${idx}`}
                  sx={{
                    '&:hover': { backgroundColor: l._noLedger ? '#fee2e2' : '#e8f4fd' },
                    backgroundColor: l._noLedger ? '#fef2f2' : idx % 2 === 0 ? '#ffffff' : '#f8faff',
                    ...(l._noLedger ? { borderLeft: '3px solid #dc2626' } : {}),
                  }}
                >
                  <TableCell sx={{ padding: '0 0 0 2px', width: 36 }}>
                    <Checkbox
                      size="small"
                      checked={ackSelected.has(l.trx_id)}
                      disabled={!canAckLine(l)}
                      onChange={() => toggleAck(l.trx_id)}
                      title={canAckLine(l) ? 'Select this transaction' : 'Partially mapped — cannot acknowledge yet'}
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {l.trx_id ?? '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'right', color: '#475569' }}>
                    {formatINR(l.txnAmount)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', fontFamily: 'monospace', minWidth: 200, whiteSpace: 'nowrap' }}>{l.reference_no ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={l._noLedger ? { color: '#b91c1c', fontWeight: 600 } : undefined}>
                        {l.client_name ?? '—'}
                      </span>
                      {l._noLedger && (
                        <Chip
                          size="small"
                          label="No Tally ledger"
                          title="This client has no tally_ledger_name. Importing it would create a new ledger in Tally, so the row is excluded from both Tally exports."
                          sx={{ height: 16, fontSize: '9px', fontWeight: 700, backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca' }}
                        />
                      )}
                    </Box>
                    {l.client_id && (
                      <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>{l.client_id}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'right', fontWeight: 600, color: '#16a34a' }}>
                    {formatINR(l.allocated)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'right', color: '#7c3aed' }}>
                    {l.tds_amount > 0 ? `${formatINR(l.tds_amount)} (${l.tds_pct}%)` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'right', fontWeight: 600, color: '#0059B2' }}>
                    {formatINR(l.settled)}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <StatusChip status={l.tally} />
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <StatusChip status={l.mail} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', color: '#64748b' }}>{l.created_by ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '11px', textAlign: 'center', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {fmtDateTime(l.created_at)}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    {allowed('Unmap') && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={unmapping}
                      startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setUnmapConfirm({ trx_id: l.trx_id, invoice_num: l.reference_no })}
                      sx={{
                        textTransform: 'none',
                        fontSize: '11px',
                        py: 0.25,
                        color: '#dc2626',
                        borderColor: '#fecaca',
                        '&:hover': { borderColor: '#dc2626', backgroundColor: '#fef2f2' },
                      }}
                    >
                      Unmap
                    </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={13} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                  <Typography variant="body2">
                    {lines.length === 0 ? 'No mapped payments found.' : 'No mapped lines match your search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 15, 25, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={safePage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{ borderTop: '1px solid #e2e8f0' }}
      />

      {/* asked before every Tally export: should the clients be told first? */}
      <Dialog
        open={exportPrompt}
        onClose={() => !exporting && setExportPrompt(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DownloadIcon fontSize="small" />
          Send acknowledgements before exporting?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>
            The file will contain <strong>{exportable.length}</strong> invoice line
            {exportable.length === 1 ? '' : 's'} across <strong>{exportTxnIds.length}</strong> transaction
            {exportTxnIds.length === 1 ? '' : 's'} that are not yet synced to Tally.
          </Typography>

          {noLedgerCount > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, fontSize: '12.5px' }}>
              <strong>{noLedgerCount}</strong> row{noLedgerCount === 1 ? '' : 's'} are held back because the client has
              no Tally ledger name. Tally would create a new ledger for them on import instead of posting to the right
              one. They are marked red in the table — set their ledger name, then export again.
            </Alert>
          )}

          {exportUnacked.length > 0 ? (
            <Alert severity="info" sx={{ mt: 1.5, fontSize: '12.5px' }}>
              <strong>{exportUnacked.length}</strong> of these transaction{exportUnacked.length === 1 ? ' has' : 's have'} not
              been acknowledged to the client yet. Send the acknowledgement{exportUnacked.length === 1 ? '' : 's'} now and
              then download the file?
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mt: 1.5, fontSize: '12.5px' }}>
              Every transaction in this file has already been acknowledged — nothing to send.
            </Alert>
          )}

          {exportUnacked.length > 0 && (
            <Box sx={{ mt: 1.5, maxHeight: 150, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {exportUnacked.map((id) => (
                  <Chip
                    key={id}
                    size="small"
                    label={id}
                    sx={{ height: 20, fontSize: '10px', fontFamily: 'monospace', fontWeight: 600, backgroundColor: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setExportPrompt(false)} disabled={exporting} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            onClick={() => handleExportWithAck(false)}
            disabled={exporting}
            sx={{ textTransform: 'none', color: '#1e40af' }}
          >
            Export only
          </Button>
          {exportUnacked.length > 0 && allowed('Send Acknowledgement') && (
            <Button
              variant="contained"
              disabled={exporting}
              startIcon={<CheckCircleIcon />}
              onClick={() => handleExportWithAck(true)}
              sx={{ textTransform: 'none', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
            >
              Send &amp; export
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={ackConfirm}
        onClose={() => !sendingAck && setAckConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon fontSize="small" />
          Send acknowledgement for {ackSummary.length} transaction{ackSummary.length === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569', mb: 1 }}>
            An acknowledgement will be sent for the transactions listed below.
          </Typography>
          {ackAlreadySent.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1, fontSize: '12px' }}>
              An acknowledgement has already been sent for {ackAlreadySent.length} of these transactions (marked{' '}
              <strong>Sent</strong> below) — sending again will mail the client a second time.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px', maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HEADER_SX, fontSize: '11px' }}>Transaction</TableCell>
                  <TableCell sx={{ ...HEADER_SX, fontSize: '11px' }}>Client</TableCell>
                  <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Amount</TableCell>
                  <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'center' }}>Mail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ackSummary.map((t, i) => (
                  <TableRow
                    key={t.trx_id}
                    sx={{ backgroundColor: t.mail?.key === 'SENT' ? '#fffbeb' : i % 2 === 0 ? '#ffffff' : '#f8faff' }}
                  >
                    <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 600 }}>
                      {t.trx_id}
                      <Typography sx={{ fontSize: '9px', color: '#94a3b8', fontFamily: 'inherit' }}>
                        {t.invoices} invoice{t.invoices === 1 ? '' : 's'} · {fmtDate(t.payment_date)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '11px' }}>
                      {Array.from(t.clients).join(', ') || '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '11px', textAlign: 'right', fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>
                      {formatINR(t.allocated)}
                      {t.txnAmount > 0 && t.txnAmount !== t.allocated && (
                        <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>of {formatINR(t.txnAmount)}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <StatusChip status={t.mail} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setAckConfirm(false)} disabled={sendingAck} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={sendingAck || ackSummary.length === 0}
            startIcon={<CheckCircleIcon />}
            onClick={() => {
              setAckConfirm(false);
              handleSendAck();
            }}
            sx={{ textTransform: 'none', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
          >
            {ackAlreadySent.length > 0 ? (ackAlreadySent.length === ackSummary.length ? 'Send Again' : 'Send All') : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!unmapConfirm}
        onClose={() => setUnmapConfirm(null)}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOffIcon fontSize="small" />
          Unmap invoice?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>
            Are you sure you want to unmap this invoice from the transaction?
          </Typography>
          {unmapConfirm && (
            <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 1, fontFamily: 'monospace' }}>
              {unmapConfirm.trx_id} · {unmapConfirm.invoice_num}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setUnmapConfirm(null)} disabled={unmapping} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={unmapping}
            startIcon={<LinkOffIcon />}
            onClick={() => {
              const { trx_id: trxId, invoice_num: invoiceNum } = unmapConfirm || {};
              setUnmapConfirm(null);
              handleUnmap(trxId, invoiceNum);
            }}
            sx={{ textTransform: 'none', backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Unmap
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// ───────────────────────── auto mapping approvals view ─────────────────────────
function AutoApprovalsView({ from, to, email, can, descByTrx, amountByTrx, refreshSignal, onChanged }) {
  const allowed = (action) => (typeof can === 'function' ? can(action) : true);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState({}); // { [trx_id]: clientKey } — one client per txn
  const [working, setWorking] = useState(false);
  const [confirm, setConfirm] = useState(null); // { action: 'approve' | 'reject', trxId? }
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });
  const [rerunConfirm, setRerunConfirm] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  // TDS % chosen by hand for a transaction's client, keyed "trx_id|clientKey".
  // Absent means "keep whatever auto mapping proposed".
  const [tdsOverrides, setTdsOverrides] = useState({});

  const fetchAuto = async () => {
    const api = process.env.REACT_APP_GET_AUTO_MAPPING_APPROVALS;
    if (!api) {
      setError('API not configured (REACT_APP_GET_AUTO_MAPPING_APPROVALS)');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(api, { params: { start_date: from, end_date: to } });
      const raw = res?.data?.data ?? res?.data ?? [];
      setData(Array.isArray(raw) ? raw : []);
      setSelection({});
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load auto mappings');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, refreshSignal]);

  const clientKeyOfLine = (l) => String(l.client_fk ?? l.client_name ?? '');

  // normalize suggestion rows
  const lines = useMemo(
    () =>
      data.map((r) => {
        const allocated = num(r.amt ?? r.allocated);
        const tdsPct = num(r.tds_per ?? r.tds_pct);
        // TDS is a percentage of the invoice value; fall back to the payment when
        // the approvals feed does not carry the invoice amount
        const invoiceAmount = num(r.invoice_amount ?? r.inv_amount ?? r.due ?? r.outstanding);
        let tdsAmt = num(r.tds_amt ?? r.tds_amount);
        if (!tdsAmt && tdsPct) tdsAmt = computeTds(invoiceAmount || allocated, tdsPct);
        return {
          id: r.id,
          trx_id: r.trx_id,
          payment_date: r.trx_date ?? r.payment_date ?? r.created_at,
          // transaction-level fields only — never fall back to `amt`, which is
          // this suggestion line's allocated amount, not the payment total
          txnAmount: num(
            r.trx_amt ?? r.txn_amount ?? r.trx_amount ?? r.transaction_amount ?? r.payment_amount
          ),
          trx_mode: r.trx_mode ?? r.payment_mode ?? '',
          // the bank narration, straight off the suggestion row — without this the
          // description only appeared for transactions the Payments tab had loaded
          description: r.trx_description ?? r.description ?? r.narration ?? r.particulars ?? '',
          reference_no: r.invoice_num ?? r.reference_no,
          client_name: r.client_name,
          client_fk: r.client_fk ?? r.client_id,
          allocated,
          invoice_amount: invoiceAmount,
          tds_pct: tdsPct,
          tds_amount: tdsAmt,
          settled: allocated + tdsAmt,
          match: r.match_reason ?? r.confidence ?? '',
        };
      }),
    [data]
  );

  // group by transaction, then by candidate client (client-wise)
  const groups = useMemo(() => {
    const map = new Map();
    lines.forEach((l) => {
      if (!map.has(l.trx_id)) {
        map.set(l.trx_id, {
          trx_id: l.trx_id,
          payment_date: l.payment_date,
          // the payments API wins; the suggestion row's own field is the fallback
          txnAmount: num(amountByTrx?.[l.trx_id]) || l.txnAmount,
          trx_mode: l.trx_mode,
          description: l.description || (descByTrx && descByTrx[l.trx_id]) || '',
          clients: new Map(),
        });
      }
      const g = map.get(l.trx_id);
      const ck = clientKeyOfLine(l);
      if (!g.clients.has(ck)) {
        g.clients.set(ck, { key: ck, client_fk: l.client_fk, client_name: l.client_name, invoices: [], total: 0, tds: 0 });
      }
      const c = g.clients.get(ck);
      c.invoices.push(l);
      c.total += l.allocated;
      c.tds += l.tds_amount;
    });
    return Array.from(map.values()).map((g) => {
      const clients = Array.from(g.clients.values());
      const searchable = searchHaystack([
        g.trx_id,
        ...dateTokens(g.payment_date),
        g.trx_mode,
        g.description,
        ...amountTokens(g.txnAmount),
        bankTypeOf(g),
        ...clients.flatMap((c) => [
          c.client_name,
          c.client_fk,
          ...amountTokens(c.total),
          ...amountTokens(c.tds),
          ...c.invoices.flatMap((inv) => [inv.reference_no, ...amountTokens(inv.allocated), inv.match]),
        ]),
      ]);
      // Last resort for the amount: each candidate client is a complete
      // alternative allocation of the same credit, so the largest candidate total
      // is the payment. Exact when there is only one candidate.
      const derived = clients.reduce((max, c) => Math.max(max, c.total + c.tds), 0);
      const known = num(g.txnAmount);
      return {
        ...g,
        clients,
        txnAmount: known || derived,
        txnAmountDerived: !known && derived > 0,
        _search: searchable,
      };
    });
  }, [lines, descByTrx, amountByTrx]);

  const searchLower = search.trim().toLowerCase();
  const filteredGroups = useMemo(
    () =>
      groups.filter(
        (g) => inPaymentRange(g.payment_date, from, to) && matchesQuery(g._search, searchLower)
      ),
    [groups, searchLower, from, to]
  );

  // auto-select the only candidate for single-client transactions
  useEffect(() => {
    setSelection((prev) => {
      const next = { ...prev };
      groups.forEach((g) => {
        if (g.clients.length === 1 && next[g.trx_id] === undefined) next[g.trx_id] = g.clients[0].key;
      });
      return next;
    });
  }, [groups]);

  const selectedTxns = groups.filter((g) => selection[g.trx_id]);
  const selectedCount = selectedTxns.length;

  // Ask the backend to run auto-mapping again over the selected dates and rebuild
  // its suggestions. Suggestions only — this approves nothing.
  const handleRerun = async () => {
    const api = process.env.REACT_APP_RERUN_AUTO_MAPPING;
    if (!api) {
      setSnack({ open: true, severity: 'warning', message: 'Rerun API not configured (REACT_APP_RERUN_AUTO_MAPPING).' });
      return;
    }
    setRerunning(true);
    try {
      const res = await axios.post(api, { start_date: from, end_date: to, updated_by: email });
      const found = res?.data?.count ?? res?.data?.suggestions ?? null;
      setSnack({
        open: true,
        severity: 'success',
        message:
          found === null ? 'Auto mapping re-run finished.' : `Auto mapping re-run finished — ${found} suggestion(s).`,
      });
      await fetchAuto();
      if (onChanged) onChanged();
    } catch (err) {
      console.error('Failed to re-run auto mapping:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to re-run auto mapping',
      });
    } finally {
      setRerunning(false);
    }
  };

  const handleExport = () => {
    const out = [];
    filteredGroups.forEach((g) => {
      g.clients.forEach((c) => {
        c.invoices.forEach((inv) => {
          out.push({
            'Transaction ID': g.trx_id ?? '',
            Description: g.description ?? '',
            'Payment Date': g.payment_date ? fmtDate(g.payment_date) : '',
            'Txn Amount': g.txnAmount,
            Client: c.client_name ?? '',
            'Client ID': c.client_fk ?? '',
            'Invoice No': inv.reference_no ?? '',
            Amount: inv.allocated,
            'TDS %': tdsForLine(g, c, inv).tds_per,
            TDS: tdsForLine(g, c, inv).tds_amt,
            Settled: num(inv.allocated) + tdsForLine(g, c, inv).tds_amt,
          });
        });
      });
    });
    exportRowsToExcel(out, `Auto_Mapping_Approvals_${from}_to_${to}.xlsx`);
  };

  const tdsKey = (trxId, clientKey) => `${trxId}|${clientKey}`;

  // the % auto mapping proposed for this client, used as the dropdown's start value
  const suggestedTdsPct = (c) => {
    const hit = c.invoices.map((i) => num(i.tds_pct)).find((pv) => pv > 0);
    return hit ? String(hit) : '0';
  };

  const tdsPctOf = (g, c) => tdsOverrides[tdsKey(g.trx_id, c.key)] ?? suggestedTdsPct(c);

  // untouched rows keep the amount that came from the API; once a % is picked the
  // amount is recomputed per invoice line
  const tdsAmtOf = (g, c) => {
    const pct = tdsOverrides[tdsKey(g.trx_id, c.key)];
    if (pct === undefined) return c.tds;
    return c.invoices.reduce((sum, inv) => sum + computeTds(tdsBaseOf(inv), pct), 0);
  };

  const setTdsFor = (g, c, pct) =>
    setTdsOverrides((prev) => ({ ...prev, [tdsKey(g.trx_id, c.key)]: pct }));

  // what to send for one invoice line: the override when present, else the suggestion
  const tdsForLine = (g, c, inv) => {
    const pct = tdsOverrides[tdsKey(g.trx_id, c.key)];
    if (pct === undefined) return { tds_per: num(inv.tds_pct), tds_amt: num(inv.tds_amount) };
    return { tds_per: num(pct), tds_amt: computeTds(tdsBaseOf(inv), pct) };
  };

  const performApprove = async (onlyTrxId) => {
    const api = process.env.REACT_APP_SAVE_TRX_INVOICES;
    if (!api) {
      setSnack({ open: true, severity: 'warning', message: 'Save API not configured (REACT_APP_SAVE_TRX_INVOICES).' });
      return;
    }
    // approve a single transaction, or all selected ones
    const txns = onlyTrxId
      ? groups.filter((g) => g.trx_id === onlyTrxId && selection[g.trx_id])
      : selectedTxns;
    // one record per invoice line of the chosen client, per transaction
    const items = [];
    txns.forEach((g) => {
      const client = g.clients.find((c) => c.key === selection[g.trx_id]);
      if (!client) return;
      client.invoices.forEach((inv) => {
        // A zero allocation would settle an invoice with nothing. The card blocks
        // this, but guard the payload too — a stale render must not write one.
        if (num(inv.allocated) <= 0) return;
        const tds = tdsForLine(g, client, inv);
        items.push({
          trx_id: g.trx_id,
          client_fk: client.client_fk,
          invoice_num: inv.reference_no,
          amt: inv.allocated,
          tds_per: tds.tds_per,
          tds_amt: tds.tds_amt,
          mapping_type: 'auto',
          created_by: email,
        });
      });
    });
    if (items.length === 0) {
      setSnack({
        open: true,
        severity: 'warning',
        message: 'Nothing to approve — the suggested invoices carry no amount.',
      });
      return;
    }
    setWorking(true);
    try {
      await axios.post(api, items);
      setSnack({
        open: true,
        severity: 'success',
        message: `${txns.length} transaction${txns.length === 1 ? '' : 's'} approved.`,
      });
      if (onlyTrxId) {
        setSelection((prev) => {
          const next = { ...prev };
          delete next[onlyTrxId];
          return next;
        });
      } else {
        setSelection({});
      }
      if (onChanged) onChanged();
      else fetchAuto();
    } catch (err) {
      console.error('Failed to approve:', err);
      setSnack({ open: true, severity: 'error', message: err?.response?.data?.detail || err?.message || 'Failed to approve' });
    } finally {
      setWorking(false);
    }
  };

  const performReject = async (trxId) => {
    const api = process.env.REACT_APP_REJECT_AUTO_MAPPING;
    if (!api) {
      setSnack({ open: true, severity: 'warning', message: 'Reject API not configured.' });
      return;
    }
    setWorking(true);
    try {
      await axios.post(api, { trx_id: trxId, updated_by: email });
      setSnack({ open: true, severity: 'success', message: 'Auto mapping rejected.' });
      if (onChanged) onChanged();
      else fetchAuto();
    } catch (err) {
      console.error('Failed to reject:', err);
      setSnack({ open: true, severity: 'error', message: err?.response?.data?.detail || err?.message || 'Failed to reject' });
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (error) {
    return <Box sx={{ p: 4, textAlign: 'center', color: '#dc2626', fontSize: '14px' }}>{error}</Box>;
  }

  return (
    <>
      {/* KPI strip */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <StatCard label="Pending Transactions" value={groups.length} />
        <StatCard
          label="Multi-client"
          value={groups.filter((g) => g.clients.length > 1).length}
          palette={{ bg: '#eef2ff', border: '#4338ca', color: '#4338ca', Icon: GroupsIcon }}
        />
        <StatCard
          label="Selected"
          value={selectedCount}
          palette={{ bg: '#ecfdf5', border: '#16a34a', color: '#16a34a', Icon: CheckCircleIcon }}
        />
      </Box>

      {/* toolbar: bulk approve + search */}
      <Box sx={{ mb: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
          disabled={lines.length === 0}
          onClick={handleExport}
          sx={{ textTransform: 'none', fontSize: '12px', color: '#1e40af', borderColor: '#93c5fd' }}
        >
          Export
        </Button>
        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>
          {filteredGroups.length} of {groups.length} transactions
        </Typography>
        <TextField
          size="small"
          placeholder="Search any column..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240, ml: 'auto' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {allowed('Rerun Auto Mapping') && (
        <Button
          size="small"
          variant="outlined"
          disabled={rerunning || working}
          startIcon={rerunning ? <CircularProgress size={13} sx={{ color: '#7c3aed' }} /> : <ReplayIcon sx={{ fontSize: 16 }} />}
          onClick={() => setRerunConfirm(true)}
          title="Run auto mapping again for the selected date range and rebuild the suggestions"
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            color: '#7c3aed',
            borderColor: '#ddd6fe',
            '&:hover': { borderColor: '#7c3aed', backgroundColor: '#f5f3ff' },
          }}
        >
          {rerunning ? 'Rerunning…' : 'Rerun'}
        </Button>
        )}
        <IconButton
          size="small"
          onClick={fetchAuto}
          aria-label="Refresh"
          sx={{ color: '#1e40af', border: '1px solid #cbd5e1', borderRadius: '6px', '&:hover': { borderColor: '#1e40af', backgroundColor: '#e0e7ff' } }}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* One compact card per payment: say who paid, then approve. */}
      <Box sx={{ display: 'grid', gap: 0.75, pb: 2 }}>
        {filteredGroups.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>
              {groups.length === 0 ? 'Nothing is waiting for approval.' : 'No payments match your search.'}
            </Typography>
          </Paper>
        ) : (
          filteredGroups.map((g) => {
            const chosen = selection[g.trx_id];
            const chosenClient = g.clients.find((c) => c.key === chosen);
            const multi = g.clients.length > 1;
            // Approving a nil allocation would write a zero mapping, so it is blocked.
            const blocked = !!chosenClient && chosenClient.total <= 0;
            const canApprove = !!chosenClient && !blocked && !working;

            return (
              <Paper
                key={g.trx_id}
                sx={{
                  borderRadius: '8px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  overflow: 'hidden',
                  border: `1px solid ${chosenClient ? '#86efac' : '#e2e8f0'}`,
                }}
              >
                {/* the payment, on one line */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.6,
                    backgroundColor: '#f8faff',
                    borderBottom: '1px solid #e8edf5',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '15px', fontWeight: 800, color: g.txnAmount > 0 ? '#15803d' : '#b45309', flex: 'none' }}
                    title={g.txnAmountDerived ? 'Added up from the suggested invoices — the approvals data did not carry the payment total' : g.trx_id}
                  >
                    {g.txnAmount > 0 ? `${g.txnAmountDerived ? '≈' : ''}${formatINR(g.txnAmount)}` : 'no amount'}
                  </Typography>
                  <Typography sx={{ fontSize: '11.5px', color: '#475569', flex: 'none' }}>{fmtDate(g.payment_date)}</Typography>
                  {g.trx_mode && (
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#4338ca', backgroundColor: '#eef2ff', px: 0.5, borderRadius: '3px', flex: 'none' }}>
                      {g.trx_mode}
                    </Typography>
                  )}
                  <Typography
                    title="Transaction ID"
                    sx={{
                      fontSize: '10.5px',
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      color: '#334155',
                      backgroundColor: '#e8edf5',
                      px: 0.5,
                      borderRadius: '3px',
                      flex: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {g.trx_id}
                  </Typography>
                  <Typography
                    title={g.description || ''}
                    sx={{ fontSize: '11.5px', color: '#64748b', flex: 1, minWidth: 40, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {g.description}
                  </Typography>
                  {!chosenClient && (
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: '#b45309', flex: 'none', whiteSpace: 'nowrap' }}>
                      {multi ? `pick 1 of ${g.clients.length}` : 'confirm the client'}
                    </Typography>
                  )}
                  {allowed('Approve Auto Mapping') && (
                    <Box sx={{ display: 'flex', gap: 0.5, flex: 'none' }}>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={!canApprove}
                        onClick={() => setConfirm({ action: 'approve', trxId: g.trx_id })}
                        sx={{ textTransform: 'none', fontSize: '11.5px', fontWeight: 700, py: 0.1, minWidth: 74, backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={working}
                        onClick={() => setConfirm({ action: 'reject', trxId: g.trx_id })}
                        sx={{ textTransform: 'none', fontSize: '11.5px', py: 0.1, minWidth: 74, color: '#dc2626', borderColor: '#fecaca', '&:hover': { borderColor: '#dc2626', backgroundColor: '#fef2f2' } }}
                      >
                        Not these
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* one line per candidate client, each in its own outlined row */}
                <Box sx={{ display: 'grid', gap: 0.4, p: 0.5 }}>
                {g.clients.map((c) => {
                  const isChosen = chosen === c.key;
                  const tdsAmt = tdsAmtOf(g, c);
                  const refs = c.invoices.map((inv) => `${inv.reference_no ?? '—'}  ${formatINR(inv.allocated)}`).join('\n');
                  return (
                    <Box
                      key={c.key}
                      onClick={() => setSelection((prev) => ({ ...prev, [g.trx_id]: c.key }))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 0.6,
                        py: 0.3,
                        cursor: 'pointer',
                        borderRadius: '6px',
                        // each candidate is outlined so the eye can tell one row
                        // from the next, and the chosen one is unmistakable
                        border: `1px solid ${isChosen ? '#16a34a' : '#cbd5e1'}`,
                        boxShadow: isChosen ? 'inset 3px 0 0 #16a34a' : 'none',
                        backgroundColor: isChosen ? '#f0fdf4' : '#fff',
                        '&:hover': {
                          borderColor: isChosen ? '#15803d' : '#60a5fa',
                          backgroundColor: isChosen ? '#ecfdf5' : '#f8faff',
                        },
                      }}
                    >
                      <Radio
                        size="small"
                        checked={isChosen}
                        name={`trx-${g.trx_id}`}
                        onChange={() => setSelection((prev) => ({ ...prev, [g.trx_id]: c.key }))}
                        sx={{ p: 0.25, color: '#cbd5e1', '&.Mui-checked': { color: '#16a34a' }, flex: 'none' }}
                      />

                      <Typography
                        title={c.client_fk ? `Client ${c.client_fk}` : undefined}
                        sx={{
                          fontSize: '13px',
                          fontWeight: 700,
                          color: isChosen ? '#166534' : '#1e293b',
                          flex: 1,
                          minWidth: 90,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.client_name ?? c.client_fk ?? 'Unnamed client'}
                      </Typography>

                      {/* invoice references inline; hover for the full list with amounts */}
                      <Typography
                        title={`Invoices and the amount adjusted against each\n\n${refs}`}
                        sx={{
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          color: '#334155',
                          flex: 2,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: { xs: 'none', sm: 'block' },
                        }}
                      >
                        {c.invoices
                          .map((inv) => `${inv.reference_no ?? '—'} ${formatINR(inv.allocated)}`)
                          .join('  ·  ')}
                      </Typography>

                      <Typography sx={{ fontSize: '10px', color: '#94a3b8', flex: 'none', width: 42, textAlign: 'right' }}>
                        {c.invoices.length} inv
                      </Typography>

                      <Typography
                        sx={{ fontSize: '13.5px', fontWeight: 800, color: c.total > 0 ? '#0059B2' : '#b45309', flex: 'none', width: 96, textAlign: 'right' }}
                      >
                        {c.total > 0 ? formatINR(c.total) : 'no amount'}
                      </Typography>

                      {isChosen ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 'none' }} onClick={(e) => e.stopPropagation()}>
                          <TextField
                            select
                            size="small"
                            value={TDS_OPTIONS.includes(String(tdsPctOf(g, c))) ? String(tdsPctOf(g, c)) : '0'}
                            onChange={(e) => setTdsFor(g, c, e.target.value)}
                            SelectProps={{ sx: { fontSize: '11.5px' } }}
                            title="TDS % on the invoice amount"
                            sx={{ width: 76, backgroundColor: '#fff', '& .MuiSelect-select': { padding: '2px 6px' } }}
                          >
                            {TDS_OPTIONS.map((t) => (
                              <MenuItem key={t} value={t} sx={{ fontSize: '12px' }}>
                                {t}%
                              </MenuItem>
                            ))}
                          </TextField>
                          <Typography
                            sx={{ fontSize: '11px', fontWeight: 700, color: blocked ? '#b45309' : '#0f766e', width: 150, textAlign: 'right', whiteSpace: 'nowrap' }}
                            title={blocked ? 'No amount on these invoices — approve is blocked' : 'Payment + TDS settles this much'}
                          >
                            {blocked
                              ? 'no amount — map by hand'
                              : tdsAmt > 0
                                ? `+ ${formatINR(tdsAmt)} = ${formatINR(c.total + tdsAmt)}`
                                : 'no TDS'}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '11px', color: '#7c3aed', flex: 'none', width: 226, textAlign: 'right' }}>
                          {c.tds > 0 ? `TDS ${formatINR(c.tds)}` : ''}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
                </Box>
              </Paper>
            );
          })
        )}
      </Box>

      <Dialog
        open={rerunConfirm}
        onClose={() => !rerunning && setRerunConfirm(false)}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReplayIcon fontSize="small" />
          Re-run auto mapping?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>
            Auto mapping will run again for <strong>{from}</strong> to <strong>{to}</strong> and its suggestions on this
            tab will be rebuilt.
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 1 }}>
            Only pending suggestions change — nothing is approved, and mappings you have already approved are left
            alone. This can take a while on a wide date range.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setRerunConfirm(false)} disabled={rerunning} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={rerunning}
            startIcon={<ReplayIcon />}
            onClick={() => {
              setRerunConfirm(false);
              handleRerun();
            }}
            sx={{ textTransform: 'none', backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
          >
            Rerun
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle
          sx={{
            fontSize: '15px',
            fontWeight: 700,
            color: confirm?.action === 'reject' ? '#dc2626' : '#16a34a',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {confirm?.action === 'reject' ? <CancelIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          {confirm?.action === 'reject'
            ? 'Reject this auto mapping?'
            : confirm?.trxId
              ? 'Approve this transaction?'
              : `Approve ${selectedCount} transaction${selectedCount === 1 ? '' : 's'}?`}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>
            {confirm?.action === 'reject'
              ? 'The auto-suggested mapping for this transaction will be rejected.'
              : 'The selected client will be mapped to each transaction and approved.'}
          </Typography>
          {confirm?.action === 'reject' && confirm?.trxId && (
            <Typography sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#94a3b8', mt: 1 }}>
              {confirm.trxId}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setConfirm(null)} disabled={working} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={working}
            onClick={() => {
              const c = confirm;
              setConfirm(null);
              if (c?.action === 'reject') performReject(c.trxId);
              else performApprove(c?.trxId);
            }}
            sx={{
              textTransform: 'none',
              backgroundColor: confirm?.action === 'reject' ? '#dc2626' : '#16a34a',
              '&:hover': { backgroundColor: confirm?.action === 'reject' ? '#b91c1c' : '#15803d' },
            }}
          >
            {confirm?.action === 'reject' ? 'Reject' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}

// ───────────────────────── payment acknowledgements view ─────────────────────────
function AcknowledgementsView({ from, to, refreshSignal }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const fetchAcks = async () => {
    const api = process.env.REACT_APP_GET_PAYMENT_ACKNOWLEDGEMENT;
    if (!api) {
      setError('API not configured (REACT_APP_GET_PAYMENT_ACKNOWLEDGEMENT)');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(api, { params: { start_date: from, end_date: to } });
      const raw = res?.data?.data ?? res?.data ?? [];
      setData(Array.isArray(raw) ? raw : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load payment acknowledgements');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, refreshSignal]);

  // one row per acknowledgement record, exactly as the API returns it
  const rows = useMemo(
    () =>
      data.map((r, i) => ({
        id: r.id ?? `${r.trx_id ?? 'row'}-${i}`,
        trx_id: r.trx_id ?? '',
        client_fk: r.client_fk,
        client_name: r.client_name ?? '',
        unique_id: r.unique_id ?? '',
        invoice_num: r.invoice_num ?? '',
        amt: num(r.amt),
        mailIds: (r.mail_ids ?? '')
          .toString()
          .split(/[,;]/)
          .map((m) => m.trim())
          .filter(Boolean),
        // no timestamp passed — the Sent At column carries it, so the chip stays compact
        mail: mailStatusOf({ mail_status: r.mail_status }),
        sent_by: r.sent_by ?? '',
        sent_at: r.sent_at ?? null,
      })).map((r) => ({
        ...r,
        _search: searchHaystack([
          r.trx_id,
          r.client_name,
          r.client_fk,
          r.unique_id,
          r.invoice_num,
          ...amountTokens(r.amt),
          ...r.mailIds,
          r.mail.key === 'UNKNOWN' ? '' : r.mail.label,
          r.sent_by,
          ...dateTokens(r.sent_at),
        ]),
      })),
    [data]
  );

  const searchLower = search.trim().toLowerCase();
  const filtered = useMemo(
    () => rows.filter((r) => matchesQuery(r._search, searchLower)),
    [rows, searchLower]
  );

  const totalRows = filtered.length;
  const maxPage = totalRows <= 0 ? 0 : Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
  const safePage = page > maxPage ? maxPage : page;
  const paginated = filtered.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (totalRows > 0 && page > maxPage) setPage(maxPage);
  }, [totalRows, rowsPerPage, page, maxPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (error) {
    return <Box sx={{ p: 4, textAlign: 'center', color: '#dc2626', fontSize: '14px' }}>{error}</Box>;
  }

  return (
    <>
      {/* search only — the date range lives in the shared filter bar above */}
      <Box sx={{ mb: 1.5, display: 'flex' }}>
        <TextField
          size="small"
          placeholder="Search any column..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          sx={{ width: 280, ml: 'auto' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderRadius: '8px', maxHeight: 'calc(100vh - 330px)', overflow: 'auto' }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEADER_SX}>Transaction ID</TableCell>
              <TableCell sx={HEADER_SX}>Client Name</TableCell>
              <TableCell sx={HEADER_SX}>Unique ID</TableCell>
              <TableCell sx={HEADER_SX}>Invoice No</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'right' }}>Amount</TableCell>
              <TableCell sx={{ ...HEADER_SX, minWidth: 220 }}>Mail IDs</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Mail Status</TableCell>
              <TableCell sx={HEADER_SX}>Sent By</TableCell>
              <TableCell sx={{ ...HEADER_SX, textAlign: 'center' }}>Sent At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length > 0 ? (
              paginated.map((r, idx) => (
                <TableRow
                  key={r.id}
                  sx={{ '&:hover': { backgroundColor: '#e8f4fd' }, backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8faff' }}
                >
                  <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {r.trx_id || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px' }}>
                    {r.client_name || '—'}
                    {r.client_fk != null && r.client_fk !== '' && (
                      <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>{r.client_fk}</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#475569', whiteSpace: 'nowrap' }}>
                    {r.unique_id || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', color: '#334155', wordBreak: 'break-all' }}>
                    {r.invoice_num || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '12px', textAlign: 'right', fontWeight: 600, color: '#16a34a', whiteSpace: 'nowrap' }}>
                    {formatINR(r.amt)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', maxWidth: 320 }}>
                    {r.mailIds.length === 0 ? (
                      <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>—</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                        {r.mailIds.map((m) => (
                          <Chip
                            key={m}
                            size="small"
                            icon={<MailOutlineIcon style={{ fontSize: 12, color: '#4338ca' }} />}
                            label={m}
                            title={m}
                            sx={{
                              backgroundColor: '#eef2ff',
                              color: '#3730a3',
                              border: '1px solid #c7d2fe',
                              fontSize: '9.5px',
                              height: 19,
                              maxWidth: 300,
                              '& .MuiChip-icon': { marginLeft: '4px' },
                              '& .MuiChip-label': { paddingLeft: '4px', paddingRight: '6px' },
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <StatusChip status={r.mail} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', color: '#64748b', wordBreak: 'break-all' }}>
                    {r.sent_by || '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '11px', textAlign: 'center', color: '#475569', whiteSpace: 'nowrap' }}>
                    {r.sent_at ? fmtDateTime(r.sent_at) : '—'}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                  <Typography variant="body2">
                    {rows.length === 0
                      ? 'No payment acknowledgements in this date range.'
                      : 'No acknowledgements match your search.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 15, 25, 50]}
        component="div"
        count={totalRows}
        rowsPerPage={rowsPerPage}
        page={safePage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{ borderTop: '1px solid #e2e8f0' }}
      />
    </>
  );
}

// ───────────────────────── main dashboard ─────────────────────────
function PaymentsMapping({ email, handleAccess }) {
  // action-level permissions for this page; without a checker everything is allowed
  const can = useCallback(
    (action) => (typeof handleAccess === 'function' ? handleAccess('PaymentMapping', action) : true),
    [handleAccess]
  );

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]); // previous payments, for payer matching
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(new Map()); // trx_id -> { lines, clients }
  // what the history feed actually did, so a silent miss is visible in the dialog
  const [historyInfo, setHistoryInfo] = useState({ state: 'idle', rows: 0, message: '' });
  const historyInflight = useRef(false);
  const [clientPhones, setClientPhones] = useState([]); // client phone book, for narration phone matching
  const [phonesLoaded, setPhonesLoaded] = useState(false);

  // local (optimistic) mappings — keyed by row id
  const [mappings, setMappings] = useState({});

  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(monthEndISO());
  const [modeFilter, setModeFilter] = useState('all'); // payment mode
  const [typeFilter, setTypeFilter] = useState('all'); // mapped type
  const [mapFilter, setMapFilter] = useState('all'); // mapping status
  const [bankFilter, setBankFilter] = useState('all'); // AXIS / ICICI
  const [orderBy, setOrderBy] = useState('payment_date'); // newest payment first
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [expanded, setExpanded] = useState(new Set());

  const [dialogRid, setDialogRid] = useState(null);
  const [tab, setTab] = useState('payments');
  const [unmappedRids, setUnmappedRids] = useState(new Set()); // rows unmapped this session
  const [unmapConfirmRid, setUnmapConfirmRid] = useState(null); // pending unmap confirmation
  const [refreshSignal, setRefreshSignal] = useState(0); // bump to refresh the mapped tab
  const [ackSelected, setAckSelected] = useState(new Set()); // fully-mapped rows chosen for acknowledgement
  const [sendingAck, setSendingAck] = useState(false);
  const [ackConfirm, setAckConfirm] = useState(false);
  const [auditTrxId, setAuditTrxId] = useState(null); // trx whose audit trail is open
  const [uploadOpen, setUploadOpen] = useState(false); // bank statement upload dialog
  const [lastRefreshed, setLastRefreshed] = useState(null); // when the payments list last loaded
  const [suspenseRid, setSuspenseRid] = useState(null); // trx being parked in suspense
  const [suspenseRemark, setSuspenseRemark] = useState('');
  const [otherRid, setOtherRid] = useState(null); // trx being classified as a non-invoice receipt
  const [otherCategory, setOtherCategory] = useState('');
  const [otherRemark, setOtherRemark] = useState('');

  const toggleExpand = (k) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  // ── fetch transactions ──
  // silent = a background poll: no spinner, and a failure leaves the table as it is
  const fetchPayments = async ({ silent = false } = {}) => {
    const api = process.env.REACT_APP_CLIENT_PAYMENT_DETAILS;
    if (!api) {
      setError('API not configured (REACT_APP_CLIENT_PAYMENT_DETAILS)');
      setLoading(false);
      return;
    }
    if (!from || !to) {
      setList([]);
      setLoading(false);
      return;
    }
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await axios.get(api, { params: { start_date: from, end_date: to } });
      const data = res?.data?.data ?? res?.data ?? [];
      const arr = Array.isArray(data) ? data : [];
      setList(
        arr.map((r, i) => {
          // Normalize the trx_* transaction shape to the fields this screen uses.
          const payment_date = r.trx_date ?? r.payment_date;
          const description = r.trx_description ?? r.description;
          const status = r.trx_status ?? r.status;
          const amount = r.trx_amt ?? r.amount;
          const mode = r.trx_mode ?? r.payment_mode ?? '';
          const account = r.trx_account ?? r.account ?? '';
          const trxId = r.trx_id ?? r.id ?? r.txn_id;
          return {
            ...r,
            payment_date,
            description,
            status,
            amount,
            trx_mode: mode,
            trx_account: account,
            trx_id: trxId,
            mapping_status: r.mapping_status ?? '',
            mapped_amount: r.mapped_amount ?? null,
            mapping_type: r.mapping_type ?? '',
            is_recommend: r.is_recommend ?? '',
            _rid:
              trxId ??
              `${r.unique_id ?? ''}|${r.reference_no ?? ''}|${payment_date ?? ''}|${amount ?? ''}|${i}`,
          };
        })
      );
      setLastRefreshed(new Date());
    } catch (err) {
      if (silent) {
        console.error('Background payments refresh failed:', err);
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load payments');
        setList([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ── fetch invoices for the picker (all invoices, searchable) ──
  // Previous payments (narration + client) used to spot a payer we have seen
  // before. Fetched once, lazily, when a mapping dialog is first opened, and
  // deliberately non-fatal: mapping still works without it.
  // Client phone numbers, so a number in the narration can identify the payer
  // outright. Fetched once, lazily, and non-fatal like the history feed.
  const fetchClientPhones = async () => {
    const api = process.env.REACT_APP_GET_CLIENTS_WITH_PHONE;
    if (!api || phonesLoaded) return;
    try {
      const res = await axios.get(api, { withCredentials: true });
      const raw = rowsFromBody(res?.data);
      setClientPhones(raw);
      setPhonesLoaded(true);
    } catch (err) {
      console.error('Failed to load client phone numbers:', err);
    }
  };

  // Which transactions are sitting in Auto Mapping waiting for approval. Read
  // from the approvals feed itself — the payments feed carries no reliable flag.
  const fetchPendingApprovals = async () => {
    const api = process.env.REACT_APP_GET_AUTO_MAPPING_APPROVALS;
    if (!api) return;
    try {
      const res = await axios.get(api, {
        params: { start_date: from, end_date: to },
        withCredentials: true,
      });
      const rows = rowsFromBody(res?.data);
      const byTrx = new Map();
      rows.forEach((r) => {
        const id = r.trx_id;
        if (!id) return;
        const e = byTrx.get(id) || { lines: 0, clients: new Set() };
        e.lines += 1;
        const c = r.client_fk ?? r.client_id ?? r.client_name;
        if (c != null && c !== '') e.clients.add(String(c));
        byTrx.set(id, e);
      });
      setPendingApprovals(
        new Map(Array.from(byTrx, ([id, e]) => [id, { lines: e.lines, clients: e.clients.size }]))
      );
    } catch (err) {
      console.error('Failed to load pending auto approvals:', err);
    }
  };

  const fetchHistory = async () => {
    const api = process.env.REACT_APP_GET_HISTORY_OF_PAYMENTS;
    if (!api) {
      setHistoryInfo({ state: 'no-env', rows: 0, message: 'REACT_APP_GET_HISTORY_OF_PAYMENTS is not set.' });
      return;
    }
    if (historyLoaded || historyInflight.current) return;
    historyInflight.current = true;
    setHistoryInfo((p) => ({ ...p, state: 'loading' }));
    try {
      const res = await axios.get(api, { withCredentials: true });
      const body = res?.data;
      const raw = rowsFromBody(body);
      setPaymentHistory(raw);
      setHistoryLoaded(true);
      setHistoryInfo({
        state: raw.length ? 'ok' : 'empty',
        rows: raw.length,
        message: raw.length
          ? `${raw.length} previous payments loaded. Columns: ${Object.keys(raw[0] || {}).join(', ')}`
          : `${api} answered but carried no rows. Body keys: ${
              body && typeof body === 'object' ? Object.keys(body).join(', ') || '(none)' : typeof body
            }`,
      });
    } catch (err) {
      // left unlatched on purpose, so the next Map click tries again
      setHistoryInfo({
        state: 'error',
        rows: 0,
        message: `${api} failed: ${err?.response?.status ?? ''} ${
          err?.response?.statusText || err?.message || ''
        }`.trim(),
      });
      console.error('Failed to load payment history:', err);
    } finally {
      historyInflight.current = false;
    }
  };

  const fetchInvoices = async () => {
    const api = process.env.REACT_APP_GET_OVERALL_DUE;
    if (!api) {
      setInvoicesLoading(false);
      return;
    }
    setInvoicesLoading(true);
    try {
      const res = await axios.get(api);
      const data = res?.data?.data ?? res?.data ?? [];
      const arr = Array.isArray(data) ? data : [];

      // one row per reference no; credit/debit amounts come from the `credit` / `debit`
      // fields and are summed deduping by unique credit_num / debit_num.
      const byRef = new Map();
      arr.forEach((r) => {
        const reference_no = r.reference_no ?? r.invoice_num ?? r.invoice_number;
        if (!reference_no) return;
        const key = String(reference_no);
        if (!byRef.has(key)) {
          byRef.set(key, {
            ...r,
            reference_no,
            client_name: r.client_name,
            client_id: r.client_id ?? r.client_fk,
            group_name: r.group_name,
            inv_date: r.inv_date ?? r.invoice_date,
            invoice_amount: num(r.invoice_amount ?? r.invoice_amt ?? r.inv_amount),
            prev_payment: num(r.prev_payment ?? r.previous_payment ?? r.paid_amount),
            due: num(r.due ?? r.due_amount ?? r.outstanding ?? r.balance),
            _credits: new Map(),
            _debits: new Map(),
          });
        }
        const e = byRef.get(key);
        if (!num(e.invoice_amount)) e.invoice_amount = num(r.invoice_amount ?? r.invoice_amt ?? r.inv_amount);
        if (!num(e.prev_payment)) e.prev_payment = num(r.prev_payment ?? r.previous_payment ?? r.paid_amount);
        if (!num(e.due)) e.due = num(r.due ?? r.due_amount ?? r.outstanding ?? r.balance);
        // The note number only exists to deduplicate: the same credit note can
        // repeat across rows of one invoice. When the feed sends an amount with no
        // note number the amount is still real, so key it by its own value —
        // repeats collapse, genuinely different amounts each count.
        const noteKey = (v, amount) =>
          v != null && v !== '' ? `n:${v}` : `a:${amount}`;
        const creditAmt = num(r.credit ?? r.credit_amount ?? r.creditnote_amt);
        const debitAmt = num(r.debit ?? r.debit_amount ?? r.debitnote_amt);
        const creditNum = r.creditnote_num ?? r.credit_num ?? r.credit_note_num ?? r.cn_num;
        const debitNum = r.debitnote_num ?? r.debit_num ?? r.debit_note_num ?? r.dn_num;
        if (creditAmt) e._credits.set(noteKey(creditNum, creditAmt), creditAmt);
        if (debitAmt) e._debits.set(noteKey(debitNum, debitAmt), debitAmt);
      });

      const invoices = Array.from(byRef.values()).map((e) => ({
        ...e,
        credit: Array.from(e._credits.values()).reduce((s, v) => s + v, 0),
        debit: Array.from(e._debits.values()).reduce((s, v) => s + v, 0),
      }));
      setInvoices(invoices);
    } catch {
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    fetchPendingApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    fetchInvoices();
    // These were fetched by the Map click that also opened the dialog, so the
    // first suggestions were computed with no history and no phone list and only
    // corrected themselves once the responses landed. Load them with the tab.
    fetchHistory();
    fetchClientPhones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── derived rows with mapping info ──
  const rows = useMemo(
    () =>
      list.map((r) => {
        const allocs = mappings[r._rid] || [];
        const isUnmapped = unmappedRids.has(r._rid);
        const localAllocated = allocatedTotal(allocs);
        // total mapped = already mapped on server (unless unmapped this session) + this session,
        // never more than the payment amount.
        const serverMapped = isUnmapped ? 0 : num(r.mapped_amount);
        const amt = num(r.amount);
        const rawMapped = serverMapped + localAllocated;
        const totalMapped = amt > 0 ? Math.min(rawMapped, amt) : rawMapped;
        // session change wins; else unmapped override; else server mapping_status.
        const status =
          allocs.length > 0
            ? statusByMapped(totalMapped, r.amount)
            : isUnmapped
              ? MAP_STATUS.UNMAPPED
              : isSuspenseRow(r)
                ? MAP_STATUS.SUSPENSE
                : r.mapping_status
                  ? statusFromApi(r.mapping_status)
                  : statusByMapped(totalMapped, r.amount);
        return {
          ...r,
          _allocs: allocs,
          _mapStatus: status,
          _allocated: localAllocated,
          _mappedAmount: totalMapped,
          _unmappedAmount: Math.max(0, amt - totalMapped),
          _tds: tdsTotal(allocs),
          _clients: uniqueClientCount(allocs),
          _mail: mailStatusOf(r), // so the ack dialog can warn about resends
          _pendingApproval: pendingApprovals.get(r.trx_id) || null,
          // everything this row shows, including the mapped invoices behind the
          // expander, so the search box covers every column
          _search: searchHaystack([
            r.trx_id,
            ...dateTokens(r.payment_date),
            r.description,
            r.status,
            ...amountTokens(r.amount),
            r.trx_mode,
            r.trx_account,
            r.unique_id,
            r.reference_no,
            r.client_name,
            ...amountTokens(totalMapped),
            ...amountTokens(Math.max(0, amt - totalMapped)),
            ...amountTokens(tdsTotal(allocs)),
            status.label,
            r.mapping_type === 'manual_type' ? 'manual' : r.mapping_type,
            otherInfoOf(r).label,
            otherInfoOf(r).reason,
            bankTypeOf(r),
            pendingApprovals.has(r.trx_id) ? 'waiting approval auto mapping pending' : '',
            ...allocs.flatMap((a) => [a.reference_no, a.client_name, a.client_id, ...amountTokens(a.allocated)]),
          ]),
        };
      }),
    [list, mappings, unmappedRids, pendingApprovals]
  );

  // trx_id → description (from the payments API) to enrich the auto-mapping view
  const descByTrx = useMemo(() => {
    const m = {};
    list.forEach((r) => {
      if (r.trx_id != null && r.trx_id !== '') m[r.trx_id] = r.description ?? '';
    });
    return m;
  }, [list]);

  // trx_ids already in the payments table — lets the statement upload flag rows
  // that have been imported before. Case/space-insensitive to avoid false misses.
  const existingTrxIds = useMemo(() => {
    const s = new Set();
    list.forEach((r) => {
      if (r.trx_id != null && r.trx_id !== '') s.add(String(r.trx_id).trim().toUpperCase());
    });
    return s;
  }, [list]);

  // trx_id → the actual payment amount. The approvals API returns per-invoice
  // suggestion rows, so the payments API is the source of truth for the total.
  const amountByTrx = useMemo(() => {
    const m = {};
    list.forEach((r) => {
      if (r.trx_id != null && r.trx_id !== '') m[r.trx_id] = num(r.amount);
    });
    return m;
  }, [list]);

  const modeOptions = useMemo(
    () => Array.from(new Set(list.map((r) => r.trx_mode).filter(Boolean))).sort(),
    [list]
  );
  const typeOptions = useMemo(
    () => Array.from(new Set(list.map((r) => r.mapping_type).filter(Boolean))).sort(),
    [list]
  );

  const searchLower = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    return rows.filter((row) => {
      if (!inPaymentRange(row.payment_date, from, to)) return false;
      if (modeFilter !== 'all' && (row.trx_mode ?? '') !== modeFilter) return false;
      if (typeFilter !== 'all' && (row.mapping_type ?? '') !== typeFilter) return false;
      if (mapFilter !== 'all' && row._mapStatus.key !== mapFilter) return false;
      if (bankFilter !== 'all' && bankTypeOf(row) !== bankFilter) return false;
      return matchesQuery(row._search, searchLower);
    });
  }, [rows, modeFilter, typeFilter, mapFilter, bankFilter, searchLower, from, to]);

  const sorted = useMemo(() => {
    const cmp = (a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (orderBy === 'amount') {
        av = num(av);
        bv = num(bv);
        return order === 'asc' ? av - bv : bv - av;
      }
      if (orderBy === 'payment_date') {
        av = new Date(av || 0).getTime();
        bv = new Date(bv || 0).getTime();
        if (av !== bv) return order === 'asc' ? av - bv : bv - av;
        // same payment date — unmapped first, then partial, mapped, suspense, other
        return mapOrderRank(a) - mapOrderRank(b);
      }
      if (orderBy === '_mapStatus') {
        const ar = mapOrderRank(a);
        const br = mapOrderRank(b);
        if (ar !== br) return order === 'asc' ? ar - br : br - ar;
        // within a bucket, newest payment first
        const at = new Date(a.payment_date || 0).getTime();
        const bt = new Date(b.payment_date || 0).getTime();
        return bt - at;
      }
      const as = String(av ?? '').toLowerCase();
      const bs = String(bv ?? '').toLowerCase();
      const c = as.localeCompare(bs, undefined, { numeric: true });
      return order === 'asc' ? c : -c;
    };
    return [...filtered].sort(cmp);
  }, [filtered, orderBy, order]);

  const totalRows = sorted.length;
  const maxPage = totalRows <= 0 ? 0 : Math.max(0, Math.ceil(totalRows / rowsPerPage) - 1);
  const safePage = page > maxPage ? maxPage : page;
  const paginated = sorted.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (totalRows > 0 && page > maxPage) setPage(maxPage);
  }, [totalRows, rowsPerPage, page, maxPage]);

  // ── KPI metrics ──
  const totalAmount = rows.reduce((s, r) => s + num(r.amount), 0);
  const counts = useMemo(() => {
    const c = { UNMAPPED: 0, PARTIAL: 0, MAPPED: 0, SUSPENSE: 0, OTHER: 0, unmappedAmt: 0, groupTxns: 0 };
    rows.forEach((r) => {
      c[r._mapStatus.key] += 1;
      // 'other' credits are settled business; suspense money is still unapplied,
      // so it stays counted as awaiting mapping
      if (r._mapStatus.key !== 'MAPPED' && r._mapStatus.key !== 'OTHER') {
        c.unmappedAmt += num(r.amount) - r._mappedAmount;
      }
      if (r._clients > 1) c.groupTxns += 1;
    });
    return c;
  }, [rows]);

  const handleSort = (col) => () => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
    setPage(0);
  };

  const handleExport = () => {
    const out = sorted.map((r) => ({
      'Transaction ID': r.trx_id ?? '',
      'Payment Date': r.payment_date ? fmtDate(r.payment_date) : '',
      Description: r.description ?? '',
      'Payment Status': r.status ?? '',
      'Payment Amount': num(r.amount),
      'Payment Mode': r.trx_mode ?? '',
      'Mapped Amount': r._mappedAmount,
      'Unmapped Amount': r._unmappedAmount,
      'Mapped Type': r.mapping_type === 'manual_type' ? 'Manual' : r.mapping_type ?? '',
      'Mapping Status': r._mapStatus.label,
    }));
    exportRowsToExcel(out, `Payments_${from}_to_${to}.xlsx`);
  };

  // Every parked payment in the date range — deliberately ignoring the search and
  // status filters, so this is always the full chase-up list.
  // parked payments and payments booked as something other than an invoice
  // receipt: both are money sitting outside the invoice ledger
  const suspenseRows = useMemo(
    () => rows.filter((r) => r._mapStatus.key === 'SUSPENSE' || r._mapStatus.key === 'OTHER'),
    [rows]
  );

  // Exported in the suspense template's layout, so the file goes straight into
  // Tally as bank-to-suspense entries.
  const handleExportSuspense = async () => {
    const out = buildSuspenseRows(
      suspenseRows.map((r) => ({
        payment_date: r.payment_date,
        description: r.description ?? '',
        amount: num(r.amount),
        trx_id: r.trx_id,
        bank_type: r.bank_type,
        // drives the ledger choice in the sheet
        is_other: r._mapStatus.key === 'OTHER',
        parked_reason:
          [otherInfoOf(r).label, otherInfoOf(r).reason].filter(Boolean).join(' - '),
      }))
    );
    if (out.length === 0) return;
    try {
      await exportSuspenseSheet(out, `Suspense_Other_${from}_to_${to}.xlsx`);
    } catch (err) {
      console.error('Failed to build the suspense sheet:', err);
      setSnack({ open: true, severity: 'error', message: 'Could not build the suspense export file.' });
    }
  };

  const toggleAck = (rid) =>
    setAckSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });

  // fully-mapped rows currently visible on the page (for select-all)
  const mappedPageRids = paginated.filter((r) => r._mapStatus.key === 'MAPPED').map((r) => r._rid);
  const allMappedSelected = mappedPageRids.length > 0 && mappedPageRids.every((id) => ackSelected.has(id));
  const toggleAckAll = () =>
    setAckSelected((prev) => {
      const next = new Set(prev);
      if (allMappedSelected) mappedPageRids.forEach((id) => next.delete(id));
      else mappedPageRids.forEach((id) => next.add(id));
      return next;
    });

  const handleSendAck = async () => {
    const api = process.env.REACT_APP_SEND_PAYMENT_ACKNOWLEDGEMENT;
    if (!api) {
      setSnack({ open: true, severity: 'warning', message: 'Acknowledgement API not configured (REACT_APP_SEND_PAYMENT_ACKNOWLEDGEMENT).' });
      return;
    }
    const items = rows
      .filter((r) => ackSelected.has(r._rid) && r._mapStatus.key === 'MAPPED')
      .map((r) => ({ trx_id: r.trx_id, updated_by: email }));
    if (items.length === 0) return;
    setSendingAck(true);
    try {
      await axios.post(api, items);
      setSnack({
        open: true,
        severity: 'success',
        message: `Acknowledgement sent for ${items.length} transaction${items.length === 1 ? '' : 's'}.`,
      });
      setAckSelected(new Set());
    } catch (err) {
      console.error('Failed to send acknowledgement:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to send acknowledgement',
      });
    } finally {
      setSendingAck(false);
    }
  };

  const ackCount = ackSelected.size;

  // selected transactions that already had an acknowledgement mailed — the
  // confirm dialog calls these out so a resend is never accidental
  const ackAlreadySent = useMemo(
    () => rows.filter((r) => ackSelected.has(r._rid) && r._mapStatus.key === 'MAPPED' && r._mail?.key === 'SENT'),
    [rows, ackSelected]
  );

  // refresh the payments transactions + invoice dues
  // A background poll must never fight the person using the page: skip it while a
  // dialog is open, while something is saving, or while the tab is in the
  // background. The newest answer is read from a ref so the timer never restarts.
  const autoRefreshBlocked = useRef(false);
  autoRefreshBlocked.current =
    loading ||
    saving ||
    sendingAck ||
    uploadOpen ||
    ackConfirm ||
    !!dialogRid ||
    !!otherRid ||
    !!suspenseRid ||
    !!auditTrxId ||
    !!unmapConfirmRid;

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (autoRefreshBlocked.current) return;
      fetchPayments({ silent: true });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const refreshPayments = () => {
    fetchPayments();
    fetchInvoices();
    fetchHistory();
    fetchClientPhones();
    fetchPendingApprovals();
  };
  // refresh BOTH tabs' data after any mapping change
  const refreshAll = () => {
    refreshPayments();
    setRefreshSignal((n) => n + 1); // triggers the mapped tab to refetch
  };

  // Park an unidentified credit in suspense. The backend sets the status itself;
  // it only needs the transaction, who did it, and an optional note.
  const handleSaveSuspense = async () => {
    const api = process.env.REACT_APP_MARK_TRX_SUSPENSE;
    const txn = list.find((r) => r._rid === suspenseRid);
    if (!txn) return;

    const note = suspenseRemark.trim();
    const payload = {
      trx_id: txn.trx_id ?? suspenseRid,
      updated_by: email,
      ...(note ? { notes: note } : {}), // omitted when blank, so the column stays NULL
    };

    if (!api) {
      setSnack({
        open: true,
        severity: 'warning',
        message: 'API not configured (REACT_APP_MARK_TRX_SUSPENSE).',
      });
      return;
    }

    setSaving(true);
    try {
      await axios.post(api, payload);
      setSuspenseRid(null);
      setSuspenseRemark('');
      setSnack({ open: true, severity: 'success', message: 'Moved to suspense.' });
      refreshAll();
    } catch (err) {
      console.error('Failed to move the payment to suspense:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to move to suspense',
      });
    } finally {
      setSaving(false);
    }
  };

  // Mark a credit that is not an invoice payment — a transfer, a bank credit or
  // other income. Handled by its own endpoint, so no invoice mapping is written.
  const handleSaveOther = async () => {
    const api = process.env.REACT_APP_MAP_OTHER_PAYMENT_TO_INVOICE;
    const txn = list.find((r) => r._rid === otherRid);
    const cat = OTHER_MAPPING_CATEGORIES.find((c) => c.id === otherCategory);
    if (!txn || !cat) return;

    const reason = otherRemark.trim();
    const payload = {
      trx_id: txn.trx_id ?? otherRid,
      other_status: cat.id,
      // omitted entirely when blank, so the column stays NULL
      ...(reason ? { other_reason: reason } : {}),
      updated_by: email,
    };

    if (!api) {
      setSnack({
        open: true,
        severity: 'warning',
        message: 'API not configured (REACT_APP_MAP_OTHER_PAYMENT_TO_INVOICE).',
      });
      return;
    }

    setSaving(true);
    try {
      await axios.post(api, payload);
      setOtherRid(null);
      setOtherCategory('');
      setOtherRemark('');
      setSnack({ open: true, severity: 'success', message: `Marked as ${cat.label}.` });
      refreshAll();
    } catch (err) {
      console.error('Failed to mark the payment as other:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to save',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMapping = async (rid, allocations) => {
    const api = process.env.REACT_APP_SAVE_TRX_INVOICES;
    const txn = list.find((r) => r._rid === rid);
    const trxId = txn?.trx_id ?? rid;

    // one record per mapped invoice line, matching collection.collection_trx_mapping
    const payload = (allocations || []).map((a) => ({
      trx_id: trxId,
      client_fk: a.client_fk ?? a.client_id,
      invoice_num: a.reference_no,
      amt: num(a.allocated),
      tds_per: num(a.tds_pct),
      tds_amt: num(a.tds_amount),
      mapping_type: 'manual_type',
      created_by: email,
    }));

    const clearUnmapped = () =>
      setUnmappedRids((prev) => {
        const n = new Set(prev);
        n.delete(rid);
        return n;
      });

    if (!api) {
      // no endpoint configured — keep the optimistic local behaviour
      setMappings((prev) => {
        const next = { ...prev };
        if (!allocations || allocations.length === 0) delete next[rid];
        else next[rid] = allocations;
        return next;
      });
      clearUnmapped();
      setDialogRid(null);
      setSnack({ open: true, severity: 'warning', message: 'Save API not configured — mapping kept locally only.' });
      return;
    }

    setSaving(true);
    try {
      await axios.post(api, payload);
      // clear local optimistic state — the refetch below makes the server authoritative,
      // so mapped_amount / mapping_status aren't double-counted.
      setMappings((prev) => {
        const next = { ...prev };
        delete next[rid];
        return next;
      });
      clearUnmapped();
      setDialogRid(null);
      setSnack({
        open: true,
        severity: 'success',
        message: `Mapping saved (${payload.length} invoice${payload.length === 1 ? '' : 's'}).`,
      });
      // refresh both tabs so mapping_status / mapped_amount / dues reflect the change
      refreshAll();
    } catch (err) {
      console.error('Failed to save mapping:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to save mapping',
      });
    } finally {
      setSaving(false);
    }
  };

  // Unmap a fully-mapped transaction: remove its saved mapping records.
  const handleUnmap = async (rid) => {
    const api = process.env.REACT_APP_UNMAP_TRX_INVOICES;
    const txn = list.find((r) => r._rid === rid);
    const trxId = txn?.trx_id ?? rid;

    const applyLocal = () => {
      setMappings((prev) => {
        const next = { ...prev };
        delete next[rid];
        return next;
      });
      setUnmappedRids((prev) => new Set(prev).add(rid));
    };

    if (!api) {
      applyLocal();
      setSnack({ open: true, severity: 'warning', message: 'Unmap API not configured — cleared locally only.' });
      return;
    }

    setSaving(true);
    try {
      await axios.post(api, { trx_id: trxId, updated_by: email });
      applyLocal();
      setSnack({ open: true, severity: 'success', message: 'Mapping removed.' });
      // refresh both tabs so mapping_status / mapped_amount / dues reflect the change
      refreshAll();
    } catch (err) {
      console.error('Failed to unmap:', err);
      setSnack({
        open: true,
        severity: 'error',
        message: err?.response?.data?.detail || err?.message || 'Failed to unmap',
      });
    } finally {
      setSaving(false);
    }
  };

  const dialogRow = rows.find((r) => r._rid === dialogRid) || null;

  return (
    <Box sx={{ p: 2 }}>
      {/* title */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWalletIcon sx={{ color: '#0059B2', fontSize: 24 }} />
          <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0059B2' }}>Payments</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: 0.25,
            backgroundColor: '#f1f5f9',
            padding: '3px',
            borderRadius: '6px',
            width: 'fit-content',
          }}
        >
          {[
            { id: 'payments', label: 'Payments' },
            { id: 'mapped', label: 'Payments Mapped' },
            { id: 'auto', label: 'Auto Mapping - Approvals' },
            { id: 'acks', label: 'Payment Acknowledgements' },
            { id: 'dashboard', label: 'Collection Dashboard' },
          ].map((t) => (
            <Box
              key={t.id}
              onClick={() => setTab(t.id)}
              sx={{
                padding: '4px 14px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '4px',
                transition: 'all 0.15s',
                color: tab === t.id ? '#fff' : '#64748b',
                backgroundColor: tab === t.id ? '#1e40af' : 'transparent',
                '&:hover': tab !== t.id ? { color: '#1e40af', backgroundColor: '#e0e7ff' } : {},
              }}
            >
              {t.label}
            </Box>
          ))}
        </Box>
        {lastRefreshed && (
          <Typography
            sx={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap', ml: 'auto' }}
            title={`Payments refresh on their own every ${AUTO_REFRESH_MS / 60000} minutes`}
          >
            updated {fmtTimeOnly(lastRefreshed)}
          </Typography>
        )}
      </Box>

      {/* shared date range + quick presets (applies to all tabs) */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        {DATE_PRESETS.map((p) => (
          <Chip
            key={p.id}
            label={p.label}
            size="small"
            onClick={() => {
              const r = computePreset(p.id);
              if (r) {
                setFrom(r.from);
                setTo(r.to);
              }
            }}
            sx={{
              fontSize: '11px',
              height: 26,
              cursor: 'pointer',
              backgroundColor: from === computePreset(p.id)?.from && to === computePreset(p.id)?.to ? '#1e40af' : '#eef2ff',
              color: from === computePreset(p.id)?.from && to === computePreset(p.id)?.to ? '#fff' : '#1e40af',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#c7d2fe' },
            }}
          />
        ))}
      </Box>

      {tab === 'mapped' ? (
        <MappedView from={from} to={to} email={email} can={can} refreshSignal={refreshSignal} onChanged={refreshPayments} />
      ) : tab === 'acks' ? (
        <AcknowledgementsView from={from} to={to} refreshSignal={refreshSignal} />
      ) : tab === 'dashboard' ? (
        <CollectionDashboard from={from} to={to} refreshSignal={refreshSignal} />
      ) : tab === 'auto' ? (
        <AutoApprovalsView
          from={from}
          to={to}
          email={email}
          can={can}
          descByTrx={descByTrx}
          amountByTrx={amountByTrx}
          refreshSignal={refreshSignal}
          onChanged={refreshAll}
        />
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
          <CircularProgress size={32} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 4, textAlign: 'center', color: '#dc2626', fontSize: '14px' }}>{error}</Box>
      ) : (
        <>
          {/* KPI strip */}
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <StatCard label="Transactions" value={rows.length} />
            <StatCard
              label="Total Amount"
              value={formatINR(totalAmount)}
              palette={{ bg: '#eef2ff', border: '#3b82f6', color: '#1e40af', Icon: PaymentsIcon }}
            />
            <StatCard
              label="Unmapped"
              value={counts.UNMAPPED}
              palette={{ bg: '#fef2f2', border: '#dc2626', color: '#dc2626', Icon: ErrorOutlineIcon }}
            />
            <StatCard
              label="Partially Mapped"
              value={counts.PARTIAL}
              palette={{ bg: '#fef3c7', border: '#d97706', color: '#d97706', Icon: DonutLargeIcon }}
            />
            <StatCard
              label="Fully Mapped"
              value={counts.MAPPED}
              palette={{ bg: '#ecfdf5', border: '#16a34a', color: '#16a34a', Icon: CheckCircleIcon }}
            />
            {counts.SUSPENSE > 0 && (
              <StatCard
                label="Suspense"
                value={counts.SUSPENSE}
                hint="Payments parked because the payer could not be identified"
                palette={{ bg: '#fff7ed', border: '#c2410c', color: '#c2410c', Icon: HelpOutlineIcon }}
              />
            )}
            {counts.OTHER > 0 && (
              <StatCard
                label="Other"
                value={counts.OTHER}
                hint="Booked as something other than an invoice receipt"
                palette={{ bg: '#f5f3ff', border: '#7c3aed', color: '#7c3aed', Icon: AccountBalanceWalletIcon }}
              />
            )}
            <StatCard
              label="Unmapped Amount"
              value={formatINR(counts.unmappedAmt)}
              palette={{ bg: '#fff7ed', border: '#ea580c', color: '#ea580c', Icon: AccountBalanceWalletIcon }}
            />
          </Box>

          {/* filter bar */}
          <Box
            sx={{
              mb: 1.5,
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {can('Upload Statement') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AccountBalanceIcon sx={{ fontSize: 16 }} />}
              onClick={() => setUploadOpen(true)}
              sx={{ textTransform: 'none', fontSize: '12px', color: '#1e40af', borderColor: '#93c5fd' }}
            >
              Upload Statement
            </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              disabled={sorted.length === 0}
              onClick={handleExport}
              sx={{ textTransform: 'none', fontSize: '12px', color: '#1e40af', borderColor: '#93c5fd' }}
            >
              Export
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
              disabled={suspenseRows.length === 0}
              onClick={handleExportSuspense}
              title={
                suspenseRows.length === 0
                  ? 'No payments are parked in suspense for this date range'
                  : 'Download every payment parked in suspense for this date range, with the reason and how old it is'
              }
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                color: '#c2410c',
                borderColor: '#fed7aa',
                '&:hover': { borderColor: '#c2410c', backgroundColor: '#fff7ed' },
              }}
            >
              Export Suspense / Other ({suspenseRows.length})
            </Button>
            {can('Send Acknowledgement') && (
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
              disabled={ackCount === 0 || sendingAck}
              onClick={() => setAckConfirm(true)}
              sx={{ textTransform: 'none', fontSize: '12px', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
            >
              {sendingAck ? 'Sending…' : `Send Acknowledgement (${ackCount})`}
            </Button>
            )}
            <TextField
              size="small"
              placeholder="Search any column..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{ flex: '1 1 150px', minWidth: 130, maxWidth: 220, ml: 'auto' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {/* The closed control shows a short label so four filters fit on one
                row; the open list keeps the wording that explains each choice. */}
            <FormControl size="small" sx={{ minWidth: 118 }}>
              <Select
                value={mapFilter}
                onChange={(e) => { setMapFilter(e.target.value); setPage(0); }}
                sx={{ fontSize: '12px' }}
                title="Mapping status"
                renderValue={(v) => MAP_FILTER_SHORT[v] ?? v}
              >
                <MenuItem value="all">All Mapping Status</MenuItem>
                <MenuItem value="UNMAPPED">Unmapped</MenuItem>
                <MenuItem value="PARTIAL">Partially Mapped</MenuItem>
                <MenuItem value="MAPPED">Fully Mapped</MenuItem>
                <MenuItem value="SUSPENSE">Suspense (parked)</MenuItem>
                <MenuItem value="OTHER">Other (not an invoice payment)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 96 }}>
              <Select
                value={bankFilter}
                onChange={(e) => { setBankFilter(e.target.value); setPage(0); }}
                sx={{ fontSize: '12px' }}
                title="Bank"
                renderValue={(v) => (v === 'all' ? 'All Banks' : v)}
              >
                <MenuItem value="all">All Banks</MenuItem>
                {BANK_TYPE_OPTIONS.map((b) => (
                  <MenuItem key={b} value={b}>{b}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {modeOptions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 104 }}>
                <Select
                  value={modeFilter}
                  onChange={(e) => { setModeFilter(e.target.value); setPage(0); }}
                  sx={{ fontSize: '12px' }}
                  title="Payment mode"
                  renderValue={(v) => (v === 'all' ? 'All Modes' : v)}
                >
                  <MenuItem value="all">All Payment Modes</MenuItem>
                  {modeOptions.map((m) => (
                    <MenuItem key={m} value={m}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {typeOptions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 104 }}>
                <Select
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
                  sx={{ fontSize: '12px' }}
                  title="Mapped type"
                  renderValue={(v) => (v === 'all' ? 'All Types' : v === 'manual_type' ? 'Manual' : v)}
                >
                  <MenuItem value="all">All Mapped Types</MenuItem>
                  {typeOptions.map((t) => (
                    <MenuItem key={t} value={t}>{t === 'manual_type' ? 'Manual' : t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <IconButton
              size="small"
              onClick={() => { fetchPayments(); fetchInvoices(); fetchHistory(); fetchClientPhones(); fetchPendingApprovals(); }}
              disabled={loading}
              aria-label="Refresh"
              sx={{
                color: '#1e40af',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                '&:hover': { borderColor: '#1e40af', backgroundColor: '#e0e7ff' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* table */}
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              borderRadius: '8px',
              maxHeight: 'calc(100vh - 360px)',
              overflow: 'auto',
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...HEADER_SX, width: COL.check, padding: '6px 0 6px 2px' }} title="Select fully-mapped transactions to acknowledge">
                    <Checkbox
                      size="small"
                      checked={allMappedSelected}
                      indeterminate={!allMappedSelected && mappedPageRids.some((id) => ackSelected.has(id))}
                      onChange={toggleAckAll}
                      disabled={mappedPageRids.length === 0}
                      sx={{ p: 0.5, color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, width: COL.expand, padding: '6px 0' }} />
                  <TableCell sx={{ ...HEADER_SX, width: COL.trxId, padding: '6px 8px 6px 4px', whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={orderBy === 'trx_id'}
                      direction={orderBy === 'trx_id' ? order : undefined}
                      onClick={handleSort('trx_id')}
                      sx={{ color: '#fff' }}
                    >
                      Transaction ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center', width: COL.date, padding: CELL_PAD, whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={orderBy === 'payment_date'}
                      direction={orderBy === 'payment_date' ? order : undefined}
                      onClick={handleSort('payment_date')}
                      sx={{ color: '#fff' }}
                    >
                      Payment Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, minWidth: COL.desc, width: 'auto', padding: CELL_PAD, ...HIDE_SM }}>Payment Description</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center', width: COL.status, padding: '6px 4px', whiteSpace: 'nowrap', ...HIDE_SM }}>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : undefined}
                      onClick={handleSort('status')}
                      sx={{ color: '#fff' }}
                    >
                      Payment Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'right', width: COL.amount, padding: '6px 8px 6px 4px', whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={orderBy === 'amount'}
                      direction={orderBy === 'amount' ? order : undefined}
                      onClick={handleSort('amount')}
                      sx={{ color: '#fff' }}
                    >
                      Payment Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center', width: COL.mode, padding: '6px 4px', ...HIDE_SM }}>
                    <TableSortLabel
                      active={orderBy === 'trx_mode'}
                      direction={orderBy === 'trx_mode' ? order : undefined}
                      onClick={handleSort('trx_mode')}
                      sx={{ color: '#fff' }}
                    >
                      Payment Mode
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'right', width: COL.mappedAmount, padding: '6px 8px 6px 4px' }}>Mapped Amount</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center', width: COL.mappedType, padding: '6px 4px', ...HIDE_SM }}>Mapped Type</TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center', width: COL.mapStatus, padding: '6px 4px' }}>
                    <TableSortLabel
                      active={orderBy === '_mapStatus'}
                      direction={orderBy === '_mapStatus' ? order : undefined}
                      onClick={handleSort('_mapStatus')}
                      sx={{ color: '#fff' }}
                    >
                      Mapping Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...HEADER_SX, textAlign: 'center', width: COL.action, padding: '6px 4px' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length > 0 ? (
                  paginated.map((row, idx) => {
                    const statusStr = (row.status ?? '').toString();
                    const isActive = ['active', 'success', 'completed', 'paid', 'settled'].includes(
                      statusStr.toLowerCase()
                    );
                    const isOpen = expanded.has(row._rid);
                    const hasMappings = row._allocs.length > 0;
                    const canAck = row._mapStatus.key === 'MAPPED';
                    const other = otherInfoOf(row);
                    return (
                      <React.Fragment key={row._rid}>
                        <TableRow
                          sx={{
                            '&:hover': { backgroundColor: '#e8f4fd' },
                            backgroundColor: isOpen ? '#e0e7ff' : idx % 2 === 0 ? '#ffffff' : '#f8faff',
                          }}
                        >
                          <TableCell sx={{ padding: '0 0 0 2px', width: COL.check }}>
                            <Checkbox
                              size="small"
                              checked={ackSelected.has(row._rid)}
                              disabled={!canAck}
                              onChange={() => toggleAck(row._rid)}
                              sx={{ p: 0.5 }}
                            />
                          </TableCell>
                          <TableCell sx={{ padding: 0, width: COL.expand }}>
                            {hasMappings ? (
                              <IconButton
                                size="small"
                                sx={{ padding: '2px' }}
                                onClick={() => toggleExpand(row._rid)}
                                aria-label={isOpen ? 'collapse' : 'expand'}
                              >
                                {isOpen ? (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowRightIcon fontSize="small" />
                                )}
                              </IconButton>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap', width: COL.trxId, padding: '6px 8px 6px 4px' }}>
                            {row.trx_id == null ? (
                              '—'
                            ) : canAck ? (
                              // fully-mapped rows expose their audit trail
                              <Box
                                component="span"
                                role="button"
                                tabIndex={0}
                                title="View transaction mapping logs"
                                onClick={() => setAuditTrxId(row.trx_id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setAuditTrxId(row.trx_id);
                                  }
                                }}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.4,
                                  cursor: 'pointer',
                                  color: '#0059B2',
                                  borderBottom: '1px dashed #93c5fd',
                                  '&:hover': { color: '#1e40af', borderBottomColor: '#1e40af' },
                                }}
                              >
                                {row.trx_id}
                                <HistoryIcon sx={{ fontSize: 13 }} />
                              </Box>
                            ) : (
                              row.trx_id
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', textAlign: 'center', whiteSpace: 'nowrap', width: COL.date, padding: CELL_PAD }}>
                            {fmtDateTime(row.payment_date)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: '11px',
                              color: '#475569',
                              minWidth: COL.desc,
                              width: 'auto',
                              padding: CELL_PAD,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              fontFamily: 'monospace',
                              ...HIDE_SM,
                            }}
                            title={row.description ?? ''}
                          >
                            {row.description ?? '—'}
                            {row.client_name && (
                              <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontFamily: 'inherit' }}>
                                {row.client_name}
                                {row.unique_id ? ` · ${row.unique_id}` : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', textAlign: 'center', width: COL.status, padding: '6px 4px', ...HIDE_SM }}>
                            {statusStr ? (
                              <Chip
                                size="small"
                                label={statusStr}
                                sx={{
                                  backgroundColor: isActive ? '#ecfdf5' : '#fef2f2',
                                  color: isActive ? '#16a34a' : '#dc2626',
                                  fontSize: '11px',
                                  height: 22,
                                }}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', textAlign: 'right', fontWeight: 600, color: '#16a34a', width: COL.amount, padding: '6px 8px 6px 4px', whiteSpace: 'nowrap' }}>
                            {formatINR(row.amount)}
                            {hasMappings && row._mapStatus.key === 'PARTIAL' && (
                              <Typography sx={{ fontSize: '9px', color: '#d97706' }}>
                                {formatINR(row._allocated)} mapped
                              </Typography>
                            )}
                            {row._tds > 0 && (
                              <Typography sx={{ fontSize: '9px', color: '#7c3aed' }}>
                                +{formatINR(row._tds)} TDS
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', textAlign: 'center', width: COL.mode, padding: '6px 4px', ...HIDE_SM }}>
                            {row.trx_mode ? (
                              <Chip
                                size="small"
                                label={row.trx_mode}
                                sx={{
                                  backgroundColor: '#eef2ff',
                                  color: '#4338ca',
                                  fontSize: '11px',
                                  height: 22,
                                  fontWeight: 600,
                                }}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px', textAlign: 'right', fontWeight: 600, color: row._mappedAmount > 0 ? '#0059B2' : '#94a3b8', width: COL.mappedAmount, padding: '6px 8px 6px 4px', whiteSpace: 'nowrap' }}>
                            {row._mappedAmount > 0 ? formatINR(row._mappedAmount) : '—'}
                            {row._unmappedAmount > 0 && (
                              <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#dc2626', whiteSpace: 'nowrap' }}>
                                {formatINR(row._unmappedAmount)} unmapped
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', width: COL.mappedType, padding: '6px 4px', ...HIDE_SM }}>
                            {row.mapping_type ? (
                              <Chip
                                size="small"
                                label={
                                  row.mapping_type === 'manual_type'
                                    ? 'Manual'
                                    : row.mapping_type === 'auto'
                                      ? 'Auto'
                                      : row.mapping_type === 'other'
                                        ? 'Other'
                                        : row.mapping_type
                                }
                                sx={
                                  row.mapping_type === 'other'
                                    ? { backgroundColor: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe', fontSize: '10px', height: 20, fontWeight: 600 }
                                    : { backgroundColor: '#f1f5f9', color: '#475569', fontSize: '10px', height: 20, fontWeight: 600 }
                                }
                              />
                            ) : (row.is_recommend ?? '').toString().toLowerCase() === 'not_recommend' ? (
                              <Typography sx={{ fontSize: '9px', fontWeight: 600, color: '#b45309', fontStyle: 'italic' }}>
                                Suggested for manual mapping
                              </Typography>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', width: COL.mapStatus, padding: '6px 4px' }}>
                            <Box
                              sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}
                              title={other.isSuspense && other.reason ? `Parked: ${other.reason}` : undefined}
                            >
                              <MapStatusChip status={row._mapStatus} />
                              {row._pendingApproval && (
                                <Chip
                                  size="small"
                                  clickable
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setTab('auto');
                                  }}
                                  icon={<HourglassEmptyIcon style={{ fontSize: 12, color: '#0e7490' }} />}
                                  label="In Approvals"
                                  title={
                                    `Waiting in Auto Mapping - Approvals: ${row._pendingApproval.lines} suggested invoice line` +
                                    `${row._pendingApproval.lines === 1 ? '' : 's'}` +
                                    `${row._pendingApproval.clients > 1 ? ` across ${row._pendingApproval.clients} possible clients` : ''}` +
                                    '. Click to open that tab and approve or reject it.'
                                  }
                                  sx={{
                                    backgroundColor: '#ecfeff',
                                    color: '#0e7490',
                                    fontSize: '9.5px',
                                    height: 19,
                                    fontWeight: 700,
                                    border: '1px solid #a5f3fc',
                                    '& .MuiChip-icon': { marginLeft: '4px' },
                                    '&:hover': { backgroundColor: '#cffafe' },
                                  }}
                                />
                              )}
                              {row._clients > 1 && (
                                <Chip
                                  size="small"
                                  icon={<GroupsIcon style={{ fontSize: 13, color: '#4338ca' }} />}
                                  label={`${row._clients} clients`}
                                  sx={{
                                    backgroundColor: '#eef2ff',
                                    color: '#4338ca',
                                    fontSize: '10px',
                                    height: 20,
                                    border: '1px solid #c7d2fe',
                                    '& .MuiChip-icon': { marginLeft: '4px' },
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', width: COL.action, padding: '6px 4px' }}>
                            {other.isOther ? (
                              // parked or booked elsewhere, but still re-mappable to an invoice
                              <Box>
                                {can('Map') && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddLinkIcon sx={{ fontSize: 15 }} />}
                                    onClick={() => {
                                      fetchInvoices();
                                      fetchHistory();
                                      fetchClientPhones();
                                      setDialogRid(row._rid);
                                    }}
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '11px',
                                      py: 0.25,
                                      minWidth: 0,
                                      backgroundColor: '#1e40af',
                                      '&:hover': { backgroundColor: '#1e3a8a' },
                                    }}
                                  >
                                    Map
                                  </Button>
                                )}
                                {/* what it was booked as, so the change is a deliberate one */}
                                {!other.isSuspense && other.label && (
                                  <Box title={other.reason ? `Reason: ${other.reason}` : undefined} sx={{ mt: 0.4 }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#7c3aed', lineHeight: 1.2 }}>
                                      {other.label}
                                    </Typography>
                                    {other.reason && (
                                      <Typography
                                        sx={{
                                          fontSize: '9px',
                                          color: '#94a3b8',
                                          fontStyle: 'italic',
                                          display: '-webkit-box',
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical',
                                          overflow: 'hidden',
                                        }}
                                      >
                                        {other.reason}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            ) : row._mapStatus.key === 'MAPPED' ? (
                              !can('Unmap') ? null : (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={saving}
                                startIcon={<LinkOffIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setUnmapConfirmRid(row._rid)}
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '11px',
                                  py: 0.25,
                                  color: '#dc2626',
                                  borderColor: '#fecaca',
                                  '&:hover': { borderColor: '#dc2626', backgroundColor: '#fef2f2' },
                                }}
                              >
                                Unmap
                              </Button>
                              )
                            ) : (
                              <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {can('Map') && (
                                <Button
                                  size="small"
                                  variant={hasMappings ? 'outlined' : 'contained'}
                                  startIcon={<AddLinkIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => {
                                    fetchInvoices();
                                    fetchHistory();
                                    fetchClientPhones();
                                    setDialogRid(row._rid);
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '11px',
                                    py: 0.25,
                                    minWidth: 0,
                                    ...(hasMappings
                                      ? { color: '#1e40af', borderColor: '#93c5fd' }
                                      : { backgroundColor: '#1e40af', '&:hover': { backgroundColor: '#1e3a8a' } }),
                                  }}
                                >
                                  {hasMappings ? 'Edit' : 'Map'}
                                </Button>
                                )}
                                {/* not an invoice payment — transfer, interest, other income */}
                                {can('Map') && (
                                  <IconButton
                                    size="small"
                                    disabled={saving}
                                    onClick={() => {
                                      setOtherCategory('');
                                      setOtherRemark('');
                                      setOtherRid(row._rid);
                                    }}
                                    title="Other — not an invoice payment (transfer, interest, other income)"
                                    sx={{
                                      p: 0.4,
                                      color: '#7c3aed',
                                      border: '1px solid #ddd6fe',
                                      borderRadius: '6px',
                                      '&:hover': { borderColor: '#7c3aed', backgroundColor: '#f5f3ff' },
                                    }}
                                  >
                                    <AccountBalanceWalletIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                )}
                                {/* nobody knows whose money this is yet — park it */}
                                {can('Map') && (
                                  <IconButton
                                    size="small"
                                    disabled={saving}
                                    onClick={() => {
                                      setSuspenseRemark('');
                                      setSuspenseRid(row._rid);
                                    }}
                                    title="Suspense — park this payment until it can be identified"
                                    sx={{
                                      p: 0.4,
                                      color: '#c2410c',
                                      border: '1px solid #fed7aa',
                                      borderRadius: '6px',
                                      '&:hover': { borderColor: '#c2410c', backgroundColor: '#fff7ed' },
                                    }}
                                  >
                                    <HelpOutlineIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* expanded mapped invoices */}
                        <TableRow>
                          <TableCell sx={{ p: 0, borderBottom: isOpen ? '1px solid #e2e8f0' : 'none' }} colSpan={12}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: '#f8faff' }}>
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#475569', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ReceiptLongIcon sx={{ fontSize: 15 }} />
                                  Mapped Invoices ({row._allocs.length})
                                  {row._clients > 1 && (
                                    <Chip
                                      size="small"
                                      icon={<GroupsIcon style={{ fontSize: 13, color: '#4338ca' }} />}
                                      label={`across ${row._clients} clients`}
                                      sx={{ ml: 0.5, backgroundColor: '#eef2ff', color: '#4338ca', fontSize: '10px', height: 20, '& .MuiChip-icon': { marginLeft: '4px' } }}
                                    />
                                  )}
                                </Typography>
                                <Table size="small" sx={{ backgroundColor: '#fff', borderRadius: '6px', overflow: 'hidden' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ ...HEADER_SX, fontSize: '11px' }}>Reference No</TableCell>
                                      <TableCell sx={{ ...HEADER_SX, fontSize: '11px' }}>Client</TableCell>
                                      <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Outstanding</TableCell>
                                      <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Payment</TableCell>
                                      <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>TDS</TableCell>
                                      <TableCell sx={{ ...HEADER_SX, fontSize: '11px', textAlign: 'right' }}>Settled</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {groupAllocsByClient(row._allocs).map((grp) => (
                                      <React.Fragment key={grp.key}>
                                        {grp.showHeader && (
                                          <TableRow sx={{ backgroundColor: '#eef2ff' }}>
                                            <TableCell colSpan={6} sx={{ py: 0.4 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <GroupsIcon sx={{ fontSize: 13, color: '#4338ca' }} />
                                                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#3730a3' }}>
                                                  {grp.client}
                                                </Typography>
                                                <Typography sx={{ fontSize: '10px', color: '#6366f1' }}>
                                                  · {formatINR(grp.payment)} payment
                                                  {grp.tds > 0 ? ` + ${formatINR(grp.tds)} TDS` : ''}
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        )}
                                        {grp.items.map((a, i) => (
                                          <TableRow key={`${a.reference_no}-${i}`} sx={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8faff' }}>
                                            <TableCell sx={{ fontSize: '11px', fontFamily: 'monospace' }}>{a.reference_no}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>
                                              {a.client_name ?? '—'}
                                              <Typography sx={{ fontSize: '9px', color: '#94a3b8' }}>{a.client_id ?? ''}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '11px', textAlign: 'right', color: '#0059B2', fontWeight: 600 }}>
                                              {formatINR(a.due)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '11px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>
                                              {formatINR(a.allocated)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '11px', textAlign: 'right', color: '#7c3aed', fontWeight: 600 }}>
                                              {num(a.tds_amount) > 0 ? `${formatINR(a.tds_amount)} (${num(a.tds_pct)}%)` : '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '11px', textAlign: 'right', color: '#0059B2', fontWeight: 600 }}>
                                              {formatINR(num(a.allocated) + num(a.tds_amount))}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={12} sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                      <Typography variant="body2">
                        {list.length === 0 ? 'No payments found.' : 'No payments match the filters.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 15, 25, 50]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={safePage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ borderTop: '1px solid #e2e8f0' }}
          />
        </>
      )}

      <MappingDialog
        open={!!dialogRow}
        transaction={dialogRow}
        invoices={invoices}
        invoicesLoading={invoicesLoading}
        paymentHistory={paymentHistory}
        historyInfo={historyInfo}
        clientPhones={clientPhones}
        initialAllocations={dialogRow?._allocs || []}
        lockedMapped={dialogRow && dialogRow._allocs.length === 0 ? num(dialogRow.mapped_amount) : 0}
        saving={saving}
        onClose={() => setDialogRid(null)}
        onSave={(allocations) => handleSaveMapping(dialogRid, allocations)}
      />

      <BankStatementUpload
        open={uploadOpen}
        email={email}
        existingTrxIds={existingTrxIds}
        existingRange={{ from, to }}
        onClose={() => setUploadOpen(false)}
        onUploaded={(n) => {
          setSnack({ open: true, severity: 'success', message: `${n} transaction${n === 1 ? '' : 's'} uploaded from the statement.` });
          fetchPayments();
        }}
      />

      <AuditLogDialog
        open={!!auditTrxId}
        trxId={auditTrxId}
        transaction={list.find((r) => r.trx_id === auditTrxId) || null}
        onClose={() => setAuditTrxId(null)}
      />

      {/* park an unidentified credit */}
      {(() => {
        const txn = list.find((r) => r._rid === suspenseRid) || null;
        const close = () => {
          setSuspenseRid(null);
          setSuspenseRemark('');
        };
        return (
          <Dialog open={!!suspenseRid} onClose={() => !saving && close()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '10px' } }}>
            <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#c2410c', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpOutlineIcon fontSize="small" />
              Move to suspense?
            </DialogTitle>
            <DialogContent>
              {txn && (
                <Box sx={{ backgroundColor: '#f8faff', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1, mb: 1.5 }}>
                  <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>
                    {txn.trx_id} · {formatINR(txn.amount)}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#64748b' }}>
                    {txn.description || 'No description'} · {fmtDate(txn.payment_date)}
                  </Typography>
                </Box>
              )}
              <Typography sx={{ fontSize: '13px', color: '#475569' }}>
                The payment will be parked in suspense so it stops sitting in the unmapped queue. It stays visible
                here and can be mapped to invoices once you know whose money it is.
              </Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Why is it parked? (optional)"
                value={suspenseRemark}
                onChange={(e) => setSuspenseRemark(e.target.value)}
                sx={{ mt: 1.5 }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
              <Button onClick={close} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={saving}
                startIcon={<HelpOutlineIcon />}
                onClick={handleSaveSuspense}
                sx={{ textTransform: 'none', backgroundColor: '#c2410c', '&:hover': { backgroundColor: '#9a3412' } }}
              >
                {saving ? 'Saving…' : 'Move to suspense'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* classify a credit that is not an invoice payment */}
      {(() => {
        const otherTxn = list.find((r) => r._rid === otherRid) || null;
        const cat = OTHER_MAPPING_CATEGORIES.find((c) => c.id === otherCategory);
        const remarkMissing = !!cat?.remarkRequired && otherRemark.trim() === '';
        const closeOther = () => {
          setOtherRid(null);
          setOtherCategory('');
          setOtherRemark('');
        };
        return (
          <Dialog
            open={!!otherRid}
            onClose={() => !saving && closeOther()}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '10px' } }}
          >
            <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceWalletIcon fontSize="small" />
              Not an invoice payment
            </DialogTitle>
            <DialogContent>
              {otherTxn && (
                <Box sx={{ backgroundColor: '#f8faff', border: '1px solid #e2e8f0', borderRadius: '8px', p: 1, mb: 1.5 }}>
                  <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, color: '#1e293b' }}>
                    {otherTxn.trx_id} · {formatINR(otherTxn.amount)}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', color: '#64748b' }}>
                    {otherTxn.description || 'No description'} · {fmtDate(otherTxn.payment_date)}
                  </Typography>
                </Box>
              )}

              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569', mb: 0.75 }}>
                What is this credit?
              </Typography>
              {OTHER_MAPPING_CATEGORIES.map((c) => (
                <Box
                  key={c.id}
                  onClick={() => setOtherCategory(c.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    p: 0.75,
                    mb: 0.5,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: `1px solid ${otherCategory === c.id ? '#7c3aed' : '#e2e8f0'}`,
                    backgroundColor: otherCategory === c.id ? '#f5f3ff' : '#fff',
                    '&:hover': { borderColor: '#a78bfa' },
                  }}
                >
                  <Radio
                    size="small"
                    checked={otherCategory === c.id}
                    sx={{ p: 0.25, color: '#c4b5fd', '&.Mui-checked': { color: '#7c3aed' } }}
                  />
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1e293b' }}>{c.label}</Typography>
                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>{c.hint}</Typography>
                  </Box>
                </Box>
              ))}

              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label={cat?.remarkRequired ? 'Remark (required)' : 'Remark (optional)'}
                value={otherRemark}
                onChange={(e) => setOtherRemark(e.target.value)}
                error={remarkMissing}
                helperText={remarkMissing ? 'Please say what this credit was for.' : ' '}
                sx={{ mt: 1 }}
              />

              {otherTxn && (
                <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                  The whole transaction ({formatINR(otherTxn.amount)}) is marked under this head. No invoice is
                  mapped and no due is settled.
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.5 }}>
              <Button onClick={closeOther} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={saving || !cat || remarkMissing}
                onClick={handleSaveOther}
                sx={{ textTransform: 'none', backgroundColor: '#7c3aed', '&:hover': { backgroundColor: '#6d28d9' } }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <Dialog
        open={ackConfirm}
        onClose={() => setAckConfirm(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon fontSize="small" />
          Send acknowledgement for {ackCount} transaction{ackCount === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>
            An acknowledgement will be sent for the selected fully-mapped transactions.
          </Typography>
          {ackAlreadySent.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, fontSize: '12px' }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 700, mb: 0.5 }}>
                An acknowledgement has already been sent for {ackAlreadySent.length} of these
                {ackAlreadySent.length === 1 ? ' transactions' : ' transactions'} — sending again will mail the client a
                second time.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {ackAlreadySent.map((r) => (
                  <Chip
                    key={r._rid}
                    size="small"
                    label={`${r.trx_id} · ${formatINR(r.amount)}`}
                    title={r._mail.at ? `Sent ${fmtDateTime(r._mail.at)}` : 'Already sent'}
                    sx={{
                      height: 20,
                      fontSize: '10px',
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      backgroundColor: '#fff',
                      color: '#b45309',
                      border: '1px solid #fde68a',
                    }}
                  />
                ))}
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setAckConfirm(false)} disabled={sendingAck} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={sendingAck}
            startIcon={<CheckCircleIcon />}
            onClick={() => {
              setAckConfirm(false);
              handleSendAck();
            }}
            sx={{ textTransform: 'none', backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}
          >
            {ackAlreadySent.length > 0 ? (ackAlreadySent.length === ackCount ? 'Send Again' : 'Send All') : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!unmapConfirmRid}
        onClose={() => setUnmapConfirmRid(null)}
        PaperProps={{ sx: { borderRadius: '10px' } }}
      >
        <DialogTitle sx={{ fontSize: '15px', fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOffIcon fontSize="small" />
          Unmap transaction?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '13px', color: '#475569' }}>
            Are you sure you want to unmap this transaction? This will remove its invoice mapping.
          </Typography>
          {(() => {
            const t = list.find((r) => r._rid === unmapConfirmRid);
            return t ? (
              <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 1 }}>
                {t.trx_id} · {formatINR(t.amount)}
              </Typography>
            ) : null;
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button onClick={() => setUnmapConfirmRid(null)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            startIcon={<LinkOffIcon />}
            onClick={() => {
              const rid = unmapConfirmRid;
              setUnmapConfirmRid(null);
              handleUnmap(rid);
            }}
            sx={{ textTransform: 'none', backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
          >
            Unmap
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentsMapping;
